import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import ExpandDiv from "../common/ExpandDiv";
import {
  FormInput,
  FormSelect,
  FormCheckbox,
  FormRadioButton,
  FormTextArea,
  FieldTable,
  ToggleSwitch,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function COTFCPSD() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);
    buildClinicalNarrativeSummary();

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const preSignForm = () => {
    const obj = { ...data };

    if (obj.SiblingDetails["@value"]) {
      const siblingDetails = JSON.parse(obj.SiblingDetails["@value"]);

      if (siblingDetails)
        obj.SiblingDetails["@value"] = JSON.stringify(
          siblingDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.PrimaryHouseholdDetails["@value"]) {
      const primaryHouseholdDetails = JSON.parse(
        obj.PrimaryHouseholdDetails["@value"]
      );

      if (primaryHouseholdDetails)
        obj.PrimaryHouseholdDetails["@value"] = JSON.stringify(
          primaryHouseholdDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.SecondaryHouseholdDetails["@value"]) {
      const secondaryHouseholdDetails = JSON.parse(
        obj.SecondaryHouseholdDetails["@value"]
      );

      if (secondaryHouseholdDetails)
        obj.SecondaryHouseholdDetails["@value"] = JSON.stringify(
          secondaryHouseholdDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.SupportivePeopleDetails["@value"]) {
      const supportivePeopleDetails = JSON.parse(
        obj.SupportivePeopleDetails["@value"]
      );

      if (supportivePeopleDetails)
        obj.SupportivePeopleDetails["@value"] = JSON.stringify(
          supportivePeopleDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.FamilyHistoryDetails["@value"]) {
      const familyHistoryDetails = JSON.parse(
        obj.FamilyHistoryDetails["@value"]
      );

      if (familyHistoryDetails)
        obj.FamilyHistoryDetails["@value"] = JSON.stringify(
          familyHistoryDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.CurrentMedicationDetails["@value"]) {
      const currentMedicationDetails = JSON.parse(
        obj.CurrentMedicationDetails["@value"]
      );

      if (currentMedicationDetails)
        obj.CurrentMedicationDetails["@value"] = JSON.stringify(
          currentMedicationDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.TraumaHistory["@value"]) {
      const traumaHistory = JSON.parse(obj.TraumaHistory["@value"]);

      if (traumaHistory)
        obj.TraumaHistory["@value"] = JSON.stringify(
          traumaHistory.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.DiagnosisDetails["@value"]) {
      const diagnosisDetails = JSON.parse(obj.DiagnosisDetails["@value"]);

      if (diagnosisDetails)
        obj.DiagnosisDetails["@value"] = JSON.stringify(
          diagnosisDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.TreatmentRecommendations["@value"]) {
      const treatmentRecommendations = JSON.parse(
        obj.TreatmentRecommendations["@value"]
      );

      if (treatmentRecommendations)
        obj.TreatmentRecommendations["@value"] = JSON.stringify(
          treatmentRecommendations.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    setData(obj);
  };

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const handleTraumaSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TraumaticExposureInput = {
        currentTarget: {
          type: "checkbox",
          name: "TraumaticExposure",
          checked: false,
        },
      };
      handleChange(TraumaticExposureInput);

      const AvoidantTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "AvoidantTrauma",
          checked: false,
        },
      };
      handleChange(AvoidantTraumaInput);

      const HypervigilanceTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "HypervigilanceTrauma",
          checked: false,
        },
      };
      handleChange(HypervigilanceTraumaInput);

      const IntrusiveDreamsInput = {
        currentTarget: {
          type: "checkbox",
          name: "IntrusiveDreams",
          checked: false,
        },
      };
      handleChange(IntrusiveDreamsInput);

      const NegativeThoughtsInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeThoughts",
          checked: false,
        },
      };
      handleChange(NegativeThoughtsInput);

      const CocentrationProblemsInput = {
        currentTarget: {
          type: "checkbox",
          name: "CocentrationProblems",
          checked: false,
        },
      };
      handleChange(CocentrationProblemsInput);

      const RecklessBehaviorTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "RecklessBehaviorTrauma",
          checked: false,
        },
      };
      handleChange(RecklessBehaviorTraumaInput);

      const SleepDisturbancesTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesTrauma",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesTraumaInput);

      const OtherTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherTrauma",
          checked: false,
        },
      };
      handleChange(OtherTraumaInput);

      const OtherTraumaDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherTraumaDetails",
          value: "",
        },
      };
      handleChange(OtherTraumaDetailsInput);

      const TraumaSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsFrequency",
          value: "",
        },
      };
      handleChange(TraumaSymptomsFrequencyInput);

      const TraumaSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsIntensity",
          value: "",
        },
      };
      handleChange(TraumaSymptomsIntensityInput);

      const TraumaSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsDuration",
          value: "",
        },
      };
      handleChange(TraumaSymptomsDurationInput);

      const TraumaSymptomsOnsetInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsOnset",
          value: "",
        },
      };
      handleChange(TraumaSymptomsOnsetInput);
    }
  };

  const handleAnxietyDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AnxietyDisorderExcessWorryInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderExcessWorry",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderExcessWorryInput);

      const AnxietyDisorderEasilyFatiguedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderEasilyFatigued",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderEasilyFatiguedInput);

      const MuscleTensionInput = {
        currentTarget: {
          type: "checkbox",
          name: "MuscleTension",
          checked: false,
        },
      };
      handleChange(MuscleTensionInput);

      const OverworriedInput = {
        currentTarget: {
          type: "checkbox",
          name: "Overworried",
          checked: false,
        },
      };
      handleChange(OverworriedInput);

      const AnxietyDisorderDifficultyConcentratingInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderDifficultyConcentrating",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderDifficultyConcentratingInput);

      const SleepDisturbancesAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesAnxiety",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesAnxietyInput);

      const RestlessAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "RestlessAnxiety",
          checked: false,
        },
      };
      handleChange(RestlessAnxietyInput);

      const IrritabilityInput = {
        currentTarget: {
          type: "checkbox",
          name: "Irritability",
          checked: false,
        },
      };
      handleChange(IrritabilityInput);

      const OtherAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAnxiety",
          checked: false,
        },
      };
      handleChange(OtherAnxietyInput);

      const OtherAnxietyDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAnxietyDetails",
          value: "",
        },
      };
      handleChange(OtherAnxietyDetailsInput);

      const AnxietyFrequencyInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyFrequency",
          checked: false,
        },
      };
      handleChange(AnxietyFrequencyInput);

      const AnxietyIntensityInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyIntensity",
          checked: false,
        },
      };
      handleChange(AnxietyIntensityInput);

      const AnxietyDurationInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDuration",
          checked: false,
        },
      };
      handleChange(AnxietyDurationInput);
    }
  };

  const handleDepressionChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DepressedMoodInput = {
        currentTarget: {
          type: "checkbox",
          name: "DepressedMood",
          checked: false,
        },
      };
      handleChange(DepressedMoodInput);

      const SleepChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepChanges",
          checked: false,
        },
      };
      handleChange(SleepChangesInput);

      const GuiltInput = {
        currentTarget: {
          type: "checkbox",
          name: "Guilt",
          checked: false,
        },
      };
      handleChange(GuiltInput);

      const DiminishedInterestInput = {
        currentTarget: {
          type: "checkbox",
          name: "DiminishedInterest",
          checked: false,
        },
      };
      handleChange(DiminishedInterestInput);

      const PurposelessMovementInput = {
        currentTarget: {
          type: "checkbox",
          name: "PurposelessMovement",
          checked: false,
        },
      };
      handleChange(PurposelessMovementInput);

      const DifficultyConcentratingDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyConcentratingDepression",
          checked: false,
        },
      };
      handleChange(DifficultyConcentratingDepressionInput);

      const WeightChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "WeightChanges",
          checked: false,
        },
      };
      handleChange(WeightChangesInput);

      const FatigueDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "FatigueDepression",
          checked: false,
        },
      };
      handleChange(FatigueDepressionInput);

      const ThoughtsOfDeathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ThoughtsOfDeath",
          checked: false,
        },
      };
      handleChange(ThoughtsOfDeathInput);

      const OtherDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherDepression",
          checked: false,
        },
      };
      handleChange(OtherDepressionInput);

      const OtherDepressionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherDepressionDetails",
          value: "",
        },
      };
      handleChange(OtherDepressionDetailsInput);

      const DepressionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DepressionFrequency",
          value: "",
        },
      };
      handleChange(DepressionFrequencyInput);

      const DepressionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DepressionIntensity",
          value: "",
        },
      };
      handleChange(DepressionIntensityInput);

      const DepressionDurationInput = {
        currentTarget: {
          type: "input",
          name: "DepressionDuration",
          value: "",
        },
      };
      handleChange(DepressionDurationInput);
    }
  };

  const handleAttentionHyperactivityChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AttentionHyperactivityEasilyDistractedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AttentionHyperactivityEasilyDistracted",
          checked: false,
        },
      };
      handleChange(AttentionHyperactivityEasilyDistractedInput);

      const DifficultyFollowThroughInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyFollowThrough",
          checked: false,
        },
      };
      handleChange(DifficultyFollowThroughInput);

      const ForgetfulInput = {
        currentTarget: {
          type: "checkbox",
          name: "Forgetful",
          checked: false,
        },
      };
      handleChange(ForgetfulInput);

      const DifficultyListeningInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyListening",
          checked: false,
        },
      };
      handleChange(DifficultyListeningInput);

      const DisorganizedAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "DisorganizedAttention",
          checked: false,
        },
      };
      handleChange(DisorganizedAttentionInput);

      const FidgetyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Fidgety",
          checked: false,
        },
      };
      handleChange(FidgetyInput);

      const LackOfDetailsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LackOfDetails",
          checked: false,
        },
      };
      handleChange(LackOfDetailsInput);

      const LosesThingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LosesThings",
          checked: false,
        },
      };
      handleChange(LosesThingsInput);

      const InterruptsOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "InterruptsOthers",
          checked: false,
        },
      };
      handleChange(InterruptsOthersInput);

      const UnableToSitStillInput = {
        currentTarget: {
          type: "checkbox",
          name: "UnableToSitStill",
          checked: false,
        },
      };
      handleChange(UnableToSitStillInput);

      const ExcessiveTalkingInput = {
        currentTarget: {
          type: "checkbox",
          name: "ExcessiveTalking",
          checked: false,
        },
      };
      handleChange(ExcessiveTalkingInput);

      const DifficultyWaitingInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyWaiting",
          checked: false,
        },
      };
      handleChange(DifficultyWaitingInput);

      const OtherAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAttention",
          checked: false,
        },
      };
      handleChange(OtherAttentionInput);

      const OtherAttentionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAttentionDetails",
          value: "",
        },
      };
      handleChange(OtherAttentionDetailsInput);

      const AttentionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "AttentionFrequency",
          value: "",
        },
      };
      handleChange(AttentionFrequencyInput);

      const AttentionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "AttentionIntensity",
          value: "",
        },
      };
      handleChange(AttentionIntensityInput);

      const AttentionDurationInput = {
        currentTarget: {
          type: "input",
          name: "AttentionDuration",
          value: "",
        },
      };
      handleChange(AttentionDurationInput);
    }
  };

  const handleSubstanceUseSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PatternOfSubstanceUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "PatternOfSubstanceUse",
          checked: false,
        },
      };
      handleChange(PatternOfSubstanceUseInput);

      const DesiresCutDownUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "DesiresCutDownUse",
          checked: false,
        },
      };
      handleChange(DesiresCutDownUseInput);

      const TimeSpentObtainingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeSpentObtaining",
          checked: false,
        },
      };
      handleChange(TimeSpentObtainingInput);

      const CravingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Cravings",
          checked: false,
        },
      };
      handleChange(CravingsInput);

      const FailureOfResponsibilitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "FailureOfResponsibilities",
          checked: false,
        },
      };
      handleChange(FailureOfResponsibilitiesInput);

      const NegativeImpactInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeImpact",
          checked: false,
        },
      };
      handleChange(NegativeImpactInput);

      const AbandonsActivitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "AbandonsActivities",
          checked: false,
        },
      };
      handleChange(AbandonsActivitiesInput);

      const ToleranceInput = {
        currentTarget: {
          type: "checkbox",
          name: "Tolerance",
          checked: false,
        },
      };
      handleChange(ToleranceInput);

      const SUWithdrawalInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUWithdrawal",
          checked: false,
        },
      };
      handleChange(SUWithdrawalInput);

      const SUOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUOther",
          checked: false,
        },
      };
      handleChange(SUOtherInput);

      const SUOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "SUOtherDetails",
          value: "",
        },
      };
      handleChange(SUOtherDetailsInput);

      const SubstanceUseFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseFrequency",
          value: "",
        },
      };
      handleChange(SubstanceUseFrequencyInput);

      const SubstanceUseIntensityInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseIntensity",
          value: "",
        },
      };
      handleChange(SubstanceUseIntensityInput);

      const SubstanceUseDurationInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseDuration",
          value: "",
        },
      };
      handleChange(SubstanceUseDurationInput);
    }
  };

  const handleDMDDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TemperInput = {
        currentTarget: {
          type: "checkbox",
          name: "Temper",
          checked: false,
        },
      };
      handleChange(TemperInput);

      const OutsideOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutsideOutbursts",
          checked: false,
        },
      };
      handleChange(OutsideOutburstsInput);

      const FrequentOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "FrequentOutbursts",
          checked: false,
        },
      };
      handleChange(FrequentOutburstsInput);

      const DMDDIrritableInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDIrritable",
          checked: false,
        },
      };
      handleChange(DMDDIrritableInput);

      const OutburstsAroundOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutburstsAroundOthers",
          checked: false,
        },
      };
      handleChange(OutburstsAroundOthersInput);

      const DMDDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDOther",
          checked: false,
        },
      };
      handleChange(DMDDOtherInput);

      const DMDDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DMDDOtherDetails",
          value: "",
        },
      };
      handleChange(DMDDOtherDetailsInput);

      const DMDDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DMDDFrequency",
          value: "",
        },
      };
      handleChange(DMDDFrequencyInput);

      const DMDDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DMDDIntensity",
          value: "",
        },
      };
      handleChange(DMDDIntensityInput);

      const DMDDDurationInput = {
        currentTarget: {
          type: "input",
          name: "DMDDDuration",
          value: "",
        },
      };
      handleChange(DMDDDurationInput);
    }
  };

  const handleConductDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AggressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "Aggression",
          checked: false,
        },
      };
      handleChange(AggressionInput);

      const DestructionOfPropertyInput = {
        currentTarget: {
          type: "checkbox",
          name: "DestructionOfProperty",
          checked: false,
        },
      };
      handleChange(DestructionOfPropertyInput);

      const TheftInput = {
        currentTarget: {
          type: "checkbox",
          name: "Theft",
          checked: false,
        },
      };
      handleChange(TheftInput);

      const SeriousViolationsInput = {
        currentTarget: {
          type: "checkbox",
          name: "SeriousViolations",
          checked: false,
        },
      };
      handleChange(SeriousViolationsInput);

      const OtherConductDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherConductDisorder",
          checked: false,
        },
      };
      handleChange(OtherConductDisorderInput);

      const OtherConductDisorderDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherConductDisorderDetails",
          value: "",
        },
      };
      handleChange(OtherConductDisorderDetailsInput);

      const ConductDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderFrequency",
          value: "",
        },
      };
      handleChange(ConductDisorderFrequencyInput);

      const ConductDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderIntensity",
          value: "",
        },
      };
      handleChange(ConductDisorderIntensityInput);

      const ConductDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderDuration",
          value: "",
        },
      };
      handleChange(ConductDisorderDurationInput);
    }
  };

  const handleODDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const IrritableODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "IrritableODD",
          checked: false,
        },
      };
      handleChange(IrritableODDInput);

      const ArgumentativeInput = {
        currentTarget: {
          type: "checkbox",
          name: "Argumentative",
          checked: false,
        },
      };
      handleChange(ArgumentativeInput);

      const VindictiveInput = {
        currentTarget: {
          type: "checkbox",
          name: "Vindictive",
          checked: false,
        },
      };
      handleChange(VindictiveInput);

      const OtherODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherODD",
          checked: false,
        },
      };
      handleChange(OtherODDInput);

      const OtherODDDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherODDDetails",
          value: "",
        },
      };
      handleChange(OtherODDDetailsInput);

      const ODDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ODDFrequency",
          value: "",
        },
      };
      handleChange(ODDFrequencyInput);

      const ODDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ODDIntensity",
          value: "",
        },
      };
      handleChange(ODDIntensityInput);

      const ODDDurationInput = {
        currentTarget: {
          type: "input",
          name: "ODDDuration",
          value: "",
        },
      };
      handleChange(ODDDurationInput);
    }
  };

  const handleOCDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OCDPresenceInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDPresence",
          checked: false,
        },
      };
      handleChange(OCDPresenceInput);

      const ObsessionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Obsessions",
          checked: false,
        },
      };
      handleChange(ObsessionsInput);

      const TimeConsumingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeConsuming",
          checked: false,
        },
      };
      handleChange(TimeConsumingInput);

      const CompulsionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Compulsions",
          checked: false,
        },
      };
      handleChange(CompulsionsInput);

      const ReduceAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "ReduceAnxiety",
          checked: false,
        },
      };
      handleChange(ReduceAnxietyInput);

      const SuppressUrgesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SuppressUrges",
          checked: false,
        },
      };
      handleChange(SuppressUrgesInput);

      const OCDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDOther",
          checked: false,
        },
      };
      handleChange(OCDOtherInput);

      const OCDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OCDOtherDetails",
          value: "",
        },
      };
      handleChange(OCDOtherDetailsInput);

      const OCDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OCDFrequency",
          value: "",
        },
      };
      handleChange(OCDFrequencyInput);

      const OCDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OCDIntensity",
          value: "",
        },
      };
      handleChange(OCDIntensityInput);

      const OCDDurationInput = {
        currentTarget: {
          type: "input",
          name: "OCDDuration",
          value: "",
        },
      };
      handleChange(OCDDurationInput);
    }
  };

  const handlePanicDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PoundingHeartInput = {
        currentTarget: {
          type: "checkbox",
          name: "PoundingHeart",
          checked: false,
        },
      };
      handleChange(PoundingHeartInput);

      const PanicDisorderSweatingInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderSweating",
          checked: false,
        },
      };
      handleChange(PanicDisorderSweatingInput);

      const TremblingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Trembling",
          checked: false,
        },
      };
      handleChange(TremblingInput);

      const ShortnessOfBreathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ShortnessOfBreath",
          checked: false,
        },
      };
      handleChange(ShortnessOfBreathInput);

      const ChokingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Choking",
          checked: false,
        },
      };
      handleChange(ChokingInput);

      const ChestPainInput = {
        currentTarget: {
          type: "checkbox",
          name: "ChestPain",
          checked: false,
        },
      };
      handleChange(ChestPainInput);

      const PanicDisorderNauseaInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderNausea",
          checked: false,
        },
      };
      handleChange(PanicDisorderNauseaInput);

      const DizzyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Dizzy",
          checked: false,
        },
      };
      handleChange(DizzyInput);

      const ChillsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Chills",
          checked: false,
        },
      };
      handleChange(ChillsInput);

      const DerealizationInput = {
        currentTarget: {
          type: "checkbox",
          name: "Derealization",
          checked: false,
        },
      };
      handleChange(DerealizationInput);

      const FearOfCrazyInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfCrazy",
          checked: false,
        },
      };
      handleChange(FearOfCrazyInput);

      const FearOfDyingInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfDying",
          checked: false,
        },
      };
      handleChange(FearOfDyingInput);

      const PanicDisorderOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderOther",
          checked: false,
        },
      };
      handleChange(PanicDisorderOtherInput);

      const PanicDisorderOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderOtherDetails",
          value: "",
        },
      };
      handleChange(PanicDisorderOtherDetailsInput);

      const PanicDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderFrequency",
          value: "",
        },
      };
      handleChange(PanicDisorderFrequencyInput);

      const PanicDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderIntensity",
          value: "",
        },
      };
      handleChange(PanicDisorderIntensityInput);

      const PanicDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderDuration",
          value: "",
        },
      };
      handleChange(PanicDisorderDurationInput);
    }
  };

  const handlePhobiasChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PhobiasSpecificFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasSpecificFear",
          checked: false,
        },
      };
      handleChange(PhobiasSpecificFearInput);

      const ImmediateFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "ImmediateFear",
          checked: false,
        },
      };
      handleChange(ImmediateFearInput);

      const PhobiasAvoidanceInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasAvoidance",
          checked: false,
        },
      };
      handleChange(PhobiasAvoidanceInput);

      const FearExaggeratedInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearExaggerated",
          checked: false,
        },
      };
      handleChange(FearExaggeratedInput);

      const PhobiasOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasOther",
          checked: false,
        },
      };
      handleChange(PhobiasOtherInput);

      const PhobiasOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasOtherDetails",
          value: "",
        },
      };
      handleChange(PhobiasOtherDetailsInput);

      const PhobiasFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasFrequency",
          value: "",
        },
      };
      handleChange(PhobiasFrequencyInput);

      const PhobiasIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasIntensity",
          value: "",
        },
      };
      handleChange(PhobiasIntensityInput);

      const PhobiasDurationInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasDuration",
          value: "",
        },
      };
      handleChange(PhobiasDurationInput);
    }
  };

  const handleDIChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DILowIQInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILowIQ",
          checked: false,
        },
      };
      handleChange(DILowIQInput);

      const DISensoryImpairmentsInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISensoryImpairments",
          checked: false,
        },
      };
      handleChange(DISensoryImpairmentsInput);

      const DISpeechDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpeechDelays",
          checked: false,
        },
      };
      handleChange(DISpeechDelaysInput);

      const DIMotorCoordinationDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DIMotorCoordinationDelays",
          checked: false,
        },
      };
      handleChange(DIMotorCoordinationDelaysInput);

      const DILearningDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILearningDisorder",
          checked: false,
        },
      };
      handleChange(DILearningDisorderInput);

      const DISpatialDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpatialDelays",
          checked: false,
        },
      };
      handleChange(DISpatialDelaysInput);

      const DevelopmentalImpairmentsOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DevelopmentalImpairmentsOther",
          checked: false,
        },
      };
      handleChange(DevelopmentalImpairmentsOtherInput);

      const DevelopmentalImpairmentsOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DevelopmentalImpairmentsOtherDetails",
          value: "",
        },
      };
      handleChange(DevelopmentalImpairmentsOtherDetailsInput);
    }
  };

  const handleOtherSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OtherSymptomsDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDetails",
          value: "",
        },
      };
      handleChange(OtherSymptomsDetailsInput);

      const OtherSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsFrequency",
          value: "",
        },
      };
      handleChange(OtherSymptomsFrequencyInput);

      const OtherSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsIntensity",
          value: "",
        },
      };
      handleChange(OtherSymptomsIntensityInput);

      const OtherSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDuration",
          value: "",
        },
      };
      handleChange(OtherSymptomsDurationInput);
    }
  };

  const getFamilyMemberConcerns = (index) => {
    const concerns = [];
    const fmData = JSON.parse(data.FamilyHistoryDetails["@value"])[index];

    if (fmData.MentalHealth === "true") concerns.push("Mental Health");
    if (fmData.Medical === "true") concerns.push("Medical");
    if (fmData.Disability === "true") concerns.push("Disability");
    if (fmData.SubstanceAbuse === "true") concerns.push("Substance Abuse");
    if (fmData.Suicidal === "true") concerns.push("Suicidal");
    if (fmData.Legal === "true") concerns.push("Legal");
    if (fmData.Trauma === "true") concerns.push("Trauma");
    if (fmData.Perpetration === "true") concerns.push("Perpetration");
    if (fmData.PartnerViolence === "true")
      concerns.push("Intimate Partner Violence");
    if (fmData.Other === "true") concerns.push("Other");

    if (concerns.length > 0) return concerns.join(", ");
    else return "None reported";
  };

  const sexAtBirthOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Intersex", name: "Intersex" },
    { _id: "Sex not listed", name: "Sex not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const genderIdentityOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Non-binary", name: "Non-binary" },
    { _id: "Agender", name: "Agender" },
    { _id: "Transgender (FtM)", name: "Transgender (FtM)" },
    { _id: "Transgender (MtF)", name: "Transgender (MtF)" },
    { _id: "Identity not listed", name: "Identity not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pronounOptions = [
    { _id: "She/Her/Hers", name: "She/Her/Hers" },
    { _id: "He/Him/His", name: "He/Him/His" },
    { _id: "They/Them/Their", name: "They/Them/Their" },
    { _id: "Prefer to self-describe", name: "Prefer to self-describe" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const psdTypeOptions = [
    { _id: "Pre-Placement", name: "Pre-Placement" },
    { _id: "Initial", name: "Initial" },
    { _id: "Review/Update", name: "Review/Update" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "N/A", name: "N/A" },
  ];

  const yesNoUnknownOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const yesNoNoneOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
  ];

  const unknownNAOptions = [
    { _id: "N/A", name: "N/A" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const householdOptions = [
    { _id: "Parent's Home", name: "Parent's Home" },
    {
      _id: "Kinship/Other Caregiver Home",
      name: "Kinship/Other Caregiver Home",
    },
    { _id: "Foster Home", name: "Foster Home" },
    { _id: "Other", name: "Other" },
  ];

  const relationshipOptions = [
    { _id: "Father", name: "Father" },
    { _id: "Mother", name: "Mother" },
    { _id: "Grandfather", name: "Grandfather" },
    { _id: "Grandmother", name: "Grandmother" },
    { _id: "Brother", name: "Brother" },
    { _id: "Sister", name: "Sister" },
    { _id: "Step-father", name: "Step-father" },
    { _id: "Step-mother", name: "Step-mother" },
    { _id: "Foster Parent", name: "Foster Parent" },
    { _id: "Uncle", name: "Uncle" },
    { _id: "Aunt", name: "Aunt" },
    { _id: "Cousin", name: "Cousin" },
    { _id: "Family Friend", name: "Family Friend" },
    { _id: "Coach", name: "Coach" },
    { _id: "Mentor", name: "Mentor" },
    { _id: "Friend/Peer", name: "Friend/Peer" },
    { _id: "Foster Sibling", name: "Foster Sibling" },
    { _id: "Paramour", name: "Paramour" },
    { _id: "Other Family Member", name: "Other Family Member" },
    { _id: "Other", name: "Other" },
    { _id: "Child", name: "Child" },
  ];

  const familyMemberOptions = [
    { _id: "Father", name: "Father" },
    { _id: "Mother", name: "Mother" },
    { _id: "Grandfather", name: "Grandfather" },
    { _id: "Grandmother", name: "Grandmother" },
    { _id: "Uncle", name: "Uncle" },
    { _id: "Aunt", name: "Aunt" },
    { _id: "Brother", name: "Brother" },
    { _id: "Sister", name: "Sister" },
    { _id: "Step-father", name: "Step-father" },
    { _id: "Step-mother", name: "Step-mother" },
    { _id: "Cousin", name: "Cousin" },
  ];

  const caregiverCooperationOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "N/A, only one caregiver", name: "N/A, only one caregiver" },
  ];

  const permanencyPlanOptions = [
    {
      _id: "Reunification",
      name: "Reunification",
    },
    {
      _id: "Adoption",
      name: "Adoption",
    },
    { _id: "Kinship", name: "Kinship" },
    { _id: "Other", name: "Other" },
  ];

  const educationOptions = [
    {
      _id: "Currently Enrolled in School/Client is School Aged",
      name: "Currently Enrolled in School/Client is School Aged",
    },
    {
      _id: "Client has not started school",
      name: "Client has not started school",
    },
    { _id: "Graduated", name: "Graduated" },
    { _id: "GED", name: "GED" },
    { _id: "Dropout/Did not complete", name: "Dropout/Did not complete" },
  ];

  const schoolTransferOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "BID Meeting Needed", name: "BID Meeting Needed" },
  ];

  const functioningLevelOptions = [
    { _id: "Age Appropriate", name: "Age Appropriate" },
    { _id: "Regressed", name: "Regressed" },
    { _id: "Advanced", name: "Advanced" },
    { _id: "N/A", name: "N/A" },
    { _id: "Unable to Assess", name: "Unable to Assess" },
  ];

  const cpsInvolvementOptions = [
    { _id: "Current", name: "Current" },
    { _id: "Historical", name: "Historical" },
    { _id: "None", name: "None" },
  ];

  const complianceOptions = [
    {
      _id: "Consistently takes as directed",
      name: "Consistently takes as directed",
    },
    {
      _id: "Inconsistent with directed use",
      name: "Inconsistent with directed use",
    },
    { _id: "Refuses to take", name: "Refuses to take" },
    { _id: "Forgets to take", name: "Forgets to take" },
  ];

  const useOfProtectionOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Not with Consistency", name: "Not with Consistency" },
    { _id: "N/A", name: "N/A" },
  ];

  const sexualOrientationOptions = [
    {
      _id: "Heterosexual",
      name: "Heterosexual",
    },
    {
      _id: "Gay",
      name: "Gay",
    },
    { _id: "Lesbian", name: "Lesbian" },
    { _id: "Bisexual", name: "Bisexual" },
    { _id: "Pansexual", name: "Pansexual" },
    { _id: "Polysexual", name: "Polysexual" },
    { _id: "Asexual", name: "Asexual" },
    { _id: "Queer/Questioning", name: "Queer/Questioning" },
    { _id: "Orientation not listed", name: "Orientation not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pregnancyOptions = [
    { _id: "Current", name: "Current" },
    { _id: "Past", name: "Past" },
    { _id: "None Reported", name: "None Reported" },
    { _id: "N/A", name: "N/A" },
  ];

  const substanceUseOptions = [
    { _id: "Current", name: "Current" },
    { _id: "Past", name: "Past" },
    { _id: "None Reported", name: "None Reported" },
  ];

  const traumaOptions = [
    { _id: "Physical Abuse", name: "Physical Abuse" },
    { _id: "Sexual Abuse", name: "Sexual Abuse" },
    { _id: "Neglect", name: "Neglect" },
    { _id: "Emotional Abuse", name: "Emotional Abuse" },
    { _id: "Domestic Violence", name: "Domestic Violence" },
    { _id: "Accident", name: "Accident" },
    { _id: "Acts of Violence", name: "Acts of Violence" },
    { _id: "Bullying", name: "Bullying" },
    { _id: "Death/Tragedy", name: "Death/Tragedy" },
    { _id: "Natural Disaster", name: "Natural Disaster" },
    { _id: "Community Violence", name: "Community Violence" },
    { _id: "Medical/Serious Illness", name: "Medical/Serious Illness" },
    { _id: "Abandonment", name: "Abandonment" },
    { _id: "War/Terrorism", name: "War/Terrorism" },
    { _id: "Exploitation/Trafficking", name: "Exploitation/Trafficking" },
    { _id: "Criminal Activity", name: "Criminal Activity" },
    { _id: "Caregiver Disruptions", name: "Caregiver Disruptions" },
    { _id: "Kidnapping", name: "Kidnapping" },
    { _id: "Other", name: "Other" },
  ];

  const statusOptions = [
    { _id: "Victim", name: "Victim" },
    { _id: "Witnessed", name: "Witnessed" },
    { _id: "Suspected", name: "Suspected" },
    { _id: "Threatened", name: "Threatened" },
    { _id: "Perceived", name: "Perceived" },
    { _id: "Repeated Exposure", name: "Repeated Exposure" },
    { _id: "Indirect Exposure", name: "Indirect Exposure" },
  ];

  const timeframeOptions = [
    { _id: "None/Never", name: "None/Never" },
    { _id: "Current", name: "Current" },
    { _id: "Last 30 Days", name: "Last 30 Days" },
    { _id: "Last 6 Months", name: "Last 6 Months" },
    { _id: "Last 6-12 Months", name: "Last 6-12 Months" },
    { _id: "More Than 12 Months Ago", name: "More Than 12 Months Ago" },
  ];

  const riskOptions = [
    { _id: "None Noted", name: "None Noted" },
    { _id: "Low", name: "Low" },
    { _id: "Moderate", name: "Moderate" },
    { _id: "High", name: "High" },
  ];

  const treatmentRecommendationOptions = [
    {
      _id: "Individual Counseling/Therapy",
      name: "Individual Counseling/Therapy",
    },
    { _id: "Family Counseling/Therapy", name: "Family Counseling/Therapy" },
    { _id: "Group Counseling/Therapy", name: "Group Counseling/Therapy" },
    { _id: "Basic Living Skills", name: "Basic Living Skills" },
    { _id: "Mental Health Consultation", name: "Mental Health Consultation" },
    {
      _id: "Crisis Intervention and Resolution",
      name: "Crisis Intervention and Resolution",
    },
    { _id: "Family Support Services", name: "Family Support Services" },
    { _id: "Case Management Services", name: "Case Management Services" },
    { _id: "Psychoeducation", name: "Psychoeducation" },
    { _id: "Rehabilitation Skills", name: "Rehabilitation Skills" },
    {
      _id: "Community Psychiatric Support Services (CPST)",
      name: "Community Psychiatric Support Services (CPST)",
    },
    {
      _id: "Psychiatric Evaluation/Services",
      name: "Psychiatric Evaluation/Services",
    },
    {
      _id: "Psychosocial Rehabilitation Services (PSR)",
      name: "Psychosocial Rehabilitation Services (PSR)",
    },
    {
      _id: "Therapeutic Behavioral Services (TBS)",
      name: "Therapeutic Behavioral Services (TBS)",
    },
    {
      _id: "Behavior Management Training/Services",
      name: "Behavior Management Training/Services",
    },
    { _id: "Animal Assisted Therapy", name: "Animal Assisted Therapy" },
    {
      _id: "Community Based Treatment Services",
      name: "Community Based Treatment Services",
    },
    { _id: "In Home Treatment Services", name: "In Home Treatment Services" },
    {
      _id: "Early Childhood Intervention Services",
      name: "Early Childhood Intervention Services",
    },
    { _id: "Mentoring", name: "Mentoring" },
    {
      _id: "Substance Abuse Specific Treatment",
      name: "Substance Abuse Specific Treatment",
    },
    {
      _id: "AA/NA/COA",
      name: "AA/NA/COA",
    },
  ];

  const buildClinicalNarrativeSummary = () => {
    if (!formDisabled) {
      let summary = "";

      // Presenting Problem & Strengths
      let presentingProblemSummary = "";
      if (data.ReferralReason["@value"] && data.ReferralReason["@value"] !== "")
        presentingProblemSummary += `Reason for Placement: ${data.ReferralReason["@value"]}; `;

      if (data.ReferralSource["@value"] && data.ReferralSource["@value"] !== "")
        presentingProblemSummary += `Referral Source: ${data.ReferralSource["@value"]}; `;

      if (
        data.PresentingProblem["@value"] &&
        data.PresentingProblem["@value"] !== ""
      )
        presentingProblemSummary += `Presenting Problem: ${data.PresentingProblem["@value"]};`;

      if (data.ClientProblem["@value"] && data.ClientProblem["@value"] !== "")
        presentingProblemSummary += `Client Description of Problem: ${data.ClientProblem["@value"]};`;

      if (
        data.CaregiverProblem["@value"] &&
        data.CaregiverProblem["@value"] !== ""
      )
        presentingProblemSummary += `Caregiver Description of Problem: ${data.CaregiverProblem["@value"]};`;

      if (
        data.ClientStrengths["@value"] &&
        data.ClientStrengths["@value"] !== ""
      )
        presentingProblemSummary += `Client Description of Strengths: ${data.ClientStrengths["@value"]};`;

      if (
        data.ClientAbilities["@value"] &&
        data.ClientAbilities["@value"] !== ""
      )
        presentingProblemSummary += `Client abilities, skills, and interests: ${data.ClientAbilities["@value"]};`;

      if (presentingProblemSummary !== "")
        summary += `Presenting Problem:\r\n${presentingProblemSummary.trimEnd()}\r\n\r\n`;

      //Family Environment and History
      let familyHistorySummary = "";
      if (
        data.PrimaryHouseholdDetails["@value"] &&
        data.PrimaryHouseholdDetails["@value"] !== ""
      )
        familyHistorySummary += `Primary Household: ${JSON.parse(
          data.PrimaryHouseholdDetails["@value"]
        )
          .filter((item) => !item.IsEditable)
          .map(
            (item) =>
              `${item.Name} - ${item.Relationship} - ${item.HouseholdMember} - ${item.Comments}`
          )
          .join(", ")
          .trimEnd()}; `;

      if (
        data.FamilyHistoryDetails["@value"] &&
        data.FamilyHistoryDetails["@value"] !== ""
      )
        familyHistorySummary += `Family Members: ${JSON.parse(
          data.FamilyHistoryDetails["@value"]
        )
          .filter((item) => !item.IsEditable)
          .map(
            (item, index) =>
              `${item.FamilyMember} - ${getFamilyMemberConcerns(index)}`
          )
          .join(", ")
          .trimEnd()}; `;

      if (familyHistorySummary !== "")
        summary += `Family History:\r\n${familyHistorySummary.trimEnd()}\r\n\r\n`;

      // Culture/Ethnicity/Religion/Spirituality
      let cultureSummary = "";
      if (data.CulturalGroup["@value"] && data.CulturalGroup["@value"] !== "")
        cultureSummary += `Identified cultural group: ${data.CulturalGroup["@value"]}; `;

      if (data.Traditions["@value"] && data.Traditions["@value"] !== "")
        cultureSummary += `Special cultural traditions/celebrations/rituals: ${data.Traditions["@value"]}; `;

      if (
        data.LanguagesSpoken["@value"] &&
        data.LanguagesSpoken["@value"] !== ""
      )
        cultureSummary += `Languages spoken: ${data.LanguagesSpoken["@value"]}; `;

      if (data.FamilyValues["@value"] && data.FamilyValues["@value"] !== "")
        cultureSummary += `What are the family's values: ${data.FamilyValues["@value"]}; `;

      if (
        data.NegativeCulturalExperienceComments["@value"] &&
        data.NegativeCulturalExperienceComments["@value"] !== ""
      )
        cultureSummary += `Comments regarding negative experiences due to customs/ethnicity/race/SES: ${data.NegativeCulturalExperienceComments["@value"]}; `;

      if (data.PlacesLived["@value"] && data.PlacesLived["@value"] !== "")
        cultureSummary += `Places lived: ${data.PlacesLived["@value"]}; `;

      if (
        data.CulturalDifferencesComments["@value"] &&
        data.CulturalDifferencesComments["@value"] !== ""
      )
        cultureSummary += `Comments regarding cultural differences between youth and placement family: ${data.CulturalDifferencesComments["@value"]}; `;

      if (cultureSummary !== "")
        summary += `Culture/Ethnicity:\r\n${cultureSummary.trimEnd()}\r\n\r\n`;

      // Religion/Spirituality
      if (
        data.ReligiousComments["@value"] &&
        data.ReligiousComments["@value"] !== ""
      )
        summary += `Comments regarding youth's religious or spiritual belief system: ${data.CulturalDifferencesComments["@value"]}; `;

      // Developmental History
      let developmentalSummary = "";
      if (
        data.PHPrenatalCareComments["@value"] &&
        data.PHPrenatalCareComments["@value"] !== ""
      )
        developmentalSummary += `Comments regarding prenatal care: ${data.PHPrenatalCareComments["@value"]}`;

      if (
        data.PHExposureComments["@value"] &&
        data.PHExposureComments["@value"] !== ""
      )
        developmentalSummary += `Comments regarding mother's exposure to alcohol, tobacco, or other drugs: ${data.PHExposureComments["@value"]}`;

      if (
        data.PHTraumaComments["@value"] &&
        data.PHTraumaComments["@value"] !== ""
      )
        developmentalSummary += `Comments regarding mother's history of DV or significant trauma during pregnancy: ${data.PHTraumaComments["@value"]}`;

      if (
        data.PHComplicationsComments["@value"] &&
        data.PHComplicationsComments["@value"] !== ""
      )
        developmentalSummary += `Comments regarding birth complications: ${data.PHComplicationsComments["@value"]}`;

      if (
        data.PHConcernsComments["@value"] &&
        data.PHConcernsComments["@value"] !== ""
      )
        developmentalSummary += `Comments regarding mother's medical concerns: ${data.PHConcernsComments["@value"]}`;

      if (
        data.DevelopmentalDelaysDetails["@value"] &&
        data.DevelopmentalDelaysDetails["@value"] !== ""
      )
        developmentalSummary += `Physical, cognitive/IQ, social, emotional delays: ${data.DevelopmentalDelaysDetails["@value"]}; `;

      if (
        data.SpecializedServicesDetails["@value"] &&
        data.SpecializedServicesDetails["@value"] !== ""
      )
        developmentalSummary += `Specialized Services: ${data.SpecializedServicesDetails["@value"]}; `;

      if (developmentalSummary !== "")
        summary += `Developmental History:\r\n${developmentalSummary.trimEnd()}\r\n\r\n`;

      //Education/Employment
      let educationSummary = "";
      if (data.SchoolName["@value"] && data.SchoolName["@value"] !== "")
        educationSummary += `School Name: ${data.SchoolName["@value"]}; `;

      if (data.CurrentGrade["@value"] && data.CurrentGrade["@value"] !== "")
        educationSummary += `Current Grade: ${data.CurrentGrade["@value"]}; `;

      if (
        data.DisciplineConcernsDetails["@value"] &&
        data.DisciplineConcernsDetails["@value"] !== ""
      )
        educationSummary += `Discipline Concerns: ${data.DisciplineConcernsDetails["@value"]}; `;

      if (data.AwardsDetails["@value"] && data.AwardsDetails["@value"] !== "")
        educationSummary += `Awards: ${data.AwardsDetails["@value"]}; `;

      if (data.SchoolAttitude["@value"] && data.SchoolAttitude["@value"] !== "")
        educationSummary += `Youth's attitude toward school: ${data.SchoolAttitude["@value"]}; `;

      if (educationSummary !== "")
        summary += `Education/Employment:\r\n${educationSummary.trimEnd()}\r\n\r\n`;

      // Activites of Daily Living
      let dailyLivingSummary = "";
      if (
        data.MoneyManagement["@value"] &&
        data.MoneyManagement["@value"] !== ""
      )
        dailyLivingSummary += `Money management: ${data.MoneyManagement["@value"]}; `;

      if (data.FoodManagement["@value"] && data.FoodManagement["@value"] !== "")
        dailyLivingSummary += `Food management: ${data.FoodManagement["@value"]}; `;

      if (data.Hygiene["@value"] && data.Hygiene["@value"] !== "")
        dailyLivingSummary += `Personal appearance and hygiene: ${data.Hygiene["@value"]}; `;

      if (data.Health["@value"] && data.Health["@value"] !== "")
        dailyLivingSummary += `Health awareness/Personal Health: ${data.Health["@value"]}; `;

      if (data.Housekeeping["@value"] && data.Housekeeping["@value"] !== "")
        dailyLivingSummary += `Housekeeping: ${data.Housekeeping["@value"]}; `;

      if (
        data.ResourceKnowledge["@value"] &&
        data.ResourceKnowledge["@value"] !== ""
      )
        dailyLivingSummary += `Knowledge of community resources: ${data.ResourceKnowledge["@value"]}; `;

      if (data.TimeManagement["@value"] && data.TimeManagement["@value"] !== "")
        dailyLivingSummary += `Time management: ${data.TimeManagement["@value"]}; `;

      if (data.Interaction["@value"] && data.Interaction["@value"] !== "")
        dailyLivingSummary += `Interactions with Others: ${data.Interaction["@value"]}; `;

      if (data.SkillsComments["@value"] && data.SkillsComments["@value"] !== "")
        dailyLivingSummary += `Comments: ${data.SkillsComments["@value"]}; `;

      if (dailyLivingSummary !== "")
        summary += `Activities of Daily Living:\r\n${dailyLivingSummary.trimEnd()}\r\n\r\n`;

      // Friendship/Social Peer Support/Relationships
      let relationshipsSummary = "";
      if (data.CoopSkills["@value"] && data.CoopSkills["@value"] !== "")
        relationshipsSummary += `Cooperative Play Skills: ${data.CoopSkills["@value"]};`;

      if (
        data.CommunicationSkills["@value"] &&
        data.CommunicationSkills["@value"] !== ""
      )
        relationshipsSummary += `Communication and Interaction Skills: ${data.CommunicationSkills["@value"]};`;

      if (
        data.EmotionalUnderstanding["@value"] &&
        data.EmotionalUnderstanding["@value"] !== ""
      )
        relationshipsSummary += `Emotional understanding and self-regulation skills: ${data.EmotionalUnderstanding["@value"]};`;

      if (data.ProblemSolving["@value"] && data.ProblemSolving["@value"] !== "")
        relationshipsSummary += `Social problem-solving skills: ${data.ProblemSolving["@value"]};`;

      if (
        data.ConflictResolution["@value"] &&
        data.ConflictResolution["@value"] !== ""
      )
        relationshipsSummary += `Conflict resolution skills: ${data.ConflictResolution["@value"]};`;

      if (
        data.DecisionMakingSkills["@value"] &&
        data.DecisionMakingSkills["@value"] !== ""
      )
        relationshipsSummary += `Decision Making Skills: ${data.DecisionMakingSkills["@value"]};`;

      if (data.BulliedDetails["@value"] && data.BulliedDetails["@value"] !== "")
        relationshipsSummary += `Comments regarding bullying: ${data.BulliedDetails["@value"]};`;

      if (
        data.AcceptedByPeersDetails["@value"] &&
        data.AcceptedByPeersDetails["@value"] !== ""
      )
        relationshipsSummary += `Comments regarding peer group acceptance: ${data.AcceptedByPeersDetails["@value"]};`;

      if (
        data.NegativeAssociationsDetails["@value"] &&
        data.NegativeAssociationsDetails["@value"] !== ""
      )
        relationshipsSummary += `Comments regarding negative peer group associations: ${data.NegativeAssociationsDetails["@value"]};`;

      if (relationshipsSummary !== "")
        summary += `${relationshipsSummary}\r\n\r\n`;

      // Legal History
      let legalSummary = "";
      if (
        data.LegalInvolvement["@value"] &&
        data.LegalInvolvement["@value"] === "Yes"
      )
        legalSummary += `Youth has current or past charges involved in the legal system; `;

      if (data.OnProbation["@value"] && data.OnProbation["@value"] === "Yes")
        legalSummary += `Youth is currently on probation or parole; `;

      if (
        data.CPSInvolvementDetails["@value"] &&
        data.CPSInvolvementDetails["@value"] !== ""
      )
        legalSummary += `Current Children's Protective Services (CPS) Involvement with Family: ${data.CPSInvolvementDetails["@value"]}; `;

      if (legalSummary !== "")
        summary += `Legal History:\r\n${legalSummary.trimEnd()}\r\n\r\n`;

      // Medical History/Medications
      let medicalSummary = "";
      if (data.PCP["@value"] && data.PCP["@value"] !== "")
        if (data.PCPPhone["@value"] && data.PCPPhone["@value"] !== "")
          medicalSummary += `Primary Care Physician: ${data.PCP["@value"]}, Phone: ${data.PCPPhone["@value"]}; `;
        else
          medicalSummary += `Primary Care Physician: ${data.PCP["@value"]}; `;

      if (
        data.LastMedicalExamDate["@value"] &&
        data.LastMedicalExamDate["@value"] !== ""
      )
        medicalSummary += `Date of last medical exam: ${moment(
          data.LastMedicalExamDate["@value"]
        ).format("L")}; `;

      if (
        data.LastDentalExamDate["@value"] &&
        data.LastDentalExamDate["@value"] !== ""
      )
        medicalSummary += `Date of last dental exam: ${moment(
          data.LastDentalExamDate["@value"]
        ).format("L")}; `;

      if (
        data.AllergiesDetails["@value"] &&
        data.AllergiesDetails["@value"] !== ""
      )
        medicalSummary += `Allergies: ${data.AllergiesDetails["@value"]}; `;

      if (
        data.MedicalConditionsDetails["@value"] &&
        data.MedicalConditionsDetails["@value"] !== ""
      )
        medicalSummary += `Medical conditions/concerns: ${data.MedicalConditionsDetails["@value"]}; `;

      if (
        data.NutritionalConcernsDetails["@value"] &&
        data.NutritionalConcernsDetails["@value"] !== ""
      )
        medicalSummary += `Nutritional concerns: ${data.NutritionalConcernsDetails["@value"]}; `;

      if (
        data.DentalConcernsDetails["@value"] &&
        data.DentalConcernsDetails["@value"] !== ""
      )
        medicalSummary += `Dental concerns: ${data.DentalConcernsDetails["@value"]}; `;

      if (
        data.ConcussionsDetails["@value"] &&
        data.ConcussionsDetails["@value"] !== ""
      )
        medicalSummary += `Concussions or Head Injuries: ${data.ConcussionsDetails["@value"]}; `;

      if (
        data.UndiagnosedComments["@value"] &&
        data.UndiagnosedComments["@value"] !== ""
      )
        medicalSummary += `Comments regarding symptoms that suggest an undiagnosed medical illness: ${data.UndiagnosedComments["@value"]}; `;

      if (
        data.MedicalComments["@value"] &&
        data.MedicalComments["@value"] !== ""
      )
        medicalSummary += `General Medical Comments: ${data.MedicalComments["@value"]}; `;

      if (
        data.AdvanceDirectives["@value"] &&
        data.AdvanceDirectives["@value"] !== "Yes"
      )
        medicalSummary += `Youth has advance directive(s) in place; `;

      if (
        data.CurrentMedicationDetails["@value"] &&
        data.CurrentMedicationDetails["@value"] !== ""
      )
        medicalSummary += `Current Medications: ${JSON.parse(
          data.CurrentMedicationDetails["@value"]
        )
          .filter((item) => !item.IsEditable)
          .map((medication) => medication.Name)
          .join(", ")}; `;

      if (
        data.SideEffectsDetails["@value"] &&
        data.SideEffectsDetails["@value"] !== ""
      )
        medicalSummary += `Side effects from medications: ${data.SideEffectsDetails["@value"]}; `;

      if (
        data.PsychotropicMedsDetails["@value"] &&
        data.PsychotropicMedsDetails["@value"] !== ""
      )
        medicalSummary += `Past psychotropic nedications: ${data.PsychotropicMedsDetails["@value"]}; `;

      if (data.GenderIdentity["@value"] && data.GenderIdentity["@value"] !== "")
        medicalSummary += `Gender identity: ${data.GenderIdentity["@value"]}; `;

      if (
        data.SexualOrientation["@value"] &&
        data.SexualOrientation["@value"] !== ""
      )
        medicalSummary += `Sexual orientation: ${data.SexualOrientation["@value"]}; `;

      if (
        data.SexuallyActive["@value"] &&
        data.SexuallyActive["@value"] !== "" &&
        data.SexuallyActive["@value"] !== "N/A"
      )
        medicalSummary += `Sexually Active: ${data.SexuallyActive["@value"]}; `;

      if (
        data.CaregiverAwareComments["@value"] &&
        data.CaregiverAwareComments["@value"] === "Yes"
      )
        medicalSummary += `Caregiver comments: ${data.CaregiverAwareComments["@value"]}; `;

      if (
        data.UseProtectionComments["@value"] &&
        data.UseProtectionComments["@value"] !== ""
      )
        medicalSummary += `Use of Protection comments: ${data.UseProtectionComments["@value"]}; `;

      if (
        data.AwareOfRisks["@value"] &&
        data.AwareOfRisks["@value"] === "Yes" &&
        data.AwareOfRisksComments["@value"] &&
        data.AwareOfRisksComments["@value"] !== ""
      )
        medicalSummary += `Sexual risks comments: ${data.AwareOfRisksComments["@value"]}; `;

      if (
        data.Pregnancy["@value"] &&
        data.Pregnancy["@value"] !== "" &&
        data.Pregnancy["@value"] === "Yes" &&
        data.PregnancyComments["@value"] &&
        data.PregnancyComments["@value"] !== ""
      )
        medicalSummary += `Pregnancy comments: ${data.PregnancyComments["@value"]}; `;

      if (
        data.Children["@value"] &&
        data.Children["@value"] === "Yes" &&
        data.ChildrenComments["@value"] &&
        data.ChildrenComments["@value"] !== ""
      )
        medicalSummary += `Children comments: ${data.Children["@value"]}; `;

      if (
        data.SexualAbuseVictimComments["@value"] &&
        data.SexualAbuseVictimComments["@value"] !== ""
      )
        medicalSummary += `Sexual abuse comments: ${data.SexualAbuseVictimComments["@value"]}; `;

      if (
        data.SexuallyAbusiveComments["@value"] &&
        data.SexuallyAbusiveComments["@value"] !== ""
      )
        medicalSummary += `Sexually abusive/offending behavior: ${data.SexuallyAbusiveComments["@value"]}; `;

      if (medicalSummary !== "")
        summary += `Medical History/Medications:\r\n${medicalSummary.trimEnd()}\r\n\r\n`;

      // Mental Health Treatment History
      let mentalHealthSummary = "";
      if (
        data.PastMentalHealthTreatmentComments["@value"] &&
        data.PastMentalHealthTreatmentComments["@value"] !== ""
      )
        mentalHealthSummary += `Past mental health counseling/treatment: ${data.PastMentalHealthTreatmentComments["@value"]}; `;

      if (
        data.PastResidentialFacilityPlacementComments["@value"] &&
        data.PastResidentialFacilityPlacementComments["@value"] !== ""
      )
        mentalHealthSummary += `Past placement in a group home/residential treatment facility: ${data.PastResidentialFacilityPlacementComments["@value"]}; `;

      if (
        data.PastPsychiatricHospitalPlacementComments["@value"] &&
        data.PastPsychiatricHospitalPlacementComments["@value"] !== ""
      )
        mentalHealthSummary += `Past placement in a psychiatric hospital/unit: ${data.PastPsychiatricHospitalPlacementComments["@value"]}; `;

      if (
        data.InvoluntaryTreatmentComments["@value"] &&
        data.InvoluntaryTreatmentComments["@value"] !== ""
      )
        mentalHealthSummary += `History of involuntary/court-ordered treatment: ${data.InvoluntaryTreatmentComments["@value"]}; `;

      if (
        data.MentalHealthDiagnosis["@value"] &&
        data.MentalHealthDiagnosis["@value"] !== ""
      )
        mentalHealthSummary += `Previous or current mental health diagnoses: ${data.MentalHealthDiagnosis["@value"]}; `;

      if (mentalHealthSummary !== "")
        summary += `Mental Health Treatment History:\r\n${mentalHealthSummary.trimEnd()}\r\n\r\n`;

      // Alcohol/Drug Use History
      let substanceUseSummary = "";
      if (
        data.SubstanceUseTreatmentComments["@value"] &&
        data.SubstanceUseTreatmentComments["@value"] !== ""
      )
        substanceUseSummary += `Alcohol/Drug related treatment comments: ${data.SubstanceUseTreatmentComments["@value"]}; `;

      if (substanceUseSummary !== "")
        summary += `Alcohol/Drug Use History:\r\n${substanceUseSummary.trimEnd()}\r\n\r\n`;

      // Trauma History
      let traumaSummary = "";
      if (data.TraumaHistory["@value"] && data.TraumaHistory["@value"] !== "")
        traumaSummary += `${JSON.parse(data.TraumaHistory["@value"])
          .filter((item) => !item.IsEditable)
          .map((item) => `${item.Trauma}, ${item.Status}, ${item.Comments}`)
          .join(". ")}; `;

      if (
        data.CopingStrategies["@value"] &&
        data.CopingStrategies["@value"] !== ""
      )
        traumaSummary += `Coping strategies: ${data.CopingStrategies["@value"]}; `;

      if (traumaSummary !== "")
        summary += `Trauma History:\r\n${traumaSummary.trimEnd()}\r\n\r\n`;

      // Mental Status
      let mentalStatusSummary = "";
      let appearanceSummary = "";
      let behaviorSummary = "";
      let speechSummary = "";
      let moodSummary = "";
      let affectSummary = "";
      let perceptionSummary = "";
      let cognitionSummary = "";
      let insightSummary = "";

      if (
        data.AppropriateAppearance["@value"] &&
        data.AppropriateAppearance["@value"] === "true"
      )
        appearanceSummary += "Appropriate, ";

      if (data.Neat["@value"] && data.Neat["@value"] === "true")
        appearanceSummary += "Neat, ";

      if (data.Disheveled["@value"] && data.Disheveled["@value"] === "true")
        appearanceSummary += "Disheveled, ";

      if (
        data.BizarreAppearance["@value"] &&
        data.BizarreAppearance["@value"] === "true"
      )
        appearanceSummary += "Bizarre, ";

      if (
        data.OtherAppearance["@value"] &&
        data.OtherAppearance["@value"] === "true" &&
        data.OtherAppearanceDetails["@value"] &&
        data.OtherAppearanceDetails["@value"] !== ""
      )
        appearanceSummary += `Other: ${data.OtherAppearanceDetails["@value"]}, `;

      if (appearanceSummary !== "")
        mentalStatusSummary += `\r\nAppearance: ${appearanceSummary.slice(
          0,
          -2
        )}; `;

      if (data.Cooperative["@value"] && data.Cooperative["@value"] === "true")
        behaviorSummary += "Cooperative, ";

      if (data.Guarded["@value"] && data.Guarded["@value"] === "true")
        behaviorSummary += "Guarded, ";

      if (data.Hyperactive["@value"] && data.Hyperactive["@value"] === "true")
        behaviorSummary += "Hyperactive, ";

      if (data.Agitated["@value"] && data.Agitated["@value"] === "true")
        behaviorSummary += "Agitated, ";

      if (data.Paranoid["@value"] && data.Paranoid["@value"] === "true")
        behaviorSummary += "Paranoid, ";

      if (data.Tics["@value"] && data.Tics["@value"] === "true")
        behaviorSummary += "Tics, ";

      if (data.Impulsive["@value"] && data.Impulsive["@value"] === "true")
        behaviorSummary += "Impulsive, ";

      if (data.Aggressive["@value"] && data.Aggressive["@value"] === "true")
        behaviorSummary += "Aggressive, ";

      if (data.Withdrawn["@value"] && data.Withdrawn["@value"] === "true")
        behaviorSummary += "Withdrawn, ";

      if (
        data.BizarreBehavior["@value"] &&
        data.BizarreBehavior["@value"] === "true"
      )
        behaviorSummary += "Bizarre, ";

      if (data.Rigidity["@value"] && data.Rigidity["@value"] === "true")
        behaviorSummary += "Rigidity, ";

      if (
        data.OtherBehavior["@value"] &&
        data.OtherBehavior["@value"] === "true" &&
        data.OtherBehaviorDetails["@value"] &&
        data.OtherBehaviorDetails["@value"] !== ""
      )
        behaviorSummary += `Other: ${data.OtherBehaviorDetails["@value"]}, `;

      if (behaviorSummary !== "")
        mentalStatusSummary += `\r\nBehavior: ${behaviorSummary.slice(
          0,
          -2
        )}; `;

      if (data.Normal["@value"] && data.Normal["@value"] === "true")
        speechSummary += "Normal, ";

      if (data.Slurred["@value"] && data.Slurred["@value"] === "true")
        speechSummary += "Slurred, ";

      if (data.Pressured["@value"] && data.Pressured["@value"] === "true")
        speechSummary += "Pressured, ";

      if (data.Tangential["@value"] && data.Tangential["@value"] === "true")
        speechSummary += "Tangential, ";

      if (data.Incoherent["@value"] && data.Incoherent["@value"] === "true")
        speechSummary += "Incoherent, ";

      if (data.Stutters["@value"] && data.Stutters["@value"] === "true")
        speechSummary += "Stutters, ";

      if (
        data.OtherSpeech["@value"] &&
        data.OtherSpeech["@value"] === "true" &&
        data.OtherSpeechDetails["@value"] &&
        data.OtherSpeechDetails["@value"] !== ""
      )
        speechSummary += `Other: ${data.OtherSpeechDetails["@value"]}, `;

      if (speechSummary !== "")
        mentalStatusSummary += `\r\nSpeech: ${speechSummary.slice(0, -2)}; `;

      if (data.Euthymic["@value"] && data.Euthymic["@value"] === "true")
        moodSummary += "Euthymic, ";

      if (data.Anxious["@value"] && data.Anxious["@value"] === "true")
        moodSummary += "Anxious, ";

      if (data.Angry["@value"] && data.Angry["@value"] === "true")
        moodSummary += "Angry, ";

      if (data.Depressed["@value"] && data.Depressed["@value"] === "true")
        moodSummary += "Depressed, ";

      if (data.Euphoric["@value"] && data.Euphoric["@value"] === "true")
        moodSummary += "Euphoric, ";

      if (data.Irritable["@value"] && data.Irritable["@value"] === "true")
        moodSummary += "Irritable, ";

      if (data.Fearful["@value"] && data.Fearful["@value"] === "true")
        moodSummary += "Fearful, ";

      if (
        data.OtherMood["@value"] &&
        data.OtherMood["@value"] === "true" &&
        data.OtherMoodDetails["@value"] &&
        data.OtherMoodDetails["@value"] !== ""
      )
        moodSummary += `Other: ${data.OtherMoodDetails["@value"]}, `;

      if (moodSummary !== "")
        mentalStatusSummary += `\r\nMood: ${moodSummary.slice(0, -2)}; `;

      if (data.FullRange["@value"] && data.FullRange["@value"] === "true")
        affectSummary += "Full range, ";

      if (data.Restricted["@value"] && data.Restricted["@value"] === "true")
        affectSummary += "Restricted, ";

      if (data.Blunted["@value"] && data.Blunted["@value"] === "true")
        affectSummary += "Blunted, ";

      if (data.Flat["@value"] && data.Flat["@value"] === "true")
        affectSummary += "Flat, ";

      if (
        data.Inappropriate["@value"] &&
        data.Inappropriate["@value"] === "true"
      )
        affectSummary += "Inappropriate, ";

      if (
        data.OtherAffect["@value"] &&
        data.OtherAffect["@value"] === "true" &&
        data.OtherAffectDetails["@value"] &&
        data.OtherAffectDetails["@value"] !== ""
      )
        affectSummary += `Other: ${data.OtherAffectDetails["@value"]}, `;

      if (affectSummary !== "")
        mentalStatusSummary += `\r\nAffect: ${affectSummary.slice(0, -2)}; `;

      if (data.Logical["@value"] && data.Logical["@value"] === "true")
        perceptionSummary += "Logical, ";

      if (
        data.Preoccupations["@value"] &&
        data.Preoccupations["@value"] === "true"
      )
        perceptionSummary += "Preoccupations, ";

      if (data.Delusions["@value"] && data.Delusions["@value"] === "true")
        perceptionSummary += "Delusions, ";

      if (
        data.Hallucinations["@value"] &&
        data.Hallucinations["@value"] === "true"
      )
        perceptionSummary += "Hallucinations, ";

      if (data.Racing["@value"] && data.Racing["@value"] === "true")
        perceptionSummary += "Racing, ";

      if (data.Disorganized["@value"] && data.Disorganized["@value"] === "true")
        perceptionSummary += "Disorganized, ";

      if (data.Concrete["@value"] && data.Concrete["@value"] === "true")
        perceptionSummary += "Concrete, ";

      if (
        data.SuicidalIdeation["@value"] &&
        data.SuicidalIdeation["@value"] === "true"
      )
        perceptionSummary += "Suicidal Ideation, ";

      if (
        data.HomicidalIdeation["@value"] &&
        data.HomicidalIdeation["@value"] === "true"
      )
        perceptionSummary += "Homicidal Ideation, ";

      if (
        data.OtherPerception["@value"] &&
        data.OtherPerception["@value"] === "true" &&
        data.OtherPerceptionDetails["@value"] &&
        data.OtherPerceptionDetails["@value"] !== ""
      )
        perceptionSummary += `Other: ${data.OtherPerceptionDetails["@value"]}, `;

      if (perceptionSummary !== "")
        mentalStatusSummary += `\r\nThought Process/Perception: ${perceptionSummary.slice(
          0,
          -2
        )}; `;

      if (data.NoImpairment["@value"] && data.NoImpairment["@value"] === "true")
        cognitionSummary += "No Impairment, ";

      if (
        data.ShortAttentionSpan["@value"] &&
        data.ShortAttentionSpan["@value"] === "true"
      )
        cognitionSummary += "Short Attention Span, ";

      if (
        data.DifficultyConcentrating["@value"] &&
        data.DifficultyConcentrating["@value"] === "true"
      )
        cognitionSummary += "Difficulty Concentrating, ";

      if (
        data.Distractibility["@value"] &&
        data.Distractibility["@value"] === "true"
      )
        cognitionSummary += "Distractibility, ";

      if (
        data.MemoryImpairment["@value"] &&
        data.MemoryImpairment["@value"] === "true"
      )
        cognitionSummary += "Memory Impairment, ";

      if (
        data.LowIntelligence["@value"] &&
        data.LowIntelligence["@value"] === "true"
      )
        cognitionSummary += "Low Intelligence, ";

      if (
        data.OtherCognition["@value"] &&
        data.OtherCognition["@value"] === "true" &&
        data.OtherCognitionDetails["@value"] &&
        data.OtherCognitionDetails["@value"] !== ""
      )
        cognitionSummary += `Other: ${data.OtherCognitionDetails["@value"]}, `;

      if (cognitionSummary !== "")
        mentalStatusSummary += `\r\nCognition/Intellectual Functioning: ${cognitionSummary.slice(
          0,
          -2
        )}; `;

      if (
        data.AppropriateInsight["@value"] &&
        data.AppropriateInsight["@value"] === "true"
      )
        insightSummary += "Appropriate, ";

      if (data.Fair["@value"] && data.Fair["@value"] === "true")
        insightSummary += "Fair, ";

      if (data.Impaired["@value"] && data.Impaired["@value"] === "true")
        insightSummary += "Impaired, ";

      if (
        data.OtherInsight["@value"] &&
        data.OtherInsight["@value"] === "true" &&
        data.OtherInsightDetails["@value"] &&
        data.OtherInsightDetails["@value"] !== ""
      )
        insightSummary += `Other: ${data.OtherInsightDetails["@value"]}, `;

      if (insightSummary !== "")
        mentalStatusSummary += `\r\nInsight/Judgment: ${insightSummary.slice(
          0,
          -2
        )}; `;

      if (
        data.MentalStatusComments["@value"] &&
        data.MentalStatusComments["@value"] !== ""
      )
        mentalStatusSummary += `\r\nComments/Observations: ${data.MentalStatusComments["@value"]}; `;

      if (mentalStatusSummary !== "")
        summary += `Mental Status:${mentalStatusSummary.trimEnd()}\r\n\r\n`;

      // Lethality
      let lethalitySummary = "";
      if (
        data.SelfHarmComments["@value"] &&
        data.SelfHarmComments["@value"] !== ""
      )
        lethalitySummary += `Suicide/Harm to Self: ${data.SelfHarmComments["@value"]}; `;

      if (
        data.CurrentSelfHarmRisk["@value"] &&
        data.CurrentSelfHarmRisk["@value"] !== ""
      ) {
        lethalitySummary += `Current Risk of Harm to Self: ${data.CurrentSelfHarmRisk["@value"]}; `;
      }
      if (
        data.OtherHarmComments["@value"] &&
        data.OtherHarmComments["@value"] !== ""
      )
        lethalitySummary += `Harm to Others/Endangerment: ${data.OtherHarmComments["@value"]}; `;

      if (
        data.CurrentOtherHarmRisk["@value"] &&
        data.CurrentOtherHarmRisk["@value"] !== ""
      ) {
        lethalitySummary += `Current Risk of Harm to Others: ${data.CurrentOtherHarmRisk["@value"]}; `;
      }

      if (lethalitySummary !== "")
        summary += `Lethality:\r\n${lethalitySummary.trimEnd()}\r\n\r\n`;

      // Symptom Checklist
      let symptomSummary = "";

      if (symptomSummary !== "")
        summary += `Symptom Checklist:\r\n${symptomSummary.slice(
          0,
          -2
        )};\r\n\r\n`;

      // Diagnosis
      if (
        data.DiagnosisDetails["@value"] &&
        data.DiagnosisDetails["@value"] !== ""
      )
        summary += `Diagnosis:\r\n${JSON.parse(data.DiagnosisDetails["@value"])
          .filter((item) => !item.IsEditable)
          .map(
            (item) => `ICD-10 Code: ${item.Code}, Diagnosis: ${item.Diagnosis}`
          )
          .join("; ")};\r\n\r\n`;

      // Treatment Recommendations/Needs
      if (
        data.TreatmentRecommendations["@value"] &&
        data.TreatmentRecommendations["@value"] !== ""
      )
        summary += `Treatment Recommendations:\r\n${JSON.parse(
          data.TreatmentRecommendations["@value"]
        )
          .filter((item) => !item.IsEditable)
          .map((item) => `Recommendation: ${item.Recommendation}`)
          .join("; ")};\r\n\r\n`;

      // Clinical Narrative Summary Comments
      if (
        data.ClinicalNarrativeSummaryComments["@value"] &&
        data.ClinicalNarrativeSummaryComments["@value"] !== ""
      )
        summary += `Additional Comments:\r\n${data.ClinicalNarrativeSummaryComments["@value"]}`;

      summary = summary.trimEnd();

      const obj = { ...data };
      obj.ClinicalNarrativeSummary["@value"] = summary;
      setData(obj);
    }
  };

  const customValidate = (errs) => {
    if (
      (!data.ISClient["@value"] || data.ISClient["@value"] === "false") &&
      (!data.ISParent["@value"] || data.ISParent["@value"] === "false") &&
      (!data.ISGuardian["@value"] || data.ISGuardian["@value"] === "false") &&
      (!data.ISCaregiver["@value"] || data.ISCaregiver["@value"] === "false") &&
      (!data.ISPhysician["@value"] || data.ISPhysician["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISCPS["@value"] || data.ISCPS["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    if (
      data.FamilyVisitationNA["@value"] !== "true" &&
      data.SiblingsUnknown["@value"] !== "true"
    ) {
      const siblingDetails = JSON.parse(data.SiblingDetails["@value"]);

      if (siblingDetails.length === 0) {
        if (errs) errs.SiblingDetails = "Siblings are required.";
        else
          errs = {
            SiblingDetails: "Siblings are required.",
          };
      } else {
        const householdWithoutAllFields = siblingDetails.filter(
          (detail) =>
            detail.Name === undefined ||
            detail.Name === "" ||
            detail.Age === undefined ||
            detail.Age === "" ||
            detail.Situation === undefined ||
            detail.Situation === "" ||
            detail.Restrictions === undefined ||
            detail.Restrictions === ""
        );

        if (householdWithoutAllFields.length > 0) {
          if (errs)
            errs.SiblingDetails = "All fields are required for each Sibling.";
          else
            errs = {
              SiblingDetails: "All fields are required for each Sibling.",
            };
        } else {
          delete errs.SiblingDetails;
        }
      }
    }

    const primaryHouseholdDetails = JSON.parse(
      data.PrimaryHouseholdDetails["@value"]
    );

    if (data.NoMedications["@value"] !== "true") {
      const currentMedicationDetails = JSON.parse(
        data.CurrentMedicationDetails["@value"]
      );

      if (currentMedicationDetails.length === 0) {
        if (errs)
          errs.CurrentMedicationDetails = "Medication details are required.";
        else
          errs = {
            CurrentMedicationDetails: "Medication details are required.",
          };
      } else if (
        currentMedicationDetails.length === 1 &&
        currentMedicationDetails[0].Name === ""
      ) {
        if (errs)
          errs.CurrentMedicationDetails = "Medication details are required.";
        else
          errs = {
            CurrentMedicationDetails: "Medication details are required.",
          };
      }
    }

    if (
      data.PSDType["@value"] === "Initial" ||
      data.PSDType["@value"] === "Review/Update"
    ) {
      if (primaryHouseholdDetails.length === 0) {
        if (errs)
          errs.PrimaryHouseholdDetails =
            "Primary household details are required.";
        else
          errs = {
            PrimaryHouseholdDetails: "Primary household are required.",
          };
      } else {
        const householdWithoutAllFields = primaryHouseholdDetails.filter(
          (detail) =>
            detail.Name === undefined ||
            detail.Name === "" ||
            detail.Relationship === undefined ||
            detail.Relationship === "" ||
            detail.RelationshipQuality === undefined ||
            detail.RelationshipQuality === "" ||
            detail.Comments === undefined ||
            detail.Comments === ""
        );

        if (householdWithoutAllFields.length > 0) {
          if (errs)
            errs.PrimaryHouseholdDetails =
              "All fields are required for each Household.";
          else
            errs = {
              PrimaryHouseholdDetails:
                "All fields are required for each Household.",
            };
        } else {
          delete errs.PrimaryHouseholdDetails;
        }
      }

      if (data.SecondaryHousehold["@value"] === "Yes") {
        const secondaryHouseholdDetails = JSON.parse(
          data.SecondaryHouseholdDetails["@value"]
        );

        if (secondaryHouseholdDetails.length === 0) {
          if (errs)
            errs.SecondaryHouseholdDetails =
              "Secondary household details are required.";
          else
            errs = {
              SecondaryHouseholdDetails:
                "Secondary household details are required.",
            };
        } else {
          const householdWithoutAllFields = secondaryHouseholdDetails.filter(
            (detail) =>
              detail.Name === undefined ||
              detail.Name === "" ||
              detail.Relationship === undefined ||
              detail.Relationship === "" ||
              detail.RelationshipQuality === undefined ||
              detail.RelationshipQuality === "" ||
              detail.Comments === undefined ||
              detail.Comments === ""
          );

          if (householdWithoutAllFields.length > 0) {
            if (errs)
              errs.SecondaryHouseholdDetails =
                "All fields are required for each Household.";
            else
              errs = {
                SecondaryHouseholdDetails:
                  "All fields are required for each Household.",
              };
          } else {
            delete errs.SecondaryHouseholdDetails;
          }
        }

        if (data.SecondaryHouseholdDisciplinePracticesNA["@value"] !== "true") {
          if (
            !data.SecondaryHouseholdCaregiverCooperation["@value"] ||
            data.SecondaryHouseholdCaregiverCooperation["@value"] === ""
          )
            if (errs)
              errs.SecondaryHouseholdCaregiverCooperation = `"Cooperation" is required."`;
            else
              errs = {
                SecondaryHouseholdCaregiverCooperation: `"Cooperation" is required."`,
              };

          if (
            !data.SecondaryHouseholdSupervisionDifficulties["@value"] ||
            data.SecondaryHouseholdSupervisionDifficulties["@value"] === ""
          )
            if (errs)
              errs.SecondaryHouseholdSupervisionDifficulties = `"Supervision difficulties" is required."`;
            else
              errs = {
                SecondaryHouseholdSupervisionDifficulties: `"Supervision difficulties" is required."`,
              };

          if (
            !data.SecondaryHouseholdDisciplinePracticesDetails["@value"] ||
            data.SecondaryHouseholdDisciplinePracticesDetails["@value"] === ""
          )
            if (errs)
              errs.SecondaryHouseholdDisciplinePracticesDetails = `"Discipline practices details" is required."`;
            else
              errs = {
                SecondaryHouseholdDisciplinePracticesDetails: `"Discipline practices details" is required."`,
              };
        }
      }

      if (data.AnySupportivePeople["@value"] === "Yes") {
        const supportivePeopleDetails = JSON.parse(
          data.SupportivePeopleDetails["@value"]
        );

        if (supportivePeopleDetails.length === 0) {
          if (errs)
            errs.SupportivePeopleDetails =
              "Supportive people details are required.";
          else
            errs = {
              SupportivePeopleDetails:
                "Supportive people details are required.",
            };
        } else {
          const peopleWithoutAllFields = supportivePeopleDetails.filter(
            (detail) =>
              detail.Name === undefined ||
              detail.Name === "" ||
              detail.Relationship === undefined ||
              detail.Relationship === "" ||
              detail.RelationshipQuality === undefined ||
              detail.RelationshipQuality === "" ||
              detail.Comments === undefined ||
              detail.Comments === ""
          );

          if (peopleWithoutAllFields.length > 0) {
            if (errs)
              errs.SupportivePeopleDetails =
                "All fields are required for each person.";
            else
              errs = {
                SupportivePeopleDetails:
                  "All fields are required for each person.",
              };
          } else {
            delete errs.SupportivePeopleDetails;
          }
        }
      }

      if (data.FamilyHistoryUnknown["@value"] !== "true") {
        const familyHistoryDetails = JSON.parse(
          data.FamilyHistoryDetails["@value"]
        );

        if (familyHistoryDetails.length === 0) {
          if (errs) errs.FamilyHistoryDetails = "Family history is required.";
          else
            errs = {
              FamilyHistoryDetails: "Family history is required.",
            };
        } else if (
          familyHistoryDetails.length === 1 &&
          familyHistoryDetails[0].FamilyMember === ""
        ) {
          if (errs) errs.FamilyHistoryDetails = "Family history is required.";
          else
            errs = {
              FamilyHistoryDetails: "Family history is required.",
            };
        }
      }

      if (data.TraumaNA["@value"] !== "true") {
        const traumaHistory = JSON.parse(data.TraumaHistory["@value"]);

        if (traumaHistory.length === 0) {
          if (errs) errs.TraumaHistory = "Trauma history is required.";
          else
            errs = {
              TraumaHistory: "Trauma history is required.",
            };
        } else {
          const historyWithoutAllFields = traumaHistory.filter(
            (detail) =>
              detail.Trauma === undefined ||
              detail.Trauma === "" ||
              detail.Status === undefined ||
              detail.Status === "" ||
              detail.Comments === undefined ||
              detail.Comments === ""
          );

          if (historyWithoutAllFields.length > 0) {
            if (errs)
              errs.TraumaHistory =
                "All fields are required for each trauma detail.";
            else
              errs = {
                TraumaHistory:
                  "All fields are required for each trauma detail.",
              };
          } else {
            delete errs.TraumaHistory;
          }
        }
      }

      if (data.TraditionalFosterCare["@value"] !== "Yes") {
        const diagnosisDetails = JSON.parse(data.DiagnosisDetails["@value"]);

        if (diagnosisDetails.length === 0) {
          if (errs) errs.DiagnosisDetails = "Diagnosis details are required.";
          else
            errs = {
              DiagnosisDetails: "Diagnosis details are required.",
            };
        } else {
          const diagnosisWithoutAllFields = diagnosisDetails.filter(
            (detail) =>
              detail.Code === undefined ||
              detail.Code === "" ||
              detail.Diagnosis === undefined ||
              detail.Diagnosis === ""
          );

          if (diagnosisWithoutAllFields.length > 0) {
            if (errs)
              errs.DiagnosisDetails =
                "All fields are required for each diagnosis.";
            else
              errs = {
                DiagnosisDetails: "All fields are required for each diagnosis.",
              };
          } else {
            delete errs.DiagnosisDetails;
          }
        }
      }

      const treatmentRecommendations = JSON.parse(
        data.TreatmentRecommendations["@value"]
      );

      if (treatmentRecommendations.length === 0) {
        if (errs)
          errs.TreatmentRecommendations =
            "Treatment recommendations are required.";
        else
          errs = {
            TreatmentRecommendations: "Treatment recommendations are required.",
          };
      } else if (
        treatmentRecommendations.length === 1 &&
        treatmentRecommendations[0].Recommendation === ""
      ) {
        if (errs)
          errs.TreatmentRecommendations =
            "Treatment recommendations are required.";
        else
          errs = {
            TreatmentRecommendations: "Treatment recommendations are required.",
          };
      }

      if (
        data.MentalHealthNA["@value"] === undefined ||
        data.MentalHealthNA["@value"] !== "true"
      ) {
        if (
          data.PastMentalHealthTreatment["@value"] === undefined ||
          data.PastMentalHealthTreatment["@value"] === ""
        ) {
          if (errs)
            errs.PastMentalHealthTreatment =
              "Missing value for past mental health counseling/treatment";
          else
            errs = {
              PastMentalHealthTreatment:
                "Missing value for past mental health counseling/treatment",
            };
        }

        if (
          data.PastResidentialFacilityPlacement["@value"] === undefined ||
          data.PastResidentialFacilityPlacement["@value"] === ""
        ) {
          if (errs)
            errs.PastResidentialFacilityPlacement =
              "Missing value for past placement in a group home/residential treatment facility";
          else
            errs = {
              PastResidentialFacilityPlacement:
                "Missing value for past placement in a group home/residential treatment facility",
            };
        }

        if (
          data.PastPsychiatricHospitalPlacement["@value"] === undefined ||
          data.PastPsychiatricHospitalPlacement["@value"] === ""
        ) {
          if (errs)
            errs.PastPsychiatricHospitalPlacement =
              "Missing value for past placement in a psychiatric hospital/unit";
          else
            errs = {
              PastPsychiatricHospitalPlacement:
                "Missing value for past placement in a psychiatric hospital/unit",
            };
        }

        if (
          data.InvoluntaryTreatment["@value"] === undefined ||
          data.InvoluntaryTreatment["@value"] === ""
        ) {
          if (errs)
            errs.InvoluntaryTreatment =
              "Missing value for history of involuntary/court-ordered treatment";
          else
            errs = {
              InvoluntaryTreatment:
                "Missing value for history of involuntary/court-ordered treatment",
            };
        }

        if (
          data.MentalHealthDiagnosis["@value"] === undefined ||
          data.MentalHealthDiagnosis["@value"] === ""
        ) {
          if (errs)
            errs.MentalHealthDiagnosis =
              "Missing value for previous or current mental health diagnoses";
          else
            errs = {
              MentalHealthDiagnosis:
                "Missing value for previous or current mental health diagnoses",
            };
        }
      }

      if (data.TraumaNA["@value"] === "false") {
        if (
          (!data.SocialSupports["@value"] ||
            data.SocialSupports["@value"] === "false") &&
          (!data.InterpersonalSkills["@value"] ||
            data.InterpersonalSkills["@value"] === "false") &&
          (!data.ValidatingExperiences["@value"] ||
            data.ValidatingExperiences["@value"] === "false") &&
          (!data.Adaptability["@value"] ||
            data.Adaptability["@value"] === "false") &&
          (!data.SenseOfMastery["@value"] ||
            data.SenseOfMastery["@value"] === "false") &&
          (!data.PositiveOutlook["@value"] ||
            data.PositiveOutlook["@value"] === "false") &&
          (!data.Resources["@value"] || data.Resources["@value"] === "false") &&
          (!data.SecureAttachments["@value"] ||
            data.SecureAttachments["@value"] === "false") &&
          (!data.CaregiverStability["@value"] ||
            data.CaregiverStability["@value"] === "false") &&
          (!data.PlacementStability["@value"] ||
            data.PlacementStability["@value"] === "false") &&
          (!data.TiesToExtendedFamily["@value"] ||
            data.TiesToExtendedFamily["@value"] === "false") &&
          (!data.StrongCulturalIdentity["@value"] ||
            data.StrongCulturalIdentity["@value"] === "false") &&
          (!data.StrongSpiritualBeliefs["@value"] ||
            data.StrongSpiritualBeliefs["@value"] === "false") &&
          (!data.Pets["@value"] || data.Pets["@value"] === "false") &&
          (!data.NoTraumaProtectiveFactors["@value"] ||
            data.NoTraumaProtectiveFactors["@value"] === "false")
        ) {
          if (errs)
            errs.TraumaProtectiveFactors =
              "At least one Protective Factor is required.";
          else
            errs = {
              TraumaProtectiveFactors:
                "At least one Protective Factor is required.",
            };
        }
      }

      if (
        (!data.AppropriateAppearance["@value"] ||
          data.AppropriateAppearance["@value"] === "false") &&
        (!data.Neat["@value"] || data.Neat["@value"] === "false") &&
        (!data.Disheveled["@value"] || data.Disheveled["@value"] === "false") &&
        (!data.BizarreAppearance["@value"] ||
          data.BizarreAppearance["@value"] === "false") &&
        (!data.OtherAppearance["@value"] ||
          data.OtherAppearance["@value"] === "false")
      ) {
        if (errs) errs.MSAppearance = "At least one value is required.";
        else
          errs = {
            MSAppearance: "At least one value is required.",
          };
      }

      if (
        (!data.Cooperative["@value"] ||
          data.Cooperative["@value"] === "false") &&
        (!data.Guarded["@value"] || data.Guarded["@value"] === "false") &&
        (!data.Hyperactive["@value"] ||
          data.Hyperactive["@value"] === "false") &&
        (!data.Agitated["@value"] || data.Agitated["@value"] === "false") &&
        (!data.Paranoid["@value"] || data.Paranoid["@value"] === "false") &&
        (!data.Tics["@value"] || data.Tics["@value"] === "false") &&
        (!data.Impulsive["@value"] || data.Impulsive["@value"] === "false") &&
        (!data.Aggressive["@value"] || data.Aggressive["@value"] === "false") &&
        (!data.Withdrawn["@value"] || data.Withdrawn["@value"] === "false") &&
        (!data.BizarreBehavior["@value"] ||
          data.BizarreBehavior["@value"] === "false") &&
        (!data.Rigidity["@value"] || data.Rigidity["@value"] === "false") &&
        (!data.OtherBehavior["@value"] ||
          data.OtherBehavior["@value"] === "false")
      ) {
        if (errs) errs.MSBehavior = "At least one value is required.";
        else
          errs = {
            MSBehavior: "At least one value is required.",
          };
      }

      if (
        (!data.Normal["@value"] || data.Normal["@value"] === "false") &&
        (!data.Slurred["@value"] || data.Slurred["@value"] === "false") &&
        (!data.Pressured["@value"] || data.Pressured["@value"] === "false") &&
        (!data.Tangential["@value"] || data.Tangential["@value"] === "false") &&
        (!data.Incoherent["@value"] || data.Incoherent["@value"] === "false") &&
        (!data.Stutters["@value"] || data.Stutters["@value"] === "false") &&
        (!data.OtherSpeech["@value"] || data.OtherSpeech["@value"] === "false")
      ) {
        if (errs) errs.MSSpeech = "At least one value is required.";
        else
          errs = {
            MSSpeech: "At least one value is required.",
          };
      }

      if (
        (!data.Euthymic["@value"] || data.Euthymic["@value"] === "false") &&
        (!data.Anxious["@value"] || data.Anxious["@value"] === "false") &&
        (!data.Angry["@value"] || data.Angry["@value"] === "false") &&
        (!data.Depressed["@value"] || data.Depressed["@value"] === "false") &&
        (!data.Euphoric["@value"] || data.Euphoric["@value"] === "false") &&
        (!data.Irritable["@value"] || data.Irritable["@value"] === "false") &&
        (!data.Fearful["@value"] || data.Fearful["@value"] === "false") &&
        (!data.OtherMood["@value"] || data.OtherMood["@value"] === "false")
      ) {
        if (errs) errs.MSMood = "At least one value is required.";
        else
          errs = {
            MSMood: "At least one value is required.",
          };
      }

      if (
        (!data.FullRange["@value"] || data.FullRange["@value"] === "false") &&
        (!data.Restricted["@value"] || data.Restricted["@value"] === "false") &&
        (!data.Blunted["@value"] || data.Blunted["@value"] === "false") &&
        (!data.Flat["@value"] || data.Flat["@value"] === "false") &&
        (!data.Inappropriate["@value"] ||
          data.Inappropriate["@value"] === "false") &&
        (!data.OtherAffect["@value"] || data.OtherAffect["@value"] === "false")
      ) {
        if (errs) errs.MSAffect = "At least one value is required.";
        else
          errs = {
            MSAffect: "At least one value is required.",
          };
      }

      if (
        (!data.Logical["@value"] || data.Logical["@value"] === "false") &&
        (!data.Preoccupations["@value"] ||
          data.Preoccupations["@value"] === "false") &&
        (!data.Delusions["@value"] || data.Delusions["@value"] === "false") &&
        (!data.Hallucinations["@value"] ||
          data.Hallucinations["@value"] === "false") &&
        (!data.Racing["@value"] || data.Racing["@value"] === "false") &&
        (!data.Disorganized["@value"] ||
          data.Disorganized["@value"] === "false") &&
        (!data.Concrete["@value"] || data.Concrete["@value"] === "false") &&
        (!data.SuicidalIdeation["@value"] ||
          data.SuicidalIdeation["@value"] === "false") &&
        (!data.HomicidalIdeation["@value"] ||
          data.HomicidalIdeation["@value"] === "false") &&
        (!data.OtherPerception["@value"] ||
          data.OtherPerception["@value"] === "false")
      ) {
        if (errs) errs.MSPerception = "At least one value is required.";
        else
          errs = {
            MSPerception: "At least one value is required.",
          };
      }

      if (
        (!data.NoImpairment["@value"] ||
          data.NoImpairment["@value"] === "false") &&
        (!data.ShortAttentionSpan["@value"] ||
          data.ShortAttentionSpan["@value"] === "false") &&
        (!data.DifficultyConcentrating["@value"] ||
          data.DifficultyConcentrating["@value"] === "false") &&
        (!data.Distractibility["@value"] ||
          data.Distractibility["@value"] === "false") &&
        (!data.MemoryImpairment["@value"] ||
          data.MemoryImpairment["@value"] === "false") &&
        (!data.LowIntelligence["@value"] ||
          data.LowIntelligence["@value"] === "false") &&
        (!data.OtherCognition["@value"] ||
          data.OtherCognition["@value"] === "false")
      ) {
        if (errs) errs.MSCognition = "At least one value is required.";
        else
          errs = {
            MSCognition: "At least one value is required.",
          };
      }

      if (
        (!data.AppropriateInsight["@value"] ||
          data.AppropriateInsight["@value"] === "false") &&
        (!data.Fair["@value"] || data.Fair["@value"] === "false") &&
        (!data.Impaired["@value"] || data.Impaired["@value"] === "false") &&
        (!data.OtherInsight["@value"] ||
          data.OtherInsight["@value"] === "false")
      ) {
        if (errs) errs.MSInsight = "At least one value is required.";
        else
          errs = {
            MSInsight: "At least one value is required.",
          };
      }

      if (
        data.TraumaSymptoms["@value"] === "true" &&
        (!data.TraumaticExposure["@value"] ||
          data.TraumaticExposure["@value"] === "false") &&
        (!data.AvoidantTrauma["@value"] ||
          data.AvoidantTrauma["@value"] === "false") &&
        (!data.HypervigilanceTrauma["@value"] ||
          data.HypervigilanceTrauma["@value"] === "false") &&
        (!data.IntrusiveDreams["@value"] ||
          data.IntrusiveDreams["@value"] === "false") &&
        (!data.NegativeThoughts["@value"] ||
          data.NegativeThoughts["@value"] === "false") &&
        (!data.CocentrationProblems["@value"] ||
          data.CocentrationProblems["@value"] === "false") &&
        (!data.RecklessBehaviorTrauma["@value"] ||
          data.RecklessBehaviorTrauma["@value"] === "false") &&
        (!data.SleepDisturbancesTrauma["@value"] ||
          data.SleepDisturbancesTrauma["@value"] === "false") &&
        (!data.OtherTrauma["@value"] || data.OtherTrauma["@value"] === "false")
      ) {
        if (errs)
          errs.TraumaSymptoms = "At least one Trauma Symptom is required.";
        else
          errs = {
            TraumaSymptoms: "At least one Trauma Symptom is required.",
          };
      }

      if (
        data.AnxietyDisorder["@value"] === "true" &&
        (!data.AnxietyDisorderExcessWorry["@value"] ||
          data.AnxietyDisorderExcessWorry["@value"] === "false") &&
        (!data.AnxietyDisorderEasilyFatigued["@value"] ||
          data.AnxietyDisorderEasilyFatigued["@value"] === "false") &&
        (!data.MuscleTension["@value"] ||
          data.MuscleTension["@value"] === "false") &&
        (!data.Overworried["@value"] ||
          data.Overworried["@value"] === "false") &&
        (!data.AnxietyDisorderDifficultyConcentrating["@value"] ||
          data.AnxietyDisorderDifficultyConcentrating["@value"] === "false") &&
        (!data.SleepDisturbancesAnxiety["@value"] ||
          data.SleepDisturbancesAnxiety["@value"] === "false") &&
        (!data.RestlessAnxiety["@value"] ||
          data.RestlessAnxiety["@value"] === "false") &&
        (!data.Irritability["@value"] ||
          data.Irritability["@value"] === "false") &&
        (!data.OtherAnxiety["@value"] ||
          data.OtherAnxiety["@value"] === "false")
      ) {
        if (errs)
          errs.AnxietyDisorder = "At least one Anxiety Symptom is required.";
        else
          errs = {
            AnxietyDisorder: "At least one Anxiety Symptom is required.",
          };
      }

      if (
        data.DepressionSymptoms?.["@value"] === "true" &&
        data.DepressedMood?.["@value"] !== "true" &&
        data.SleepChanges?.["@value"] !== "true" &&
        data.Guilt?.["@value"] !== "true" &&
        data.DiminishedInterest?.["@value"] !== "true" &&
        data.PurposelessMovement?.["@value"] !== "true" &&
        data.DifficultyConcentratingDepression?.["@value"] !== "true" &&
        data.WeightChanges?.["@value"] !== "true" &&
        data.FatigueDepression?.["@value"] !== "true" &&
        data.ThoughtsOfDeath?.["@value"] !== "true" &&
        data.OtherDepression?.["@value"] !== "true"
      ) {
        if (errs)
          errs.Depression = "At least one Depression Symptom is required.";
        else
          errs = {
            Depression: "At least one Depression Symptom is required.",
          };
      }

      if (
        data.AttentionHyperactivity["@value"] === "true" &&
        (!data.AttentionHyperactivityEasilyDistracted["@value"] ||
          data.AttentionHyperactivityEasilyDistracted["@value"] === "false") &&
        (!data.DifficultyFollowThrough["@value"] ||
          data.DifficultyFollowThrough["@value"] === "false") &&
        (!data.Forgetful["@value"] || data.Forgetful["@value"] === "false") &&
        (!data.DifficultyListening["@value"] ||
          data.DifficultyListening["@value"] === "false") &&
        (!data.DisorganizedAttention["@value"] ||
          data.DisorganizedAttention["@value"] === "false") &&
        (!data.Fidgety["@value"] || data.Fidgety["@value"] === "false") &&
        (!data.LackOfDetails["@value"] ||
          data.LackOfDetails["@value"] === "false") &&
        (!data.LosesThings["@value"] ||
          data.LosesThings["@value"] === "false") &&
        (!data.InterruptsOthers["@value"] ||
          data.InterruptsOthers["@value"] === "false") &&
        (!data.UnableToSitStill["@value"] ||
          data.UnableToSitStill["@value"] === "false") &&
        (!data.ExcessiveTalking["@value"] ||
          data.ExcessiveTalking["@value"] === "false") &&
        (!data.DifficultyWaiting["@value"] ||
          data.DifficultyWaiting["@value"] === "false") &&
        (!data.OtherAttention["@value"] ||
          data.OtherAttention["@value"] === "false")
      ) {
        if (errs)
          errs.AttentionHyperactivity =
            "At least one Attention and Hyperactivity Symptom is required.";
        else
          errs = {
            AttentionHyperactivity:
              "At least one Attention and Hyperactivity Symptom is required.",
          };
      }

      if (
        data.SU["@value"] === "true" &&
        (!data.PatternOfSubstanceUse["@value"] ||
          data.PatternOfSubstanceUse["@value"] === "false") &&
        (!data.DesiresCutDownUse["@value"] ||
          data.DesiresCutDownUse["@value"] === "false") &&
        (!data.TimeSpentObtaining["@value"] ||
          data.TimeSpentObtaining["@value"] === "false") &&
        (!data.Cravings["@value"] || data.Cravings["@value"] === "false") &&
        (!data.FailureOfResponsibilities["@value"] ||
          data.FailureOfResponsibilities["@value"] === "false") &&
        (!data.NegativeImpact["@value"] ||
          data.NegativeImpact["@value"] === "false") &&
        (!data.AbandonsActivities["@value"] ||
          data.AbandonsActivities["@value"] === "false") &&
        (!data.Tolerance["@value"] || data.Tolerance["@value"] === "false") &&
        (!data.SUWithdrawal["@value"] ||
          data.SUWithdrawal["@value"] === "false") &&
        (!data.SUOther["@value"] || data.SUOther["@value"] === "false")
      ) {
        if (errs) errs.SU = "At least one Substance Use Symptom is required.";
        else
          errs = {
            SU: "At least one Substance Use Symptom is required.",
          };
      }

      if (
        data.DMDD["@value"] === "true" &&
        (!data.Temper["@value"] || data.Temper["@value"] === "false") &&
        (!data.OutsideOutbursts["@value"] ||
          data.OutsideOutbursts["@value"] === "false") &&
        (!data.FrequentOutbursts["@value"] ||
          data.FrequentOutbursts["@value"] === "false") &&
        (!data.DMDDIrritable["@value"] ||
          data.DMDDIrritable["@value"] === "false") &&
        (!data.OutburstsAroundOthers["@value"] ||
          data.OutburstsAroundOthers["@value"] === "false") &&
        (!data.DMDDOther["@value"] || data.DMDDOther["@value"] === "false")
      ) {
        if (errs)
          errs.DMDD =
            "At least one Disruptive Mood Dysregulation Disorder Symptom is required.";
        else
          errs = {
            DMDD: "At least one Disruptive Mood Dysregulation Disorder Symptom is required.",
          };
      }

      if (
        data.ConductDisorder["@value"] === "true" &&
        (!data.Aggression["@value"] || data.Aggression["@value"] === "false") &&
        (!data.DestructionOfProperty["@value"] ||
          data.DestructionOfProperty["@value"] === "false") &&
        (!data.Theft["@value"] || data.Theft["@value"] === "false") &&
        (!data.SeriousViolations["@value"] ||
          data.SeriousViolations["@value"] === "false") &&
        (!data.OtherConductDisorder["@value"] ||
          data.OtherConductDisorder["@value"] === "false")
      ) {
        if (errs)
          errs.ConductDisorder =
            "At least one Conduct Disorder Symptom is required.";
        else
          errs = {
            ConductDisorder:
              "At least one Conduct Disorder Symptom is required.",
          };
      }

      if (
        data.ODD["@value"] === "true" &&
        (!data.IrritableODD["@value"] ||
          data.IrritableODD["@value"] === "false") &&
        (!data.Argumentative["@value"] ||
          data.Argumentative["@value"] === "false") &&
        (!data.Vindictive["@value"] || data.Vindictive["@value"] === "false") &&
        (!data.OtherODD["@value"] || data.OtherODD["@value"] === "false")
      ) {
        if (errs)
          errs.ODD =
            "At least one Oppositional Defiance Disorder Symptom is required.";
        else
          errs = {
            ODD: "At least one Oppositional Defiance Disorder Symptom is required.",
          };
      }

      if (
        data.OCD["@value"] === "true" &&
        (!data.OCDPresence["@value"] ||
          data.OCDPresence["@value"] === "false") &&
        (!data.Obsessions["@value"] || data.Obsessions["@value"] === "false") &&
        (!data.TimeConsuming["@value"] ||
          data.TimeConsuming["@value"] === "false") &&
        (!data.Compulsions["@value"] ||
          data.Compulsions["@value"] === "false") &&
        (!data.ReduceAnxiety["@value"] ||
          data.ReduceAnxiety["@value"] === "false") &&
        (!data.SuppressUrges["@value"] ||
          data.SuppressUrges["@value"] === "false") &&
        (!data.OCDOther["@value"] || data.OCDOther["@value"] === "false")
      ) {
        if (errs)
          errs.OCD =
            "At least one Obsessive-Compulsive Disorder Symptom is required.";
        else
          errs = {
            OCD: "At least one Obsessive-Compulsive Disorder Symptom is required.",
          };
      }

      if (
        data.PanicDisorder["@value"] === "true" &&
        (!data.PoundingHeart["@value"] ||
          data.PoundingHeart["@value"] === "false") &&
        (!data.PanicDisorderSweating["@value"] ||
          data.PanicDisorderSweating["@value"] === "false") &&
        (!data.Trembling["@value"] || data.Trembling["@value"] === "false") &&
        (!data.ShortnessOfBreath["@value"] ||
          data.ShortnessOfBreath["@value"] === "false") &&
        (!data.Choking["@value"] || data.Choking["@value"] === "false") &&
        (!data.ChestPain["@value"] || data.ChestPain["@value"] === "false") &&
        (!data.PanicDisorderNausea["@value"] ||
          data.PanicDisorderNausea["@value"] === "false") &&
        (!data.Dizzy["@value"] || data.Dizzy["@value"] === "false") &&
        (!data.Chills["@value"] || data.Chills["@value"] === "false") &&
        (!data.Derealization["@value"] ||
          data.Derealization["@value"] === "false") &&
        (!data.FearOfCrazy["@value"] ||
          data.FearOfCrazy["@value"] === "false") &&
        (!data.FearOfDying["@value"] ||
          data.FearOfDying["@value"] === "false") &&
        (!data.PanicDisorderOther["@value"] ||
          data.PanicDisorderOther["@value"] === "false")
      ) {
        if (errs)
          errs.PanicDisorder =
            "At least one Panic Disorder Symptom is required.";
        else
          errs = {
            PanicDisorder: "At least one Panic Disorder Symptom is required.",
          };
      }

      if (
        data.Phobias["@value"] === "true" &&
        (!data.PhobiasSpecificFear["@value"] ||
          data.PhobiasSpecificFear["@value"] === "false") &&
        (!data.ImmediateFear["@value"] ||
          data.ImmediateFear["@value"] === "false") &&
        (!data.PhobiasAvoidance["@value"] ||
          data.PhobiasAvoidance["@value"] === "false") &&
        (!data.FearExaggerated["@value"] ||
          data.FearExaggerated["@value"] === "false") &&
        (!data.PhobiasOther["@value"] ||
          data.PhobiasOther["@value"] === "false")
      ) {
        if (errs) errs.Phobias = "At least one Phobia Symptom is required.";
        else
          errs = {
            Phobias: "At least one Phobia Symptom is required.",
          };
      }

      if (
        data.DI["@value"] === "true" &&
        (!data.DILowIQ["@value"] || data.DILowIQ["@value"] === "false") &&
        (!data.DISensoryImpairments["@value"] ||
          data.DISensoryImpairments["@value"] === "false") &&
        (!data.DISpeechDelays["@value"] ||
          data.DISpeechDelays["@value"] === "false") &&
        (!data.DIMotorCoordinationDelays["@value"] ||
          data.DIMotorCoordinationDelays["@value"] === "false") &&
        (!data.DILearningDisorder["@value"] ||
          data.DILearningDisorder["@value"] === "false") &&
        (!data.DISpatialDelays["@value"] ||
          data.DISpatialDelays["@value"] === "false") &&
        (!data.DevelopmentalImpairmentsOther["@value"] ||
          data.DevelopmentalImpairmentsOther["@value"] === "false")
      ) {
        if (errs)
          errs.DI = "At least one Developmental Impairment is required.";
        else
          errs = {
            DI: "At least one Developmenta lImpairment is required.",
          };
      }
    }

    setErrors(errs);
    return errs;
  };

  const sectionsToNavigate = [
    { _id: "PresentingProblem", name: "Presenting Problem & Strengths" },
    { _id: "FamilyVisitationSection", name: "Family Contact and Visitation" },
    { _id: "FamilyHistorySection", name: "Family Environment and History" },
    { _id: "Demographics", name: "Culture/Ethnicity" },
    { _id: "Religion", name: "Religion/Spirituality" },
    { _id: "DevelopmentalHistory", name: "Developmental History" },
    { _id: "Education", name: "Education/Employment" },
    { _id: "Activities", name: "Activities of Daily Living" },
    {
      _id: "Friendships",
      name: "Friendship/Social Peer Support/Relationships",
    },
    { _id: "LegalHistory", name: "Legal History" },
    { _id: "MedicalHistory", name: "Medical History/Medications" },
    {
      _id: "MentalHealthTreatmentHistory",
      name: "Mental Health Treatment History",
    },
    { _id: "SubstanceUseHistory", name: "Alcohol/Drug Use History" },
    { _id: "MentalStatus", name: "Mental Status" },
    { _id: "Lethality", name: "Lethality" },
    { _id: "TraumaHistory", name: "Trauma History" },
    { _id: "SymptomChecklist", name: "Symptom Checklist" },
    { _id: "ClinicalNarrative", name: "Clinical Narrative Summary" },
    { _id: "Diagnosis", name: "Diagnosis" },
    {
      _id: "TreatmentRecommendations",
      name: "Treatment Recommendations/Needs",
    },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={(e) => {
            preSignForm();
            toggleSignatureModal(e);
          }}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="SexAssignedAtBirth"
                    label="Sex Assigned at Birth:"
                    error={errors.SexAssignedAtBirth}
                    value={
                      data.SexAssignedAtBirth !== undefined &&
                      data.SexAssignedAtBirth["@value"]
                    }
                    options={sexAtBirthOptions}
                    onChange={handleChange}
                    identical={compare("SexAssignedAtBirth")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.SexAssignedAtBirth !== undefined &&
                    data.SexAssignedAtBirth["@value"] === "Sex not listed" && (
                      <FormInput
                        name="SexAssignedAtBirthDetails"
                        label="Please specify:"
                        error={errors.SexAssignedAtBirthDetails}
                        value={
                          data.SexAssignedAtBirthDetails !== undefined
                            ? data.SexAssignedAtBirthDetails["@value"]
                            : ""
                        }
                        onChange={handleChange}
                        identical={compare("SexAssignedAtBirthDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
                <div className="col">
                  <FormSelect
                    name="Pronouns"
                    label="Pronouns:"
                    error={errors.Pronouns}
                    value={
                      data.Pronouns !== undefined && data.Pronouns["@value"]
                    }
                    options={pronounOptions}
                    onChange={handleChange}
                    identical={compare("Pronouns")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.Pronouns !== undefined &&
                    data.Pronouns["@value"] === "Prefer to self-describe" && (
                      <FormInput
                        name="PronounsDescription"
                        label="Please describe:"
                        error={errors.PronounsDescription}
                        value={
                          data.PronounsDescription !== undefined
                            ? data.PronounsDescription["@value"]
                            : ""
                        }
                        onChange={handleChange}
                        identical={compare("PronounsDescription")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
                <div className="col">
                  <FormSelect
                    name="GenderIdentity"
                    label="Gender Identity:"
                    error={errors.GenderIdentity}
                    value={
                      data.GenderIdentity !== undefined &&
                      data.GenderIdentity["@value"]
                    }
                    options={genderIdentityOptions}
                    onChange={handleChange}
                    identical={compare("GenderIdentity")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.GenderIdentity !== undefined &&
                    data.GenderIdentity["@value"] === "Identity not listed" && (
                      <FormInput
                        name="GenderIdentityDescription"
                        label="Please specify:"
                        error={errors.GenderIdentityDescription}
                        value={
                          data.GenderIdentityDescription !== undefined
                            ? data.GenderIdentityDescription["@value"]
                            : ""
                        }
                        onChange={handleChange}
                        identical={compare("GenderIdentityDescription")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DateCompleted"
                    label="Date Completed:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormRadioButton
                    name="PSDType"
                    label="Type of PSD:"
                    error={errors.PSDType}
                    value={data.PSDType["@value"]}
                    options={psdTypeOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.PSDType["@value"] === "Review/Update" && (
                    <FormInput
                      name="ReviewReason"
                      label="Reason:"
                      error={errors.ReviewReason}
                      value={getFieldValue(data.ReviewReason)}
                      onChange={handleChange}
                      identical={compare("ReviewReason")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <h5>Information Sources:</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISClient"
                      label="Client"
                      checked={data.ISClient["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISParent"
                      label="Parent/Other"
                      checked={data.ISParent["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISGuardian"
                      label="Guardian"
                      checked={data.ISGuardian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISCaregiver"
                      label="Non-parent Caregiver"
                      checked={data.ISCaregiver["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISPhysician"
                      label="Physician"
                      checked={data.ISPhysician["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISServiceProvider"
                      label="Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3">
                    <FormCheckbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-3">
                    <FormCheckbox
                      name="ISCPS"
                      label="CPS"
                      checked={data.ISCPS["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-3">
                    <FormCheckbox
                      name="ISOther"
                      label="Other"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ISOther["@value"] === "true" && (
                      <FormInput
                        name="ISOtherDetails"
                        error={errors.ISOtherDetails}
                        value={data.ISOtherDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("ISOtherDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              {errors.InformationSources !== undefined && (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              )}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex">
                  <div className="me-2">Clinical Narrative Summary:</div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="PresentingProblem"
                expandStatus={expandStatus}
                className="mt-2"
                title="Presenting Problem &amp; Strengths"
              >
                <FormInput
                  name="ReferralReason"
                  label="Reason for Placement:"
                  error={errors.ReferralReason}
                  value={data.ReferralReason["@value"]}
                  onChange={handleChange}
                  identical={compare("ReferralReason")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="ReferralSource"
                  label="Referral Source and County:"
                  error={errors.ReferralSource}
                  value={data.ReferralSource["@value"]}
                  onChange={handleChange}
                  identical={compare("ReferralSource")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="ReferralDate"
                  label="Date of Referral:"
                  error={errors.ReferralDate}
                  value={data.ReferralDate["@value"]}
                  onChange={handleChange}
                  identical={compare("ReferralDate")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="AcceptDate"
                  label="Date Accepted for Placement:"
                  error={errors.AcceptDate}
                  value={data.AcceptDate["@value"]}
                  onChange={handleChange}
                  identical={compare("AcceptDate")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <>
                    <FormInput
                      name="PresentingProblem"
                      label="Presenting Problem:"
                      error={errors.PresentingProblem}
                      value={data.PresentingProblem["@value"]}
                      onChange={handleChange}
                      identical={compare("PresentingProblem")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="ClientProblem"
                      label="Client Description of Problem (in their own words):"
                      error={errors.ClientProblem}
                      value={data.ClientProblem["@value"]}
                      onChange={handleChange}
                      identical={compare("ClientProblem")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="CaregiverProblem"
                      label="Caregiver Description of Problem (in their own words):"
                      error={errors.CaregiverProblem}
                      value={data.CaregiverProblem["@value"]}
                      onChange={handleChange}
                      identical={compare("CaregiverProblem")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
                <FormInput
                  name="ClientStrengths"
                  label="Client Strengths and Personality (including client, caregiver, and collateral's perspective):"
                  error={errors.ClientStrengths}
                  value={data.ClientStrengths["@value"]}
                  onChange={handleChange}
                  identical={compare("ClientStrengths")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <FormTextArea
                    name="ClientAbilities"
                    label="Client abilities, skills, and interests (narrative):"
                    error={errors.ClientAbilities}
                    value={data.ClientAbilities["@value"]}
                    onChange={handleChange}
                    identical={compare("ClientAbilities")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="FamilyVisitationSection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Contact and Visitation"
              >
                <FormCheckbox
                  name="FamilyVisitationNA"
                  label="N/A"
                  divClassName="ms-3 mt-2"
                  checked={data.FamilyVisitationNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.FamilyVisitationNA["@value"] !== "true" && (
                  <>
                    <b>Mother</b>
                    <br />
                    <FormCheckbox
                      name="MotherUnknown"
                      label="Information unknown and/or there is no contact"
                      divClassName="ms-3 mt-2"
                      checked={data.MotherUnknown["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.MotherUnknown["@value"] !== "true" && (
                      <div className="container border">
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">Name</div>
                          <div className="col pt-2">
                            <FormInput
                              name="MotherName"
                              error={errors.MotherName}
                              value={data.MotherName["@value"]}
                              onChange={handleChange}
                              identical={compare("MotherName")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Phone Number
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="MotherPhone"
                              error={errors.MotherPhone}
                              value={data.MotherPhone["@value"]}
                              onChange={handleChange}
                              identical={compare("MotherPhone")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">Address</div>
                          <div className="col pt-2">
                            <FormInput
                              name="MotherAddress"
                              error={errors.MotherAddress}
                              value={data.MotherAddress["@value"]}
                              onChange={handleChange}
                              identical={compare("MotherAddress")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Other contact info
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="MotherOtherContact"
                              error={errors.MotherOtherContact}
                              value={data.MotherOtherContact["@value"]}
                              onChange={handleChange}
                              identical={compare("MotherOtherContact")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Religion/Culture
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="MotherCulture"
                              error={errors.MotherCulture}
                              value={data.MotherCulture["@value"]}
                              onChange={handleChange}
                              identical={compare("MotherCulture")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Type of contact allowed between youth and mother
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="MotherContact"
                              error={errors.MotherContact}
                              value={data.MotherContact["@value"]}
                              onChange={handleChange}
                              identical={compare("MotherContact")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-3 border-end pt-2">
                            SAFY contact restrictions or specifications
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="MotherRestrictions"
                              error={errors.MotherRestrictions}
                              value={data.MotherRestrictions["@value"]}
                              onChange={handleChange}
                              identical={compare("MotherRestrictions")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <br />
                    <b>Father</b>
                    <br />
                    <FormCheckbox
                      name="FatherUnknown"
                      label="Information unknown and/or there is no contact"
                      divClassName="ms-3 mt-2"
                      checked={data.FatherUnknown["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.FatherUnknown["@value"] !== "true" && (
                      <div className="container border">
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">Name</div>
                          <div className="col pt-2">
                            <FormInput
                              name="FatherName"
                              error={errors.FatherName}
                              value={data.FatherName["@value"]}
                              onChange={handleChange}
                              identical={compare("FatherName")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Phone Number
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="FatherPhone"
                              error={errors.FatherPhone}
                              value={data.FatherPhone["@value"]}
                              onChange={handleChange}
                              identical={compare("FatherPhone")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">Address</div>
                          <div className="col pt-2">
                            <FormInput
                              name="FatherAddress"
                              error={errors.FatherAddress}
                              value={data.FatherAddress["@value"]}
                              onChange={handleChange}
                              identical={compare("FatherAddress")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Other contact info
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="FatherOtherContact"
                              error={errors.FatherOtherContact}
                              value={data.FatherOtherContact["@value"]}
                              onChange={handleChange}
                              identical={compare("FatherOtherContact")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Religion/Culture
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="FatherCulture"
                              error={errors.FatherCulture}
                              value={data.FatherCulture["@value"]}
                              onChange={handleChange}
                              identical={compare("FatherCulture")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-3 border-end pt-2">
                            Type of contact allowed between youth and father
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="FatherContact"
                              error={errors.FatherContact}
                              value={data.FatherContact["@value"]}
                              onChange={handleChange}
                              identical={compare("FatherContact")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-3 border-end pt-2">
                            SAFY contact restrictions or specifications
                          </div>
                          <div className="col pt-2">
                            <FormInput
                              name="FatherRestrictions"
                              error={errors.FatherRestrictions}
                              value={data.FatherRestrictions["@value"]}
                              onChange={handleChange}
                              identical={compare("FatherRestrictions")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <br />
                    <b>Siblings</b>
                    <br />
                    <FormCheckbox
                      name="SiblingsUnknown"
                      label="Information unknown and/or there is no contact"
                      divClassName="ms-3 mt-2"
                      checked={data.SiblingsUnknown["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.SiblingsUnknown["@value"] !== "true" && (
                      <>
                        <FieldTable
                          name="SiblingDetails"
                          commentsHandler={handleComments}
                        >
                          <div className="container border-top border-start border-end mb-2">
                            <div className="row border-bottom">
                              <div className="col border-end py-2">
                                First Name
                              </div>
                              <div className="col border-end py-2">Age</div>
                              <div className="col border-end py-2">
                                Placement Situation
                              </div>
                              <div className="col py-2">
                                Contact/Visitation info or retrictions
                              </div>
                            </div>
                            {data.SiblingDetails["@value"] &&
                              JSON.parse(data.SiblingDetails["@value"]).map(
                                (item, index) => (
                                  <div
                                    key={item._id}
                                    className="row border-bottom"
                                  >
                                    <div className="col border-end py-2 d-flex">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <>
                                          <i
                                            className="fas fa-save me-2 d-print-none"
                                            onClick={() => {
                                              handleSaveRow(
                                                index,
                                                "SiblingDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "SiblingDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <FormInput
                                            name="Name"
                                            value={item.Name}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "SiblingDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <i
                                            className="fas fa-pen me-2 d-print-none"
                                            onClick={() => {
                                              handleEditRow(
                                                index,
                                                "SiblingDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          {item.Name}
                                        </>
                                      )}
                                    </div>
                                    <div className="col border-end py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <FormInput
                                          name="Age"
                                          value={item.Age}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SiblingDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        <>{item.Age}</>
                                      )}
                                    </div>
                                    <div className="col border-end py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <FormInput
                                          name="Situation"
                                          value={item.Situation}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SiblingDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        <>{item.Situation}</>
                                      )}
                                    </div>
                                    <div className="col py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <FormTextArea
                                          name="Restrictions"
                                          value={item.Restrictions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SiblingDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        <>{item.Restrictions}</>
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                          </div>
                          {!formDisabled ? (
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("SiblingDetails", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Sibling
                            </button>
                          ) : null}
                          {errors.SiblingDetails && (
                            <div className="alert alert-danger">
                              {errors.SiblingDetails}
                            </div>
                          )}
                        </FieldTable>
                      </>
                    )}
                    <FormRadioButton
                      name="NoContact"
                      label="Is there anyone the youth CANNOT have contact with?"
                      error={errors.NoContact}
                      value={data.NoContact["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      identical={compare("NoContact")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.NoContact["@value"] === "Yes" && (
                      <FormInput
                        name="NoContactPeople"
                        label="Please indicate:"
                        error={errors.NoContactPeople}
                        value={data.NoContactPeople["@value"]}
                        onChange={handleChange}
                        identical={compare("NoContactPeople")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="HaveVisitation"
                      label="Will the youth have visitation with family?"
                      error={errors.HaveVisitation}
                      value={data.HaveVisitation["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      identical={compare("HaveVisitation")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.HaveVisitation["@value"] === "Yes" && (
                      <FormInput
                        name="VisitationDetails"
                        label="Please indicate frequency, location, and duration of visits:"
                        error={errors.VisitationDetails}
                        value={data.VisitationDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("VisitationDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormInput
                      name="OtherInfo"
                      label="Any other pertinent contact or visitation information:"
                      error={errors.OtherInfo}
                      value={data.OtherInfo["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherInfo")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="FamilyHistorySection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Environment and History"
              >
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <>
                    <FormSelect
                      name="PrimaryHouseholdType"
                      label="Primary Household Type:"
                      error={errors.PrimaryHouseholdType}
                      value={data.PrimaryHouseholdType["@value"]}
                      options={householdOptions}
                      onChange={handleChange}
                      identical={compare("PrimaryHouseholdType")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <strong>Primary Household Members</strong>
                    <FieldTable
                      name="PrimaryHouseholdDetails"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">Name</div>
                          <div className="col border-end py-2">
                            Relationship to Client
                          </div>
                          <div className="col border-end py-2">
                            Quality of Relationship
                          </div>
                          <div className="col py-2">Comment</div>
                        </div>
                        {data.PrimaryHouseholdDetails["@value"] &&
                          JSON.parse(
                            data.PrimaryHouseholdDetails["@value"]
                          ).map((item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "PrimaryHouseholdDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "PrimaryHouseholdDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <FormInput
                                      name="Name"
                                      value={item.Name}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "PrimaryHouseholdDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "PrimaryHouseholdDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    {item.Name}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormSelect
                                    name="Relationship"
                                    value={item.Relationship}
                                    options={relationshipOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PrimaryHouseholdDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  <>{item.Relationship}</>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="RelationshipQuality"
                                    value={item.RelationshipQuality}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PrimaryHouseholdDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  <>{item.RelationshipQuality}</>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormTextArea
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PrimaryHouseholdDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  <>{item.Comments}</>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                      {!formDisabled ? (
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("PrimaryHouseholdDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Household Member
                        </button>
                      ) : null}
                      {errors.PrimaryHouseholdDetails && (
                        <div className="alert alert-danger">
                          {errors.PrimaryHouseholdDetails}
                        </div>
                      )}
                    </FieldTable>
                    <h5>Discipline and Supervision</h5>
                    <FormCheckbox
                      name="PrimaryHouseholdDisciplinePracticesNA"
                      label="N/A"
                      divClassName="ms-3 mt-2"
                      checked={
                        data.PrimaryHouseholdDisciplinePracticesNA["@value"] ===
                        "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.PrimaryHouseholdDisciplinePracticesNA["@value"] !==
                    "true" ? (
                      <>
                        <FormRadioButton
                          name="PrimaryHouseholdCaregiverCooperation"
                          label="Cooperation between caregivers on discipline and care:"
                          error={errors.PrimaryHouseholdCaregiverCooperation}
                          value={
                            data.PrimaryHouseholdCaregiverCooperation["@value"]
                          }
                          options={caregiverCooperationOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormRadioButton
                          name="PrimaryHouseholdSupervisionDifficulties"
                          label="Difficulties with the supervision/monitoring of child:"
                          error={errors.PrimaryHouseholdSupervisionDifficulties}
                          value={
                            data.PrimaryHouseholdSupervisionDifficulties[
                              "@value"
                            ]
                          }
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="PrimaryHouseholdDisciplinePracticesDetails"
                          label="Discipline Practices:"
                          error={
                            errors.PrimaryHouseholdDisciplinePracticesDetails
                          }
                          value={
                            data.PrimaryHouseholdDisciplinePracticesDetails[
                              "@value"
                            ]
                          }
                          onChange={handleChange}
                          identical={compare(
                            "PrimaryHouseholdDisciplinePracticesDetails"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          placeholder="Are practices effective, ineffective, inconsistent, or non-existent? What have caregivers tried in the past? Etc."
                        />
                      </>
                    ) : null}
                    <FormRadioButton
                      name="SecondaryHousehold"
                      label="Is there a secondary household?"
                      error={errors.SecondaryHousehold}
                      value={data.SecondaryHousehold["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SecondaryHousehold["@value"] === "Yes" ? (
                      <>
                        <FormSelect
                          name="SecondaryHouseholdType"
                          label="Secondary Household Type:"
                          error={errors.SecondaryHouseholdType}
                          value={data.SecondaryHouseholdType["@value"]}
                          options={householdOptions}
                          onChange={handleChange}
                          identical={compare("SecondaryHouseholdType")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <strong>Secondary Household Members</strong>
                        <FieldTable
                          name="SecondaryHouseholdDetails"
                          commentsHandler={handleComments}
                        >
                          <div className="container border-top border-start border-end mb-2">
                            <div className="row border-bottom">
                              <div className="col border-end py-2">Name</div>
                              <div className="col border-end py-2">
                                Relationship to Client
                              </div>
                              <div className="col border-end py-2">
                                Quality of Relationship
                              </div>
                              <div className="col py-2">Comment</div>
                            </div>
                            {data.SecondaryHouseholdDetails["@value"] &&
                              JSON.parse(
                                data.SecondaryHouseholdDetails["@value"]
                              ).map((item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "SecondaryHouseholdDetails"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "SecondaryHouseholdDetails"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <FormInput
                                          name="Name"
                                          value={item.Name}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SecondaryHouseholdDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "SecondaryHouseholdDetails"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        {item.Name}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormSelect
                                        name="Relationship"
                                        value={item.Relationship}
                                        options={relationshipOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SecondaryHouseholdDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      <>{item.Relationship}</>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="RelationshipQuality"
                                        value={item.RelationshipQuality}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SecondaryHouseholdDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      <>{item.RelationshipQuality}</>
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormTextArea
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SecondaryHouseholdDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      <>{item.Comments}</>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                          {!formDisabled ? (
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("SecondaryHouseholdDetails", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Household Member
                            </button>
                          ) : null}
                          {errors.SecondaryHouseholdDetails && (
                            <div className="alert alert-danger">
                              {errors.SecondaryHouseholdDetails}
                            </div>
                          )}
                        </FieldTable>
                        <h5>Discipline and Supervision</h5>
                        <FormCheckbox
                          name="SecondaryHouseholdDisciplinePracticesNA"
                          label="N/A"
                          divClassName="ms-3 mt-2"
                          checked={
                            data.SecondaryHouseholdDisciplinePracticesNA &&
                            data.SecondaryHouseholdDisciplinePracticesNA[
                              "@value"
                            ] === "true"
                          }
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                        {data.SecondaryHouseholdDisciplinePracticesNA[
                          "@value"
                        ] !== "true" ? (
                          <>
                            <FormRadioButton
                              name="SecondaryHouseholdCaregiverCooperation"
                              label="Cooperation between caregivers on discipline and care:"
                              error={
                                errors.SecondaryHouseholdCaregiverCooperation
                              }
                              value={
                                data.SecondaryHouseholdCaregiverCooperation[
                                  "@value"
                                ]
                              }
                              options={yesNoOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <FormRadioButton
                              name="SecondaryHouseholdSupervisionDifficulties"
                              label="Difficulties with the supervision/monitoring of child:"
                              error={
                                errors.SecondaryHouseholdSupervisionDifficulties
                              }
                              value={
                                data.SecondaryHouseholdSupervisionDifficulties[
                                  "@value"
                                ]
                              }
                              options={yesNoOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <FormInput
                              name="SecondaryHouseholdDisciplinePracticesDetails"
                              error={
                                errors.SecondaryHouseholdDisciplinePracticesDetails
                              }
                              value={
                                data
                                  .SecondaryHouseholdDisciplinePracticesDetails[
                                  "@value"
                                ]
                              }
                              onChange={handleChange}
                              identical={compare(
                                "SecondaryHouseholdDisciplinePracticesDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                              placeholder="Are practices effective, ineffective, inconsistent, or non-existent? What have caregivers tried in the past? Etc."
                            />
                          </>
                        ) : null}
                      </>
                    ) : null}
                    <FormRadioButton
                      name="AnySupportivePeople"
                      label="Are there any other important or supportive people in the client's life?"
                      error={errors.AnySupportivePeople}
                      value={data.AnySupportivePeople["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.AnySupportivePeople["@value"] === "Yes" ? (
                      <>
                        <strong>Supportive People</strong>
                        <FieldTable
                          name="SupportivePeopleDetails"
                          commentsHandler={handleComments}
                        >
                          <div className="container border-top border-start border-end mb-2">
                            <div className="row border-bottom">
                              <div className="col border-end py-2">Name</div>
                              <div className="col border-end py-2">
                                Relationship to Client
                              </div>
                              <div className="col border-end py-2">
                                Quality of Relationship
                              </div>
                              <div className="col py-2">Comment</div>
                            </div>
                            {data.SupportivePeopleDetails["@value"] &&
                              JSON.parse(
                                data.SupportivePeopleDetails["@value"]
                              ).map((item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "SupportivePeopleDetails"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "SupportivePeopleDetails"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <FormInput
                                          name="Name"
                                          value={item.Name}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SupportivePeopleDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "SupportivePeopleDetails"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        {item.Name}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormSelect
                                        name="Relationship"
                                        value={item.Relationship}
                                        options={relationshipOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SupportivePeopleDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      <>{item.Relationship}</>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="RelationshipQuality"
                                        value={item.RelationshipQuality}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SupportivePeopleDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      <>{item.RelationshipQuality}</>
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormTextArea
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SupportivePeopleDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      <>{item.Comments}</>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                          {!formDisabled ? (
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("SupportivePeopleDetails", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Supportive Person
                            </button>
                          ) : null}
                          {errors.SupportivePeopleDetails && (
                            <div className="alert alert-danger">
                              {errors.SupportivePeopleDetails}
                            </div>
                          )}
                        </FieldTable>
                      </>
                    ) : null}
                    <h5>Family History</h5>
                    <FormCheckbox
                      name="FamilyHistoryUnknown"
                      label="Family History Unknown"
                      divClassName="ms-3"
                      checked={data.FamilyHistoryUnknown["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.FamilyHistoryUnknown["@value"] !== "true" ? (
                      <FieldTable
                        name="FamilyHistoryDetails"
                        commentsHandler={handleComments}
                      >
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col-3 border-end py-2">
                              Family Member
                            </div>
                            <div className="col py-2">Concern/Issue</div>
                          </div>
                          {data.FamilyHistoryDetails["@value"] &&
                            JSON.parse(data.FamilyHistoryDetails["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <>
                                    <div className="col-3 border-end py-2 d-flex">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <>
                                          <i
                                            className="fas fa-save me-2 d-print-none"
                                            onClick={() => {
                                              handleSaveRow(
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <FormSelect
                                            name="FamilyMember"
                                            options={familyMemberOptions}
                                            value={item.FamilyMember}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <i
                                            className="fas fa-pen me-2 d-print-none"
                                            onClick={() => {
                                              handleEditRow(
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          {item.FamilyMember}
                                        </>
                                      )}
                                    </div>
                                    <div className="col py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <div className="container">
                                          <div className="row">
                                            <div className="col">
                                              <FormCheckbox
                                                name={`MentalHealth-${index}`}
                                                label="Mental Health"
                                                checked={
                                                  item.MentalHealth === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                            <div className="col">
                                              <FormCheckbox
                                                name={`Medical-${index}`}
                                                label="Medical"
                                                checked={
                                                  item.Medical === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                            <div className="col">
                                              <FormCheckbox
                                                name={`Disability-${index}`}
                                                label="Disability"
                                                checked={
                                                  item.Disability === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                          </div>
                                          <div className="row">
                                            <div className="col">
                                              <FormCheckbox
                                                name={`SubstanceAbuse-${index}`}
                                                label="Substance Abuse"
                                                checked={
                                                  item.SubstanceAbuse === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                            <div className="col">
                                              <FormCheckbox
                                                name={`Suicidal-${index}`}
                                                label="Suicidal"
                                                checked={
                                                  item.Suicidal === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                            <div className="col">
                                              <FormCheckbox
                                                name={`Legal-${index}`}
                                                label="Legal"
                                                checked={item.Legal === "true"}
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                          </div>
                                          <div className="row">
                                            <div className="col">
                                              <FormCheckbox
                                                name={`Trauma-${index}`}
                                                label="Trauma"
                                                checked={item.Trauma === "true"}
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                            <div className="col">
                                              <FormCheckbox
                                                name={`Perpetration-${index}`}
                                                label="Perpetration"
                                                checked={
                                                  item.Perpetration === "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                            <div className="col">
                                              <FormCheckbox
                                                name={`PartnerViolence-${index}`}
                                                label="Intimate Partner Violence"
                                                checked={
                                                  item.PartnerViolence ===
                                                  "true"
                                                }
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                          </div>
                                          <div className="row">
                                            <div className="col-4">
                                              <FormCheckbox
                                                name={`Other-${index}`}
                                                label="Other"
                                                checked={item.Other === "true"}
                                                onChange={(e) => {
                                                  e.currentTarget = {
                                                    ...e.currentTarget,
                                                    type: "checkbox",
                                                    name: e.currentTarget.name.split(
                                                      "-"
                                                    )[0],
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "FamilyHistoryDetails"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <>{getFamilyMemberConcerns(index)}</>
                                      )}
                                    </div>
                                  </>
                                </div>
                              )
                            )}
                        </div>
                        {!formDisabled ? (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("FamilyHistoryDetails", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Family Member
                          </button>
                        ) : null}
                        {errors.FamilyHistoryDetails && (
                          <div className="alert alert-danger">
                            {errors.FamilyHistoryDetails}
                          </div>
                        )}
                      </FieldTable>
                    ) : null}
                  </>
                ) : null}
                <FormRadioButton
                  name="RemovedFromBioHome"
                  label="Has the youth been removed from biological home?"
                  error={errors.RemovedFromBioHome}
                  value={data.RemovedFromBioHome["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.RemovedFromBioHome["@value"] === "Yes" ? (
                  <FormTextArea
                    name="RemovedFromBioHomeFeelings"
                    label="What are the youth's thoughts/feelings/attitude regarding being removed from their home?"
                    error={errors.RemovedFromBioHomeFeelings}
                    value={data.RemovedFromBioHomeFeelings["@value"]}
                    onChange={handleChange}
                    identical={compare("RemovedFromBioHomeFeelings")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Demographics"
                expandStatus={expandStatus}
                className="mt-2"
                title="Culture/Ethnicity"
              >
                <div className="container">
                  {data.PSDType["@value"] === "Initial" ||
                  data.PSDType["@value"] === "Review/Update" ? (
                    <>
                      <div className="row">
                        <div className="col">Identified cultural group:</div>
                        <div className="col">
                          <FormInput
                            name="CulturalGroup"
                            error={errors.CulturalGroup}
                            value={data.CulturalGroup["@value"]}
                            onChange={handleChange}
                            identical={compare("CulturalGroup")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Special cultural traditions/celebrations/rituals:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="Traditions"
                            error={errors.Traditions}
                            value={data.Traditions["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.Traditions["@value"] === "Yes" ? (
                            <FormInput
                              name="TraditionsComments"
                              label="Comments"
                              error={errors.TraditionsComments}
                              value={data.TraditionsComments["@value"]}
                              onChange={handleChange}
                              identical={compare("TraditionsComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div className="row">
                    <div className="col">Languages spoken:</div>
                    <div className="col">
                      <FormInput
                        name="LanguagesSpoken"
                        error={errors.LanguagesSpoken}
                        value={data.LanguagesSpoken["@value"]}
                        onChange={handleChange}
                        identical={compare("LanguagesSpoken")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {data.PSDType["@value"] === "Initial" ||
                  data.PSDType["@value"] === "Review/Update" ? (
                    <>
                      <div className="row">
                        <div className="col">What are the family's values:</div>
                        <div className="col">
                          <FormInput
                            name="FamilyValues"
                            error={errors.FamilyValues}
                            value={data.FamilyValues["@value"]}
                            onChange={handleChange}
                            identical={compare("FamilyValues")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Negative experiences due to
                          customs/ethnicity/race/SES:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="NegativeCulturalExperience"
                            error={errors.NegativeCulturalExperience}
                            value={data.NegativeCulturalExperience["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.NegativeCulturalExperience["@value"] ===
                            "Yes" && (
                            <FormInput
                              name="NegativeCulturalExperienceComments"
                              label="Comments"
                              error={errors.NegativeCulturalExperienceComments}
                              value={
                                data.NegativeCulturalExperienceComments[
                                  "@value"
                                ]
                              }
                              onChange={handleChange}
                              identical={compare(
                                "NegativeCulturalExperienceComments"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div className="row">
                    <div className="col">Is this youth in foster care?</div>
                    <div className="col">
                      <FormRadioButton
                        name="InFosterCare"
                        error={errors.InFosterCare}
                        value={data.InFosterCare["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {data.InFosterCare["@value"] === "Yes" && (
                    <>
                      <div className="row">
                        <div className="col">
                          Places Lived/Placement History (including place of
                          birth and reasons for moves):
                        </div>
                        <div className="col">
                          <FormInput
                            name="PlacesLived"
                            error={errors.PlacesLived}
                            value={data.PlacesLived["@value"]}
                            onChange={handleChange}
                            identical={compare("PlacesLived")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      {data.PSDType["@value"] === "Initial" ||
                      data.PSDType["@value"] === "Review/Update" ? (
                        <>
                          <div className="row">
                            <div className="col">
                              Differences between youth's and foster/placement
                              family's cultures:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="CulturalDifferences"
                                error={errors.CulturalDifferences}
                                value={data.CulturalDifferences["@value"]}
                                options={yesNoNAOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          {data.CulturalDifferences["@value"] === "Yes" && (
                            <div className="row">
                              <div className="col">Comments:</div>
                              <div className="col">
                                <FormInput
                                  name="CulturalDifferencesComments"
                                  error={errors.CulturalDifferencesComments}
                                  value={
                                    data.CulturalDifferencesComments["@value"]
                                  }
                                  onChange={handleChange}
                                  identical={compare(
                                    "CulturalDifferencesComments"
                                  )}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      ) : null}
                      <div className="row">
                        <div className="col">Permanency plan:</div>
                        <div className="col">
                          <FormSelect
                            name="PermanencyPlan"
                            error={errors.PermanencyPlan}
                            value={data.PermanencyPlan["@value"]}
                            options={permanencyPlanOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PermanencyPlan["@value"] === "Other" && (
                            <FormInput
                              name="PermanencyPlanOther"
                              error={errors.PermanencyPlanOther}
                              value={data.PermanencyPlanOther["@value"]}
                              onChange={handleChange}
                              identical={compare("PermanencyPlanOther")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Religion"
                expandStatus={expandStatus}
                className="mt-2"
                title="Religion/Spirituality"
              >
                <div className="container">
                  <div className="row">
                    <div className="col">
                      Does client have a religious or spiritual belief system?
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Religious"
                        error={errors.Religious}
                        value={data.Religious["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {data.Religious["@value"] === "Yes" && (
                    <div className="row">
                      <div className="col">Comments:</div>
                      <div className="col">
                        <FormInput
                          name="ReligiousComments"
                          error={errors.ReligiousComments}
                          value={data.ReligiousComments["@value"]}
                          onChange={handleChange}
                          identical={compare("ReligiousComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ExpandDiv>
              {data.PSDType["@value"] === "Initial" ||
              data.PSDType["@value"] === "Review/Update" ? (
                <ExpandDiv
                  id="DevelopmentalHistory"
                  expandStatus={expandStatus}
                  className="mt-2"
                  title="Developmental History"
                >
                  <h5>Mother's Pregnancy History:</h5>
                  <FormCheckbox
                    name="PHNoProblems"
                    label="No problems reported"
                    divClassName="ms-3"
                    checked={data.PHNoProblems["@value"] === "true"}
                    comments={parsedComments}
                    addComment={() => setShowComment(true)}
                    editComment={toggleCommentModal}
                    deleteComment={handleDeleteComment}
                    acknowledgeComment={handleAcknowledgeComment}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  {data.PHNoProblems["@value"] === undefined ||
                  data.PHNoProblems["@value"] === "false" ? (
                    <div className="container">
                      <div className="row">
                        <div className="col">Prenatal Care:</div>
                        <div className="col">
                          <FormRadioButton
                            name="PHPrenatalCare"
                            error={errors.PHPrenatalCare}
                            value={data.PHPrenatalCare["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                          {data.PHPrenatalCare["@value"] === "No" ? (
                            <FormTextArea
                              name="PHPrenatalCareComments"
                              error={errors.PHPrenatalCareComments}
                              value={data.PHPrenatalCareComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHPrenatalCareComments")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Exposure to alcohol, tobacco, or other drugs:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="PHExposure"
                            error={errors.PHExposure}
                            value={data.PHExposure["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                          {data.PHExposure["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHExposureComments"
                              error={errors.PHExposureComments}
                              value={data.PHExposureComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHExposureComments")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          History of DV or significant trauma during pregnancy:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="PHTrauma"
                            error={errors.PHTrauma}
                            value={data.PHTrauma["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                          {data.PHTrauma["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHTraumaComments"
                              error={errors.PHTraumaComments}
                              value={data.PHTraumaComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHTraumaComments")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Birth complications:</div>
                        <div className="col">
                          <FormRadioButton
                            name="PHComplications"
                            error={errors.PHComplications}
                            value={data.PHComplications["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                          {data.PHComplications["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHComplicationsComments"
                              error={errors.PHComplicationsComments}
                              value={data.PHComplicationsComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHComplicationsComments")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Medical concerns:</div>
                        <div className="col">
                          <FormRadioButton
                            name="PHConcerns"
                            error={errors.PHConcerns}
                            value={data.PHConcerns["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            comments={parsedComments}
                            addComment={() => setShowComment(true)}
                            editComment={toggleCommentModal}
                            deleteComment={handleDeleteComment}
                            acknowledgeComment={handleAcknowledgeComment}
                            disabled={formDisabled}
                          />
                          {data.PHConcerns["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHConcernsComments"
                              error={errors.PHConcernsComments}
                              value={data.PHConcernsComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHConcernsComments")}
                              comments={parsedComments}
                              addComment={() => setShowComment(true)}
                              editComment={toggleCommentModal}
                              deleteComment={handleDeleteComment}
                              acknowledgeComment={handleAcknowledgeComment}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="container">
                    <div className="row">
                      <div className="col">
                        Have there been any physical, cognitive/IQ, social,
                        emotional delays?
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="DevelopmentalDelays"
                          error={errors.DevelopmentalDelays}
                          value={data.DevelopmentalDelays["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.DevelopmentalDelays["@value"] === "Yes" && (
                          <FormTextArea
                            name="DevelopmentalDelaysDetails"
                            label="Please explain:"
                            error={errors.DevelopmentalDelaysDetails}
                            value={data.DevelopmentalDelaysDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("DevelopmentalDelaysDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        Specialized Services (Speech/Occupational/Physical
                        Therapy):
                      </div>
                      <div className="col">
                        <FormRadioButton
                          name="SpecializedServices"
                          error={errors.SpecializedServices}
                          value={data.SpecializedServices["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SpecializedServices["@value"] === "Yes" && (
                          <FormTextArea
                            name="SpecializedServicesDetails"
                            label="Please explain:"
                            error={errors.SpecializedServicesDetails}
                            value={data.SpecializedServicesDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SpecializedServicesDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </ExpandDiv>
              ) : null}
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2"
                title="Education/Employment"
              >
                <FormRadioButton
                  name="EducationStatus"
                  error={errors.EducationStatus}
                  value={data.EducationStatus["@value"]}
                  options={educationOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {(data.EducationStatus["@value"] === undefined ||
                  data.EducationStatus["@value"] === "" ||
                  data.EducationStatus["@value"] ===
                    "Currently Enrolled in School/Client is School Aged") && (
                  <>
                    <h5>Education</h5>
                    <div className="container">
                      <div className="row">
                        <div className="col">School Name:</div>
                        <div className="col">
                          <FormInput
                            name="SchoolName"
                            error={errors.SchoolName}
                            value={data.SchoolName["@value"]}
                            onChange={handleChange}
                            identical={compare("SchoolName")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">Current Grade:</div>
                        <div className="col">
                          <FormInput
                            name="CurrentGrade"
                            error={errors.CurrentGrade}
                            value={data.CurrentGrade["@value"]}
                            onChange={handleChange}
                            identical={compare("CurrentGrade")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Will youth be transferred to a different school?
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SchoolTransfer"
                            error={errors.SchoolTransfer}
                            value={data.SchoolTransfer["@value"]}
                            options={schoolTransferOptions}
                            onChange={handleChange}
                            identical={compare("SchoolTransfer")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      {data.SchoolTransfer["@value"] ===
                        "BID Meeting Needed" && (
                        <div className="row">
                          <div className="col">
                            BID Meeting Information or next steps:
                          </div>
                          <div className="col">
                            <FormInput
                              name="BIDMeetingInfo"
                              error={errors.BIDMeetingInfo}
                              value={data.BIDMeetingInfo["@value"]}
                              onChange={handleChange}
                              identical={compare("BIDMeetingInfo")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                      )}
                      <div className="row">
                        <div className="col">
                          Who holds educational decision making rights?
                        </div>
                        <div className="col">
                          <FormInput
                            name="SchoolDecisionRights"
                            error={errors.SchoolDecisionRights}
                            value={data.SchoolDecisionRights["@value"]}
                            onChange={handleChange}
                            identical={compare("SchoolDecisionRights")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Regular Classroom:</div>
                        <div className="col">
                          <FormRadioButton
                            name="RegularClassroom"
                            error={errors.RegularClassroom}
                            value={data.RegularClassroom["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">Active 504 Plan:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Active504Plan"
                            error={errors.Active504Plan}
                            value={data.Active504Plan["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-3">Active IEP:</div>
                        <div className="col-3">
                          <FormRadioButton
                            name="ActiveIEP"
                            error={errors.ActiveIEP}
                            value={data.ActiveIEP["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-3">Discipline Concerns:</div>
                        <div className="col-3">
                          <FormRadioButton
                            name="DisciplineConcerns"
                            error={errors.DisciplineConcerns}
                            value={data.DisciplineConcerns["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.DisciplineConcerns["@value"] === "Yes" && (
                            <FormTextArea
                              name="DisciplineConcernsDetails"
                              label="Please explain:"
                              error={errors.DisciplineConcernsDetails}
                              value={data.DisciplineConcernsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("DisciplineConcernsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-3">Awards/Achievements:</div>
                        <div className="col-3">
                          <FormRadioButton
                            name="Awards"
                            error={errors.Awards}
                            value={data.Awards["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.Awards["@value"] === "Yes" && (
                            <FormTextArea
                              name="AwardsDetails"
                              label="Please explain:"
                              error={errors.AwardsDetails}
                              value={data.AwardsDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("AwardsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Youth's attitude toward school:
                        </div>
                        <div className="col">
                          <FormTextArea
                            name="SchoolAttitude"
                            error={errors.SchoolAttitude}
                            value={data.SchoolAttitude["@value"]}
                            onChange={handleChange}
                            identical={compare("SchoolAttitude")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <>
                    <h5>Employment</h5>
                    <FormRadioButton
                      name="CurrentlyEmployed"
                      label="Currently Employed:"
                      error={errors.CurrentlyEmployed}
                      value={data.CurrentlyEmployed["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              {data.PSDType["@value"] === "Initial" ||
              data.PSDType["@value"] === "Review/Update" ? (
                <>
                  <ExpandDiv
                    id="Activities"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Activities of Daily Living"
                  >
                    <FormCheckbox
                      name="ADLNA"
                      label="N/A (Skip Section)"
                      divClassName="ms-3"
                      checked={data.ADLNA["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.ADLNA["@value"] !== "true" && (
                      <>
                        Does the client require additional support in the daily
                        functioning and daily activity skills listed below?
                        <div className="container">
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mb-2">
                            <div className="col">
                              <b>Skill</b>
                            </div>
                            <div className="col">
                              <b>Functioning Level</b>
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Money management:</div>
                            <div className="col">
                              <FormSelect
                                name="MoneyManagement"
                                error={errors.MoneyManagement}
                                value={data.MoneyManagement["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Food management:</div>
                            <div className="col">
                              <FormSelect
                                name="FoodManagement"
                                error={errors.FoodManagement}
                                value={data.FoodManagement["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">
                              Personal appearance and hygiene:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="Hygiene"
                                error={errors.Hygiene}
                                value={data.Hygiene["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">
                              Health awareness/Personal Health:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="Health"
                                error={errors.Health}
                                value={data.Health["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Housekeeping:</div>
                            <div className="col">
                              <FormSelect
                                name="Housekeeping"
                                error={errors.Housekeeping}
                                value={data.Housekeeping["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">
                              Knowledge of community resources:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="ResourceKnowledge"
                                error={errors.ResourceKnowledge}
                                value={data.ResourceKnowledge["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Time management:</div>
                            <div className="col">
                              <FormSelect
                                name="TimeManagement"
                                error={errors.TimeManagement}
                                value={data.TimeManagement["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Interactions with Others:</div>
                            <div className="col">
                              <FormSelect
                                name="Interaction"
                                error={errors.Interaction}
                                value={data.Interaction["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Comments:</div>
                            <div className="col">
                              <FormTextArea
                                name="SkillsComments"
                                error={errors.SkillsComments}
                                value={data.SkillsComments["@value"]}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </ExpandDiv>
                  <ExpandDiv
                    id="Friendships"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Friendship/Social Peer Support/Relationships"
                  >
                    <FormCheckbox
                      name="RelationshipsNA"
                      label="N/A (Skip Section)"
                      divClassName="ms-3"
                      checked={data.RelationshipsNA["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.RelationshipsNA["@value"] !== "true" && (
                      <>
                        <div className="container">
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom">
                            <div className="col">
                              <b>Skill</b>
                            </div>
                            <div className="col">
                              <b>Functioning Level</b>
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Cooperative play skills:</div>
                            <div className="col">
                              <FormSelect
                                name="CoopSkills"
                                error={errors.CoopSkills}
                                value={data.CoopSkills["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">
                              Communication and interaction skills:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="CommunicationSkills"
                                error={errors.CommunicationSkills}
                                value={data.CommunicationSkills["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">
                              Emotional understanding and self-regulation
                              skills:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="EmotionalUnderstanding"
                                error={errors.EmotionalUnderstanding}
                                value={data.EmotionalUnderstanding["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">
                              Social problem-solving skills:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="ProblemSolving"
                                error={errors.ProblemSolving}
                                value={data.ProblemSolving["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">
                              Conflict resolution skills:
                            </div>
                            <div className="col">
                              <FormSelect
                                name="ConflictResolution"
                                error={errors.ConflictResolution}
                                value={data.ConflictResolution["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                            <div className="col">Decision making skills:</div>
                            <div className="col">
                              <FormSelect
                                name="DecisionMakingSkills"
                                error={errors.DecisionMakingSkills}
                                value={data.DecisionMakingSkills["@value"]}
                                options={functioningLevelOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </div>
                        <FormInput
                          name="RelationshipSkillsComments"
                          label="Any Additional Comments on Above Skills:"
                          error={errors.RelationshipSkillsComments}
                          value={data.RelationshipSkillsComments["@value"]}
                          onChange={handleChange}
                          identical={compare("RelationshipSkillsComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormRadioButton
                          name="Bullied"
                          label="History of being bullied:"
                          error={errors.Bullied}
                          value={data.Bullied["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.Bullied["@value"] === "Yes" && (
                          <FormInput
                            name="BulliedDetails"
                            label="Comments:"
                            error={errors.BulliedDetails}
                            value={data.BulliedDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("BulliedDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                        <FormRadioButton
                          name="AcceptedByPeers"
                          label="Accepted by peer group:"
                          error={errors.AcceptedByPeers}
                          value={data.AcceptedByPeers["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.AcceptedByPeers["@value"] === "No" && (
                          <FormInput
                            name="AcceptedByPeersDetails"
                            label="Comments:"
                            error={errors.AcceptedByPeersDetails}
                            value={data.AcceptedByPeersDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("AcceptedByPeersDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                        <FormRadioButton
                          name="NegativeAssociations"
                          label="Negative peer group associations:"
                          error={errors.NegativeAssociations}
                          value={data.NegativeAssociations["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.NegativeAssociations["@value"] === "Yes" && (
                          <FormInput
                            name="NegativeAssociationsDetails"
                            label="Comments:"
                            error={errors.NegativeAssociationsDetails}
                            value={data.NegativeAssociationsDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("NegativeAssociationsDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </>
                    )}
                  </ExpandDiv>
                </>
              ) : null}
              <ExpandDiv
                id="LegalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Legal and/or CPS Involvement and History"
              >
                <FormCheckbox
                  name="LegalNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.LegalNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LegalNA["@value"] !== "true" && (
                  <>
                    {data.PSDType["@value"] === "Initial" ||
                    data.PSDType["@value"] === "Review/Update" ? (
                      <>
                        <FormRadioButton
                          name="LegalInvolvement"
                          label="Does client have any current or past charges involved in the legal system?"
                          error={errors.LegalInvolvement}
                          value={data.LegalInvolvement["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.LegalInvolvement["@value"] === "Yes" && (
                          <FormRadioButton
                            name="OnProbation"
                            label="Is client currently on probation or parole?"
                            error={errors.OnProbation}
                            value={data.OnProbation["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                        <FormRadioButton
                          name="CPSInvolvement"
                          label="Current Children's Protective Services (CPS) Involvement with Family:"
                          error={errors.CPSInvolvement}
                          value={data.CPSInvolvement["@value"]}
                          options={cpsInvolvementOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.CPSInvolvement["@value"] === "Current" ||
                        data.CPSInvolvement["@value"] === "Historical" ? (
                          <FormTextArea
                            name="CPSInvolvementDetails"
                            label="Please explain:"
                            error={errors.CPSInvolvementDetails}
                            value={data.CPSInvolvementDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("CPSInvolvementDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </>
                    ) : null}
                    <div className="row">
                      <div className="col">
                        <FormInput
                          name="CaseworkerName"
                          label="Caseworker Name:"
                          error={errors.CaseworkerName}
                          value={data.CaseworkerName["@value"]}
                          onChange={handleChange}
                          identical={compare("CaseworkerName")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormInput
                          name="CaseworkerPhone"
                          label="Caseworker Phone Number:"
                          error={errors.CaseworkerPhone}
                          value={data.CaseworkerPhone["@value"]}
                          onChange={handleChange}
                          identical={compare("CaseworkerPhone")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormInput
                          name="GALName"
                          label="GAL Name:"
                          error={errors.GALName}
                          value={data.GALName["@value"]}
                          onChange={handleChange}
                          identical={compare("GALName")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormInput
                          name="GALPhone"
                          label="GAL Phone Number:"
                          error={errors.GALPhone}
                          value={data.GALPhone["@value"]}
                          onChange={handleChange}
                          identical={compare("GALPhone")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="MedicalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Medical History/Medications"
              >
                <FormRadioButton
                  name="FirstEntry"
                  label="Is this the client's 1st entry into care or are they re-entering care from home/kinship care/adoption?"
                  error={errors.FirstEntry}
                  value={data.FirstEntry["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.FirstEntry["@value"] === "Yes" && (
                  <h6>
                    <i>
                      Foster parent will need to have the child medically
                      screened within 24 hours of placement.
                    </i>
                  </h6>
                )}
                <FormInput
                  name="MedicalDecisionRights"
                  label="Who holds medical decision making rights?"
                  error={errors.MedicalDecisionRights}
                  value={data.MedicalDecisionRights["@value"]}
                  onChange={handleChange}
                  identical={compare("MedicalDecisionRights")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <b>Medical</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="PCP"
                        label="Primary Care Physician:"
                        error={errors.PCP}
                        value={data.PCP["@value"]}
                        onChange={handleChange}
                        identical={compare("PCP")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="PCPPhone"
                        label="Phone:"
                        error={errors.PCPPhone}
                        value={data.PCPPhone["@value"]}
                        onChange={handleChange}
                        identical={compare("PCPPhone")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="LastMedicalExamDate"
                        label="Date of last medical exam:"
                        error={errors.LastMedicalExamDate}
                        value={data.LastMedicalExamDate["@value"]}
                        onChange={handleChange}
                        identical={compare("LastMedicalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="LastDentalExamDate"
                        label="Date of last dental exam:"
                        error={errors.LastDentalExamDate}
                        value={data.LastDentalExamDate["@value"]}
                        onChange={handleChange}
                        identical={compare("LastDentalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <FormRadioButton
                    name="MedicalInfoReceived"
                    label="Copies of immunization records and doctor/dental appts received:"
                    error={errors.MedicalInfoReceived}
                    value={data.MedicalInfoReceived["@value"]}
                    options={yesNoOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Allergies"
                        label="Allergies"
                        divClassName="ms-3"
                        checked={data.Allergies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Allergies["@value"] === "true" && (
                        <FormInput
                          name="AllergiesDetails"
                          label="List allergies and reactions, including if an epi-pen is needed:"
                          error={errors.AllergiesDetails}
                          value={data.AllergiesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("AllergiesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="MedicalConditions"
                        label="Medical Conditions/Concerns"
                        divClassName="ms-3"
                        checked={data.MedicalConditions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MedicalConditions["@value"] === "true" && (
                        <FormInput
                          name="MedicalConditionsDetails"
                          label="List:"
                          error={errors.MedicalConditionsDetails}
                          value={data.MedicalConditionsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MedicalConditionsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="NutritionalConcerns"
                        label="Nutritional concerns"
                        divClassName="ms-3"
                        checked={data.NutritionalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.NutritionalConcerns["@value"] === "true" && (
                        <FormInput
                          name="NutritionalConcernsDetails"
                          label="List:"
                          error={errors.NutritionalConcernsDetails}
                          value={data.NutritionalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("NutritionalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="DentalConcerns"
                        label="Dental concerns"
                        divClassName="ms-3"
                        checked={data.DentalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.DentalConcerns["@value"] === "true" && (
                        <FormInput
                          name="DentalConcernsDetails"
                          label="List:"
                          error={errors.DentalConcernsDetails}
                          value={data.DentalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("DentalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Concussions"
                        label="Concussions or Head Injuries"
                        divClassName="ms-3"
                        checked={data.Concussions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Concussions["@value"] === "true" && (
                        <FormInput
                          name="ConcussionsDetails"
                          label="List:"
                          error={errors.ConcussionsDetails}
                          value={data.ConcussionsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("ConcussionsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="ConcernsAttention"
                        label="Are there any health (bx or physical) concerns that require attention?"
                        divClassName="ms-3"
                        checked={data.ConcernsAttention["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.ConcernsAttention["@value"] === "true" && (
                        <FormInput
                          name="ConcernsAttentionDetails"
                          label="Please describe:"
                          error={errors.ConcernsAttentionDetails}
                          value={data.ConcernsAttentionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("ConcernsAttentionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Hospitalizations"
                        label="Any hospitalizations or surgeries?"
                        divClassName="ms-3"
                        checked={data.Hospitalizations["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Hospitalizations["@value"] === "true" && (
                        <FormInput
                          name="HospitalizationsDetails"
                          label="Please describe:"
                          error={errors.HospitalizationsDetails}
                          value={data.HospitalizationsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HospitalizationsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="Height"
                        label="Height:"
                        error={errors.Height}
                        value={data.Height["@value"]}
                        onChange={handleChange}
                        identical={compare("Height")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="Weight"
                        label="Weight:"
                        error={errors.Weight}
                        value={data.Weight["@value"]}
                        onChange={handleChange}
                        identical={compare("Weight")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="HairColor"
                        label="Hair Color:"
                        error={errors.HairColor}
                        value={data.HairColor["@value"]}
                        onChange={handleChange}
                        identical={compare("HairColor")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="EyeColor"
                        label="Eye Color:"
                        error={errors.EyeColor}
                        value={data.EyeColor["@value"]}
                        onChange={handleChange}
                        identical={compare("EyeColor")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <FormRadioButton
                  name="Undiagnosed"
                  className="mt-2"
                  label="Client has symptoms that suggest an undiagnosed medical illness that may be causing or contributing to mental health symptoms:"
                  error={errors.Undiagnosed}
                  value={data.Undiagnosed["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.Undiagnosed["@value"] === "Yes" && (
                  <FormTextArea
                    name="UndiagnosedComments"
                    label="Comments:"
                    error={errors.UndiagnosedComments}
                    value={data.UndiagnosedComments["@value"]}
                    onChange={handleChange}
                    identical={compare("UndiagnosedComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <FormTextArea
                  name="MedicalComments"
                  label="Comments:"
                  error={errors.MedicalComments}
                  value={data.MedicalComments["@value"]}
                  onChange={handleChange}
                  identical={compare("MedicalComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <div
                    title={`In the spirit of SAFY services, we like to be able to best advocate for your wishes throughout our partnership, which is why we ask about advance directives. Advance Directives explain how someone wants medical decisions made for them if they cannot make those decisions themselves.
Do you have any advanced directives for the youth in your care?`}
                  >
                    <b>Advance Directives</b> (required for all clients)
                    <i className="fas fa-info-circle"></i>
                    <br />
                    <i>*Inform client that this is not legally binding</i>
                    <FormRadioButton
                      name="AdvanceDirectives"
                      className="mt-2"
                      label="Client has any advance directives in place:"
                      error={errors.AdvanceDirectives}
                      value={data.AdvanceDirectives["@value"]}
                      options={yesNoUnknownOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                ) : null}
                <br />
                <b>Current Medications</b>
                <FormCheckbox
                  name="NoMedications"
                  label="Not currently on medications"
                  divClassName="ms-3"
                  checked={data.NoMedications["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoMedications["@value"] !== "true" ? (
                  <FieldTable
                    name="CurrentMedicationDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Medication</div>
                        <div className="col py-2">30 day supply obtained?</div>
                      </div>
                      {data.CurrentMedicationDetails["@value"]
                        ? JSON.parse(
                            data.CurrentMedicationDetails["@value"]
                          ).map((item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <FormInput
                                      name="Name"
                                      value={item.Name}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    {item.Name}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormRadioButton
                                    name={`SupplyObtained-${index}`}
                                    value={item.SupplyObtained}
                                    options={yesNoOptions}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        value: e.currentTarget.value,
                                      };
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  <>{item.SupplyObtained}</>
                                )}
                              </div>
                            </div>
                          ))
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("CurrentMedicationDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Medication
                        </button>
                      </>
                    ) : null}
                    {errors.CurrentMedicationDetails !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.CurrentMedicationDetails}
                          </div>
                        </div>
                      </div>
                    )}
                    <FormSelect
                      name="MedicationCompliance"
                      label="Compliance with medication:"
                      error={errors.MedicationCompliance}
                      value={data.MedicationCompliance["@value"]}
                      options={complianceOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="SideEffects"
                      label="Side effects from medications:"
                      error={errors.SideEffects}
                      value={data.SideEffects["@value"]}
                      options={yesNoUnknownOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SideEffects["@value"] === "Yes" && (
                      <FormInput
                        name="SideEffectsDetails"
                        label="Describe:"
                        error={errors.SideEffectsDetails}
                        value={data.SideEffectsDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("SideEffectsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </FieldTable>
                ) : null}
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <>
                    <FormRadioButton
                      name="PsychotropicMeds"
                      label="Past psychotropic medications:"
                      error={errors.PsychotropicMeds}
                      value={data.PsychotropicMeds["@value"]}
                      options={yesNoUnknownOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.PsychotropicMeds["@value"] === "Yes" && (
                      <FormInput
                        name="PsychotropicMedsDetails"
                        label="List:"
                        error={errors.PsychotropicMedsDetails}
                        value={data.PsychotropicMedsDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("PsychotropicMedsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <b>
                      Sexual History to Include Pertinent Sexual Issues/Concerns
                    </b>
                    <FormCheckbox
                      name="SexualHistoryNA"
                      label="N/A (youth is 9 or under)"
                      divClassName="ms-3"
                      checked={data.SexualHistoryNA["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.SexualHistoryNA["@value"] !== "true" && (
                      <>
                        <div className="row">
                          <div className="col">
                            <FormSelect
                              name="SexualOrientation"
                              label="Sexual orientation:"
                              error={errors.SexualOrientation}
                              value={data.SexualOrientation["@value"]}
                              options={sexualOrientationOptions}
                              onChange={handleChange}
                              identical={compare("SexualOrientation")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                              title="who someone is physically, romantically, or sexually attracted to"
                            />
                          </div>
                          <div className="col">
                            {data.SexualOrientation !== undefined &&
                              data.SexualOrientation["@value"] ===
                                "Orientation not listed" && (
                                <FormInput
                                  name="SexualOrientationDetails"
                                  label="Please specify:"
                                  error={errors.SexualOrientationDetails}
                                  value={
                                    data.SexualOrientationDetails !== undefined
                                      ? data.SexualOrientationDetails["@value"]
                                      : ""
                                  }
                                  onChange={handleChange}
                                  identical={compare(
                                    "SexualOrientationDetails"
                                  )}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              )}
                          </div>
                        </div>
                        <FormRadioButton
                          name="SexuallyActive"
                          label="Sexually Active:"
                          error={errors.SexuallyActive}
                          value={data.SexuallyActive["@value"]}
                          options={yesNoNAOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SexuallyActive["@value"] === "Yes" && (
                          <div className="ms-3">
                            <FormRadioButton
                              name="CaregiverAware"
                              label="Caregiver aware of sexual history:"
                              error={errors.CaregiverAware}
                              value={data.CaregiverAware["@value"]}
                              options={yesNoOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.CaregiverAware["@value"] === "Yes" && (
                              <FormInput
                                name="CaregiverAwareComments"
                                label="Comments:"
                                error={errors.CaregiverAwareComments}
                                value={data.CaregiverAwareComments["@value"]}
                                onChange={handleChange}
                                identical={compare("CaregiverAwareComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            )}
                            <FormRadioButton
                              name="UseProtection"
                              label="Use of protection:"
                              error={errors.UseProtection}
                              value={data.UseProtection["@value"]}
                              options={useOfProtectionOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.UseProtection["@value"] === "Yes" && (
                              <FormInput
                                name="UseProtectionComments"
                                label="Comments:"
                                error={errors.UseProtectionComments}
                                value={data.UseProtectionComments["@value"]}
                                onChange={handleChange}
                                identical={compare("UseProtectionComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            )}
                            <FormRadioButton
                              name="AwareOfRisks"
                              label="Awareness of sexual risks:"
                              error={errors.AwareOfRisks}
                              value={data.AwareOfRisks["@value"]}
                              options={yesNoOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.AwareOfRisks["@value"] === "Yes" && (
                              <FormInput
                                name="AwareOfRisksComments"
                                label="Comments:"
                                error={errors.AwareOfRisksComments}
                                value={data.AwareOfRisksComments["@value"]}
                                onChange={handleChange}
                                identical={compare("AwareOfRisksComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            )}
                            <FormRadioButton
                              name="Pregnancy"
                              label="Pregnancy:"
                              error={errors.Pregnancy}
                              value={data.Pregnancy["@value"]}
                              options={pregnancyOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.Pregnancy["@value"] === "Current" && (
                              <FormInput
                                name="PregnancyComments"
                                label="Comments:"
                                error={errors.PregnancyComments}
                                value={data.PregnancyComments["@value"]}
                                onChange={handleChange}
                                identical={compare("PregnancyComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            )}
                            <FormRadioButton
                              name="Children"
                              label="Children:"
                              error={errors.Children}
                              value={data.Children["@value"]}
                              options={yesNoNAOptions}
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.Children["@value"] === "Yes" && (
                              <FormInput
                                name="ChildrenComments"
                                label="Comments:"
                                error={errors.ChildrenComments}
                                value={data.ChildrenComments["@value"]}
                                onChange={handleChange}
                                identical={compare("ChildrenComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            )}
                          </div>
                        )}
                        <FormRadioButton
                          name="SexualAbuseVictim"
                          label="Victim of sexual abuse:"
                          error={errors.SexualAbuseVictim}
                          value={data.SexualAbuseVictim["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SexualAbuseVictim["@value"] === "Yes" ? (
                          <FormTextArea
                            name="SexualAbuseVictimComments"
                            label="If known, what is the nature of the relationship with the offender? Is the client still in contact with offender? Are caregivers aware of client being a victim of sexual abuse? Are there any ongoing safety concerns to be aware of?"
                            error={errors.SexualAbuseVictimComments}
                            value={data.SexualAbuseVictimComments["@value"]}
                            onChange={handleChange}
                            identical={compare("SexualAbuseVictimComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                        <FormRadioButton
                          name="SexuallyActingOut"
                          label="Sexually acting out/reactive behavior:"
                          error={errors.SexuallyActingOut}
                          value={data.SexuallyActingOut["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormRadioButton
                          name="SexuallyAbusive"
                          label="Sexually abusive/offending behavior:"
                          error={errors.SexuallyAbusive}
                          value={data.SexuallyAbusive["@value"]}
                          options={yesNoUnknownOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SexuallyActingOut["@value"] === "Yes" ||
                        data.SexuallyAbusive["@value"] === "Yes" ? (
                          <FormInput
                            name="SexuallyAbusiveComments"
                            label="If known, please describe if there is a safety plan in place, if one is needed, etc. Is the client seeking out or currently in SOMB treatment? Any considerations needed for placement and ongoing safety?"
                            error={errors.SexuallyAbusiveComments}
                            value={data.SexuallyAbusiveComments["@value"]}
                            onChange={handleChange}
                            identical={compare("SexuallyAbusiveComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </>
                    )}
                  </>
                ) : null}
              </ExpandDiv>
              {data.PSDType["@value"] === "Initial" ||
              data.PSDType["@value"] === "Review/Update" ? (
                <>
                  <ExpandDiv
                    id="MentalHealthTreatmentHistory"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Mental Health Treatment History"
                  >
                    <FormCheckbox
                      name="MentalHealthNA"
                      label="Unknown or N/A"
                      error={errors.MentalHealthNA}
                      checked={data.MentalHealthNA["@value"] === "true"}
                      options={unknownNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    {data.MentalHealthNA["@value"] !== "true" ? (
                      <>
                        <FormRadioButton
                          name="PastMentalHealthTreatment"
                          label="Past mental health counseling/treatment:"
                          error={errors.PastMentalHealthTreatment}
                          value={data.PastMentalHealthTreatment["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.PastMentalHealthTreatment["@value"] === "Yes" && (
                          <FormInput
                            name="PastMentalHealthTreatmentComments"
                            label="Comments:"
                            error={errors.PastMentalHealthTreatmentComments}
                            value={
                              data.PastMentalHealthTreatmentComments["@value"]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "PastMentalHealthTreatmentComments"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                        <FormRadioButton
                          name="PastResidentialFacilityPlacement"
                          label="Past placement in a group home/residential treatment facility:"
                          error={errors.PastResidentialFacilityPlacement}
                          value={
                            data.PastResidentialFacilityPlacement["@value"]
                          }
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.PastResidentialFacilityPlacement["@value"] ===
                          "Yes" && (
                          <FormInput
                            name="PastResidentialFacilityPlacementComments"
                            label="Comments:"
                            error={
                              errors.PastResidentialFacilityPlacementComments
                            }
                            value={
                              data.PastResidentialFacilityPlacementComments[
                                "@value"
                              ]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "PastResidentialFacilityPlacementComments"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                        <FormRadioButton
                          name="PastPsychiatricHospitalPlacement"
                          label="Past placement in a psychiatric hospital/unit:"
                          error={errors.PastPsychiatricHospitalPlacement}
                          value={
                            data.PastPsychiatricHospitalPlacement["@value"]
                          }
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.PastPsychiatricHospitalPlacement["@value"] ===
                          "Yes" && (
                          <FormInput
                            name="PastPsychiatricHospitalPlacementComments"
                            label="Comments:"
                            error={
                              errors.PastPsychiatricHospitalPlacementComments
                            }
                            value={
                              data.PastPsychiatricHospitalPlacementComments[
                                "@value"
                              ]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "PastPsychiatricHospitalPlacementComments"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                        <FormRadioButton
                          name="InvoluntaryTreatment"
                          label="History of involuntary/court-ordered treatment:"
                          error={errors.InvoluntaryTreatment}
                          value={data.InvoluntaryTreatment["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.InvoluntaryTreatment["@value"] === "Yes" && (
                          <FormInput
                            name="InvoluntaryTreatmentComments"
                            label="Comments:"
                            error={errors.InvoluntaryTreatmentComments}
                            value={data.InvoluntaryTreatmentComments["@value"]}
                            onChange={handleChange}
                            identical={compare("InvoluntaryTreatmentComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                        <FormInput
                          name="MentalHealthDiagnosis"
                          label="Previous or current mental health diagnosis:"
                          error={errors.MentalHealthDiagnosis}
                          value={data.MentalHealthDiagnosis["@value"]}
                          onChange={handleChange}
                          identical={compare("MentalHealthDiagnosis")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    ) : null}
                  </ExpandDiv>
                  <ExpandDiv
                    id="SubstanceUseHistory"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Alcohol/Drug Use History"
                  >
                    <FormCheckbox
                      name="SubstanceUseNA"
                      label="N/A (Skip Section)"
                      divClassName="ms-3"
                      checked={data.SubstanceUseNA["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseNA["@value"] !== "true" && (
                      <>
                        <FormRadioButton
                          name="SubstanceUse"
                          label="Current or past use of Alcohol/Drugs:"
                          error={errors.SubstanceUse}
                          value={data.SubstanceUse["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.SubstanceUse["@value"] !== "No" && (
                          <>
                            <b>History</b>
                            <div className="row">
                              <div className="col">
                                Tobacco related products:
                              </div>
                              <div className="col">
                                <FormRadioButton
                                  name="TobaccoUse"
                                  error={errors.TobaccoUse}
                                  value={data.TobaccoUse["@value"]}
                                  options={substanceUseOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">Alcohol:</div>
                              <div className="col">
                                <FormRadioButton
                                  name="AlcoholUse"
                                  error={errors.AlcoholUse}
                                  value={data.AlcoholUse["@value"]}
                                  options={substanceUseOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">Illegal drugs:</div>
                              <div className="col">
                                <FormRadioButton
                                  name="IllegalDrugUse"
                                  error={errors.IllegalDrugUse}
                                  value={data.IllegalDrugUse["@value"]}
                                  options={substanceUseOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">Prescription drugs:</div>
                              <div className="col">
                                <FormRadioButton
                                  name="PrescriptionDrugUse"
                                  error={errors.PrescriptionDrugUse}
                                  value={data.PrescriptionDrugUse["@value"]}
                                  options={substanceUseOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                Synthetic drugs/substances:
                              </div>
                              <div className="col">
                                <FormRadioButton
                                  name="SyntheticDrugUse"
                                  error={errors.SyntheticDrugUse}
                                  value={data.SyntheticDrugUse["@value"]}
                                  options={substanceUseOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                Medical treatment/hospitalization due to use:
                              </div>
                              <div className="col">
                                <FormRadioButton
                                  name="SubstanceUseTreatmentHistory"
                                  error={errors.SubstanceUseTreatmentHistory}
                                  value={
                                    data.SubstanceUseTreatmentHistory["@value"]
                                  }
                                  options={yesNoOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">Withdrawal:</div>
                              <div className="col">
                                <FormRadioButton
                                  name="SubstanceUseWithdrawal"
                                  error={errors.SubstanceUseWithdrawal}
                                  value={data.SubstanceUseWithdrawal["@value"]}
                                  options={yesNoOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                Presenting detox issues:
                              </div>
                              <div className="col">
                                <FormRadioButton
                                  name="DetoxSymptoms"
                                  error={errors.DetoxSymptoms}
                                  value={data.DetoxSymptoms["@value"]}
                                  options={yesNoOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">Overdose history:</div>
                              <div className="col">
                                <FormRadioButton
                                  name="OverdoseHistory"
                                  error={errors.OverdoseHistory}
                                  value={data.OverdoseHistory["@value"]}
                                  options={yesNoOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                Legal charges/involvement due to use:
                              </div>
                              <div className="col">
                                <FormRadioButton
                                  name="SubstanceUseLegalInvolvement"
                                  error={errors.SubstanceUseLegalInvolvement}
                                  value={
                                    data.SubstanceUseLegalInvolvement["@value"]
                                  }
                                  options={yesNoOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                Use has caused problems in different areas of
                                client's life:
                              </div>
                              <div className="col">
                                <FormRadioButton
                                  name="SubstanceUseProblems"
                                  error={errors.SubstanceUseProblems}
                                  value={data.SubstanceUseProblems["@value"]}
                                  options={yesNoOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <FormTextArea
                              name="SubstanceUseHistoryComments"
                              label="Any additional comments or information:"
                              error={errors.SubstanceUseHistoryComments}
                              value={data.SubstanceUseHistoryComments["@value"]}
                              onChange={handleChange}
                              identical={compare("SubstanceUseHistoryComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            <b>Alcohol/Drug Treatment History</b>
                            <div className="row">
                              <div className="col">
                                Has client had current or past alcohol/drug
                                related treatment?
                              </div>
                              <div className="col">
                                <FormRadioButton
                                  name="SubstanceUseTreatment"
                                  error={errors.SubstanceUseTreatment}
                                  value={data.SubstanceUseTreatment["@value"]}
                                  options={yesNoOptions}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            {data.SubstanceUseTreatment["@value"] === "Yes" && (
                              <div className="row">
                                <div className="col">Comments:</div>
                                <div className="col">
                                  <FormInput
                                    name="SubstanceUseTreatmentComments"
                                    error={errors.SubstanceUseTreatmentComments}
                                    value={
                                      data.SubstanceUseTreatmentComments[
                                        "@value"
                                      ]
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "SubstanceUseTreatmentComments"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </ExpandDiv>
                  <ExpandDiv
                    id="MentalStatus"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Mental Status"
                  >
                    <div className="container">
                      <div className="row border-bottom">
                        <div className="col-3">Appearance</div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="AppropriateAppearance"
                            label="Appropriate"
                            divClassName="ms-3"
                            checked={
                              data.AppropriateAppearance["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Neat"
                            label="Neat"
                            divClassName="ms-3"
                            checked={data.Neat["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Disheveled"
                            label="Disheveled"
                            divClassName="ms-3"
                            checked={data.Disheveled["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="BizarreAppearance"
                            label="Bizarre"
                            divClassName="ms-3"
                            checked={
                              data.BizarreAppearance["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherAppearance"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherAppearance["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherAppearance["@value"] === "true" && (
                            <FormInput
                              name="OtherAppearanceDetails"
                              label="Comments:"
                              error={errors.OtherAppearanceDetails}
                              value={data.OtherAppearanceDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherAppearanceDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSAppearance !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSAppearance}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2 border-bottom">
                        <div className="col-3">Behavior</div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="Cooperative"
                            label="Cooperative"
                            divClassName="ms-3"
                            checked={data.Cooperative["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Guarded"
                            label="Guarded"
                            divClassName="ms-3"
                            checked={data.Guarded["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Hyperactive"
                            label="Hyperactive"
                            divClassName="ms-3"
                            checked={data.Hyperactive["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Agitated"
                            label="Agitated"
                            divClassName="ms-3"
                            checked={data.Agitated["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Paranoid"
                            label="Paranoid"
                            divClassName="ms-3"
                            checked={data.Paranoid["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Tics"
                            label="Tics"
                            divClassName="ms-3"
                            checked={data.Tics["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Impulsive"
                            label="Impulsive"
                            divClassName="ms-3"
                            checked={data.Impulsive["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Aggressive"
                            label="Aggressive"
                            divClassName="ms-3"
                            checked={data.Aggressive["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Withdrawn"
                            label="Withdrawn"
                            divClassName="ms-3"
                            checked={data.Withdrawn["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="BizarreBehavior"
                            label="Bizarre"
                            divClassName="ms-3"
                            checked={data.BizarreBehavior["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Rigidity"
                            label="Rigidity"
                            divClassName="ms-3"
                            checked={data.Rigidity["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherBehavior"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherBehavior["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherBehavior["@value"] === "true" && (
                            <FormInput
                              name="OtherBehaviorDetails"
                              label="Comments:"
                              error={errors.OtherBehaviorDetails}
                              value={data.OtherBehaviorDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherBehaviorDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSBehavior !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSBehavior}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2 border-bottom">
                        <div className="col-3">Speech</div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="Normal"
                            label="Normal"
                            divClassName="ms-3"
                            checked={data.Normal["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Slurred"
                            label="Slurred"
                            divClassName="ms-3"
                            checked={data.Slurred["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Pressured"
                            label="Pressured"
                            divClassName="ms-3"
                            checked={data.Pressured["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Tangential"
                            label="Tangential"
                            divClassName="ms-3"
                            checked={data.Tangential["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Incoherent"
                            label="Incoherent"
                            divClassName="ms-3"
                            checked={data.Incoherent["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Stutters"
                            label="Stutters"
                            divClassName="ms-3"
                            checked={data.Stutters["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherSpeech"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherSpeech["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherSpeech["@value"] === "true" && (
                            <FormInput
                              name="OtherSpeechDetails"
                              label="Comments:"
                              error={errors.OtherSpeechDetails}
                              value={data.OtherSpeechDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherSpeechDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSSpeech !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSSpeech}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2 border-bottom">
                        <div className="col-3">Mood</div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="Euthymic"
                            label="Euthymic"
                            divClassName="ms-3"
                            checked={data.Euthymic["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Anxious"
                            label="Anxious"
                            divClassName="ms-3"
                            checked={data.Anxious["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Angry"
                            label="Angry"
                            divClassName="ms-3"
                            checked={data.Angry["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Depressed"
                            label="Depressed"
                            divClassName="ms-3"
                            checked={data.Depressed["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Euphoric"
                            label="Euphoric"
                            divClassName="ms-3"
                            checked={data.Euphoric["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Irritable"
                            label="Irritable"
                            divClassName="ms-3"
                            checked={data.Irritable["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Fearful"
                            label="Fearful"
                            divClassName="ms-3"
                            checked={data.Fearful["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherMood"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherMood["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherMood["@value"] === "true" && (
                            <FormInput
                              name="OtherMoodDetails"
                              label="Comments:"
                              error={errors.OtherMoodDetails}
                              value={data.OtherMoodDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherMoodDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSMood !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSMood}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2 border-bottom">
                        <div className="col-3">Affect</div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="FullRange"
                            label="Full range"
                            divClassName="ms-3"
                            checked={data.FullRange["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Restricted"
                            label="Restricted"
                            divClassName="ms-3"
                            checked={data.Restricted["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Blunted"
                            label="Blunted"
                            divClassName="ms-3"
                            checked={data.Blunted["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Flat"
                            label="Flat"
                            divClassName="ms-3"
                            checked={data.Flat["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Inappropriate"
                            label="Inappropriate"
                            divClassName="ms-3"
                            checked={data.Inappropriate["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherAffect"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherAffect["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherAffect["@value"] === "true" && (
                            <FormInput
                              name="OtherAffectDetails"
                              label="Comments:"
                              error={errors.OtherAffectDetails}
                              value={data.OtherAffectDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherAffectDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSAffect !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSAffect}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2 border-bottom">
                        <div className="col-3">Thought Process/Perception</div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="Logical"
                            label="Logical"
                            divClassName="ms-3"
                            checked={data.Logical["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Preoccupations"
                            label="Preoccupations"
                            divClassName="ms-3"
                            checked={data.Preoccupations["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Delusions"
                            label="Delusions"
                            divClassName="ms-3"
                            checked={data.Delusions["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Hallucinations"
                            label="Hallucinations"
                            divClassName="ms-3"
                            checked={data.Hallucinations["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Racing"
                            label="Racing"
                            divClassName="ms-3"
                            checked={data.Racing["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Disorganized"
                            label="Disorganized"
                            divClassName="ms-3"
                            checked={data.Disorganized["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Concrete"
                            label="Concrete"
                            divClassName="ms-3"
                            checked={data.Concrete["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="SuicidalIdeation"
                            label="Suicidal Ideation"
                            divClassName="ms-3"
                            checked={data.SuicidalIdeation["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="HomicidalIdeation"
                            label="Homicidal Ideation"
                            divClassName="ms-3"
                            checked={
                              data.HomicidalIdeation["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherPerception"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherPerception["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherPerception["@value"] === "true" && (
                            <FormInput
                              name="OtherPerceptionDetails"
                              label="Comments:"
                              error={errors.OtherPerceptionDetails}
                              value={data.OtherPerceptionDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherPerceptionDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSPerception !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSPerception}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2 border-bottom">
                        <div className="col-3">
                          Cognition/Intellectual Functioning
                        </div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="NoImpairment"
                            label="No Impairment"
                            divClassName="ms-3"
                            checked={data.NoImpairment["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="ShortAttentionSpan"
                            label="Short Attention Span"
                            divClassName="ms-3"
                            checked={
                              data.ShortAttentionSpan["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="DifficultyConcentrating"
                            label="Difficulty Concentrating"
                            divClassName="ms-3"
                            checked={
                              data.DifficultyConcentrating["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Distractibility"
                            label="Distractibility"
                            divClassName="ms-3"
                            checked={data.Distractibility["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="MemoryImpairment"
                            label="Memory Impairment"
                            divClassName="ms-3"
                            checked={data.MemoryImpairment["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="LowIntelligence"
                            label="Low Intelligence"
                            divClassName="ms-3"
                            checked={data.LowIntelligence["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherCognition"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherCognition["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherCognition["@value"] === "true" && (
                            <FormInput
                              name="OtherCognitionDetails"
                              label="Comments:"
                              error={errors.OtherCognitionDetails}
                              value={data.OtherCognitionDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherCognitionDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSCognition !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSCognition}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2 border-bottom">
                        <div className="col-3">Insight/Judgment</div>
                        <div className="col-9 d-flex flex-wrap">
                          <FormCheckbox
                            name="AppropriateInsight"
                            label="Appropriate"
                            divClassName="ms-3"
                            checked={
                              data.AppropriateInsight["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Fair"
                            label="Fair"
                            divClassName="ms-3"
                            checked={data.Fair["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="Impaired"
                            label="Impaired"
                            divClassName="ms-3"
                            checked={data.Impaired["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          <FormCheckbox
                            name="OtherInsight"
                            label="Other"
                            divClassName="ms-3"
                            checked={data.OtherInsight["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ width: "200px" }}
                          />
                          {data.OtherInsight["@value"] === "true" && (
                            <FormInput
                              name="OtherInsightDetails"
                              label="Comments:"
                              error={errors.OtherInsightDetails}
                              value={data.OtherInsightDetails["@value"]}
                              onChange={handleChange}
                              identical={compare("OtherInsightDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      {errors.MSInsight !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MSInsight}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row mt-2">
                        <div className="col-3">Comments/Observations</div>
                        <div className="col-9">
                          <FormTextArea
                            name="MentalStatusComments"
                            error={errors.MentalStatusComments}
                            value={data.MentalStatusComments["@value"]}
                            onChange={handleChange}
                            identical={compare("MentalStatusComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </ExpandDiv>
                </>
              ) : null}
              <ExpandDiv
                id="Lethality"
                expandStatus={expandStatus}
                className="mt-2"
                title="Lethality"
              >
                <FormRadioButton
                  name="Unsupervised"
                  className="mt-2"
                  label="May the youth be left unsupervised?"
                  error={errors.Unsupervised}
                  value={data.Unsupervised["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.Unsupervised["@value"] === "Yes" && (
                  <FormInput
                    name="UnsupervisedComments"
                    label="Describe amount of time and situations:"
                    error={errors.UnsupervisedComments}
                    value={data.UnsupervisedComments["@value"]}
                    onChange={handleChange}
                    identical={compare("UnsupervisedComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <FormInput
                  name="OtherSupervision"
                  label="Any other special supervision needs:"
                  error={errors.OtherSupervision}
                  value={data.OtherSupervision["@value"]}
                  onChange={handleChange}
                  identical={compare("OtherSupervision")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="LethalityNA"
                  label="N/A"
                  divClassName="ms-3"
                  checked={data.LethalityNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LethalityNA["@value"] !== "true" && (
                  <>
                    <b>Suicide/Harm to Self</b>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Self-injurious or Self-abusive Behavior
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="SelfAbusiveBehavior"
                            error={errors.SelfAbusiveBehavior}
                            value={data.SelfAbusiveBehavior["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Suicidal threats/gestures
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="SuicidalThreats"
                            error={errors.SuicidalThreats}
                            value={data.SuicidalThreats["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Suicide Attempt
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="SuicideAttempt"
                            error={errors.SuicideAttempt}
                            value={data.SuicideAttempt["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Parent/Caregiver concerns of suicide/self-harm
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CaregiverConcernsOfSelfHarm"
                            error={errors.CaregiverConcernsOfSelfHarm}
                            value={data.CaregiverConcernsOfSelfHarm["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Other expressed concerns of suicide/self-harm
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="OtherConcernsOfSelfHarm"
                            error={errors.OtherConcernsOfSelfHarm}
                            value={data.OtherConcernsOfSelfHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Current Risk of Harm to Self
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CurrentSelfHarmRisk"
                            error={errors.CurrentSelfHarmRisk}
                            value={data.CurrentSelfHarmRisk["@value"]}
                            options={riskOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <FormTextArea
                      name="SelfHarmComments"
                      label="Please provide any additional information:"
                      error={errors.SelfHarmComments}
                      value={data.SelfHarmComments["@value"]}
                      onChange={handleChange}
                      identical={compare("SelfHarmComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <br />
                    <b>Harm to Others/Endangerment</b>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Threats to harm/endanger others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ThreatsToHarmOthers"
                            error={errors.ThreatsToHarmOthers}
                            value={data.ThreatsToHarmOthers["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Thoughts of harming/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ThoughtsOfHarm"
                            error={errors.ThoughtsOfHarm}
                            value={data.ThoughtsOfHarm["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ActsOfHarm"
                            error={errors.ActsOfHarm}
                            value={data.ActsOfHarm["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts of harm/endangering animals
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ActsOfHarmAnimals"
                            error={errors.ActsOfHarmAnimals}
                            value={data.ActsOfHarmAnimals["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Acts involving weapons
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="ActsInvolvingWeapons"
                            error={errors.ActsInvolvingWeapons}
                            value={data.ActsInvolvingWeapons["@value"]}
                            options={timeframeOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Parent/Caregiver concerns of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CaregiverConcernOfOtherHarm"
                            error={errors.CaregiverConcernOfOtherHarm}
                            value={data.CaregiverConcernOfOtherHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Other expressed concerns of harm/endangering others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="OtherConcernsOfOtherHarm"
                            error={errors.OtherConcernsOfOtherHarm}
                            value={data.OtherConcernsOfOtherHarm["@value"]}
                            options={yesNoNoneOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col border-end py-2">
                          Current Risk of Harm to Others
                        </div>
                        <div className="col py-2">
                          <FormSelect
                            name="CurrentOtherHarmRisk"
                            error={errors.CurrentOtherHarmRisk}
                            value={data.CurrentOtherHarmRisk["@value"]}
                            options={riskOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <FormTextArea
                      name="OtherHarmComments"
                      label="Please provide any additional information:"
                      error={errors.OtherHarmComments}
                      value={data.OtherHarmComments["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherHarmComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SelfAbusiveBehavior["@value"] === "Current" ||
                    data.SelfAbusiveBehavior["@value"] === "Last 30 Days" ||
                    data.SuicidalThreats["@value"] === "Current" ||
                    data.SuicidalThreats["@value"] === "Last 30 Days" ||
                    data.SuicidalThreats["@value"] === "Last 6 Months" ||
                    data.SuicideAttempt["@value"] === "Current" ||
                    data.SuicideAttempt["@value"] === "Last 30 Days" ||
                    data.SuicideAttempt["@value"] === "Last 6 Months" ||
                    data.SuicideAttempt["@value"] === "Last 6-12 Months" ? (
                      <div>
                        Based on the above answers, it is required to complete a{" "}
                        <Link
                          to={`/client/${clientid}?tab=cpass`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          C-SSRS
                        </Link>
                        .
                      </div>
                    ) : null}
                    {data.CurrentSelfHarmRisk["@value"] === "Moderate" ||
                    data.CurrentSelfHarmRisk["@value"] === "High" ||
                    data.CurrentOtherHarmRisk["@value"] === "Moderate" ||
                    data.CurrentOtherHarmRisk["@value"] === "High" ? (
                      <div>
                        Based on the above answers, it is required to complete
                        an{" "}
                        <Link
                          to={`/client/${clientid}?tab=forms`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          F-267 Safety Plan
                        </Link>
                        .
                      </div>
                    ) : null}
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="TraumaHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Trauma History"
              >
                {data.PSDType["@value"] === "Pre-Placement" ? (
                  <>
                    <FormTextArea
                      name="CopingStrategies"
                      label="Coping strategies:"
                      error={errors.CopingStrategies}
                      value={data.CopingStrategies["@value"]}
                      onChange={handleChange}
                      identical={compare("CopingStrategies")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormTextArea
                      name="Triggers"
                      label="Triggers:"
                      error={errors.Triggers}
                      value={data.Triggers["@value"]}
                      onChange={handleChange}
                      identical={compare("Triggers")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <>
                    <FormCheckbox
                      name="TraumaNA"
                      label="No history of abuse or trauma"
                      divClassName="ms-3"
                      checked={data.TraumaNA["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    <br />
                    {data.TraumaNA["@value"] !== "true" && (
                      <>
                        <FieldTable
                          name="TraumaHistory"
                          commentsHandler={handleComments}
                        >
                          <div className="container border-top border-start border-end mb-2">
                            <div className="row border-bottom">
                              <div className="col-3 border-end py-2">
                                Trauma
                              </div>
                              <div className="col-3 border-end py-2">
                                Status
                              </div>
                              <div className="col-6 py-2">Comments</div>
                            </div>
                            {data.TraumaHistory["@value"]
                              ? JSON.parse(data.TraumaHistory["@value"]).map(
                                  (item, index) => (
                                    <div
                                      key={item._id}
                                      className="row border-bottom"
                                    >
                                      <div className="col-3 border-end py-2 d-flex">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <>
                                            <i
                                              className="fas fa-save me-2 d-print-none"
                                              onClick={() => {
                                                handleSaveRow(
                                                  index,
                                                  "TraumaHistory"
                                                );
                                                buildClinicalNarrativeSummary();
                                              }}
                                            ></i>
                                            <i
                                              className="far fa-trash-alt me-2 d-print-none"
                                              onClick={() => {
                                                handleDeleteRow(
                                                  index,
                                                  "TraumaHistory"
                                                );
                                                buildClinicalNarrativeSummary();
                                              }}
                                            ></i>
                                            <FormSelect
                                              name="Trauma"
                                              value={item.Trauma}
                                              options={traumaOptions}
                                              onChange={(e) => {
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "TraumaHistory"
                                                );
                                              }}
                                              disabled={formDisabled}
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <i
                                              className="fas fa-pen me-2 d-print-none"
                                              onClick={() => {
                                                handleEditRow(
                                                  index,
                                                  "TraumaHistory"
                                                );
                                                buildClinicalNarrativeSummary();
                                              }}
                                            ></i>
                                            {item.Trauma}
                                          </>
                                        )}
                                      </div>
                                      <div className="col-3 border-end py-2">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <FormSelect
                                            name="Status"
                                            value={item.Status}
                                            options={statusOptions}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "TraumaHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : (
                                          <>{item.Status}</>
                                        )}
                                      </div>
                                      <div className="col-6 py-2">
                                        {item.IsEditable === undefined ||
                                        item.IsEditable ? (
                                          <FormTextArea
                                            name="Comments"
                                            value={item.Comments}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "TraumaHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : (
                                          <>{item.Comments}</>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )
                              : null}
                          </div>
                          {!formDisabled ? (
                            <>
                              <button
                                className="btn btn-primary mb-2"
                                onClick={() => {
                                  handleAddRow("TraumaHistory", {
                                    IsEditable: true,
                                  });
                                }}
                              >
                                Add Trauma
                              </button>
                            </>
                          ) : null}
                          {errors.TraumaHistory !== undefined && (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.TraumaHistory}
                                </div>
                              </div>
                            </div>
                          )}
                        </FieldTable>
                        <br />
                        <b>Protective Factors:</b>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="SocialSupports"
                                label="Social supports"
                                divClassName="ms-3"
                                checked={
                                  data.SocialSupports["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="SenseOfMastery"
                                label="Sense of mastery"
                                divClassName="ms-3"
                                checked={
                                  data.SenseOfMastery["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="CaregiverStability"
                                label="Caregiver stability"
                                divClassName="ms-3"
                                checked={
                                  data.CaregiverStability["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="StrongSpiritualBeliefs"
                                label="Strong spiritual beliefs"
                                divClassName="ms-3"
                                checked={
                                  data.StrongSpiritualBeliefs["@value"] ===
                                  "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="InterpersonalSkills"
                                label="Interpersonal skills"
                                divClassName="ms-3"
                                checked={
                                  data.InterpersonalSkills["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="PositiveOutlook"
                                label="Positive outlook"
                                divClassName="ms-3"
                                checked={
                                  data.PositiveOutlook["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="PlacementStability"
                                label="Placement stability"
                                divClassName="ms-3"
                                checked={
                                  data.PlacementStability["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Pets"
                                label="Pets"
                                divClassName="ms-3"
                                checked={data.Pets["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="ValidatingExperiences"
                                label="Validating experiences"
                                divClassName="ms-3"
                                checked={
                                  data.ValidatingExperiences["@value"] ===
                                  "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Resources"
                                label="Resources"
                                divClassName="ms-3"
                                checked={data.Resources["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="TiesToExtendedFamily"
                                label="Ties to extended family"
                                divClassName="ms-3"
                                checked={
                                  data.TiesToExtendedFamily["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Adaptability"
                                label="Adaptability"
                                divClassName="ms-3"
                                checked={data.Adaptability["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="SecureAttachments"
                                label="Secure attachments"
                                divClassName="ms-3"
                                checked={
                                  data.SecureAttachments["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="StrongCulturalIdentity"
                                label="Strong cultural identity"
                                divClassName="ms-3"
                                checked={
                                  data.StrongCulturalIdentity["@value"] ===
                                  "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="NoTraumaProtectiveFactors"
                                label="None reported"
                                divClassName="ms-3"
                                checked={
                                  data.NoTraumaProtectiveFactors &&
                                  data.NoTraumaProtectiveFactors["@value"] ===
                                    "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col"></div>
                          </div>
                        </div>
                        {errors.TraumaProtectiveFactors !== undefined && (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.TraumaProtectiveFactors}
                              </div>
                            </div>
                          </div>
                        )}
                        <FormTextArea
                          name="CopingStrategies"
                          label="Coping strategies:"
                          error={errors.CopingStrategies}
                          value={data.CopingStrategies["@value"]}
                          onChange={handleChange}
                          identical={compare("CopingStrategies")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormTextArea
                          name="Triggers"
                          label="Triggers:"
                          error={errors.Triggers}
                          value={data.Triggers["@value"]}
                          onChange={handleChange}
                          identical={compare("Triggers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                  </>
                ) : null}
              </ExpandDiv>
              {data.PSDType["@value"] === "Initial" ||
              data.PSDType["@value"] === "Review/Update" ? (
                <>
                  <ExpandDiv
                    id="SymptomChecklist"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Symptom Checklist"
                  >
                    Mark if symptoms of diagnostic category are present.
                    Describe symptom <b>frequency, intensity and duration</b> if
                    present to assist in formulating diagnosis.
                    <br />
                    <div className="border rounded mb-2 px-2 pt-2 mt-2">
                      <FormCheckbox
                        name="TraumaSymptoms"
                        label="Symptoms of Trauma and Other Stressor-Related Disorders"
                        divClassName="ms-3 fw-bold"
                        checked={data.TraumaSymptoms["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleTraumaSymptomsChange}
                        disabled={formDisabled}
                      />
                      {data.TraumaSymptoms["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="TraumaticExposure"
                                  label="Exposure to a traumatic event"
                                  divClassName="ms-3"
                                  checked={
                                    data.TraumaticExposure["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="AvoidantTrauma"
                                  label="Persistent avoidant behavior"
                                  divClassName="ms-3"
                                  checked={
                                    data.AvoidantTrauma["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="HypervigilanceTrauma"
                                  label="Hypervigilance"
                                  divClassName="ms-3"
                                  checked={
                                    data.HypervigilanceTrauma["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="IntrusiveDreams"
                                  label="Intrusive dreams/memories"
                                  divClassName="ms-3"
                                  checked={
                                    data.IntrusiveDreams["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="NegativeThoughts"
                                  label="Negative thoughts and mood"
                                  divClassName="ms-3"
                                  checked={
                                    data.NegativeThoughts["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="CocentrationProblems"
                                  label="Problems with concentration"
                                  divClassName="ms-3"
                                  checked={
                                    data.CocentrationProblems["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="RecklessBehaviorTrauma"
                                  label="Irritable or reckless behavior"
                                  divClassName="ms-3"
                                  checked={
                                    data.RecklessBehaviorTrauma["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="SleepDisturbancesTrauma"
                                  label="Sleep disturbances"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepDisturbancesTrauma["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="OtherTrauma"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherTrauma["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherTrauma["@value"] === "true" ? (
                                  <FormInput
                                    name="OtherTraumaDetails"
                                    error={errors.OtherTraumaDetails}
                                    value={
                                      data.OtherTraumaDetails !== undefined
                                        ? data.OtherTraumaDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("OtherTraumaDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.TraumaSymptoms !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.TraumaSymptoms}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="TraumaSymptomsFrequency"
                            label="Frequency:"
                            error={errors.TraumaSymptomsFrequency}
                            value={
                              data.TraumaSymptomsFrequency !== undefined
                                ? data.TraumaSymptomsFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="TraumaSymptomsIntensity"
                            label="Intensity:"
                            error={errors.TraumaSymptomsIntensity}
                            value={
                              data.TraumaSymptomsIntensity !== undefined
                                ? data.TraumaSymptomsIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="TraumaSymptomsDuration"
                            label="Duration:"
                            error={errors.TraumaSymptomsDuration}
                            value={
                              data.TraumaSymptomsDuration !== undefined
                                ? data.TraumaSymptomsDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="TraumaSymptomsOnset"
                            label="Onset:"
                            error={errors.TraumaSymptomsOnset}
                            value={
                              data.TraumaSymptomsOnset !== undefined
                                ? data.TraumaSymptomsOnset["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("TraumaSymptomsOnset")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnoses to Consider: Post-traumatic Stress Disorder
                          (PTSD), Acute Stress Disorder, Adjustment Disorder,
                          Other Specified Trauma-and Stressor-Related Disorder
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="AnxietyDisorder"
                        label="Symptoms of Anxiety"
                        divClassName="ms-3 fw-bold"
                        checked={data.AnxietyDisorder["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleAnxietyDisorderChange}
                        disabled={formDisabled}
                      />
                      {data.AnxietyDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="AnxietyDisorderExcessWorry"
                                  label="Excess worry"
                                  divClassName="ms-3"
                                  checked={
                                    data.AnxietyDisorderExcessWorry[
                                      "@value"
                                    ] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="AnxietyDisorderEasilyFatigued"
                                  label="Easily fatigued"
                                  divClassName="ms-3"
                                  checked={
                                    data.AnxietyDisorderEasilyFatigued !==
                                      undefined &&
                                    data.AnxietyDisorderEasilyFatigued[
                                      "@value"
                                    ] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="MuscleTension"
                                  label="Muscle tension"
                                  divClassName="ms-3"
                                  checked={
                                    data.MuscleTension["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="Overworried"
                                  label="Difficulty controlling worry"
                                  divClassName="ms-3"
                                  checked={
                                    data.Overworried["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="AnxietyDisorderDifficultyConcentrating"
                                  label="Difficulty concentrating"
                                  divClassName="ms-3"
                                  checked={
                                    data.AnxietyDisorderDifficultyConcentrating !==
                                      undefined &&
                                    data.AnxietyDisorderDifficultyConcentrating[
                                      "@value"
                                    ] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="SleepDisturbancesAnxiety"
                                  label="Sleep difficulties"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepDisturbancesAnxiety["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="RestlessAnxiety"
                                  label="Restless"
                                  divClassName="ms-3"
                                  checked={
                                    data.RestlessAnxiety["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Irritability"
                                  label="Irritability"
                                  divClassName="ms-3"
                                  checked={
                                    data.Irritability["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="OtherAnxiety"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherAnxiety["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.OtherAnxiety["@value"] === "true" ? (
                                  <FormInput
                                    name="OtherAnxietyDetails"
                                    error={errors.OtherAnxietyDetails}
                                    value={
                                      data.OtherAnxietyDetails !== undefined
                                        ? data.OtherAnxietyDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("OtherAnxietyDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.AnxietyDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.AnxietyDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="AnxietyDisorderFrequency"
                            label="Frequency:"
                            error={errors.AnxietyDisorderFrequency}
                            value={
                              data.AnxietyDisorderFrequency !== undefined
                                ? data.AnxietyDisorderFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("AnxietyDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="AnxietyDisorderIntensity"
                            label="Intensity:"
                            error={errors.AnxietyDisorderIntensity}
                            value={
                              data.AnxietyDisorderIntensity !== undefined
                                ? data.AnxietyDisorderIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("AnxietyDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="AnxietyDisorderDuration"
                            label="Duration:"
                            error={errors.AnxietyDisorderDuration}
                            value={
                              data.AnxietyDisorderDuration !== undefined
                                ? data.AnxietyDisorderDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("AnxietyDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Generalized Anxiety Disorder
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="DepressionSymptoms"
                        label="Symptoms of Depression"
                        divClassName="ms-3 fw-bold"
                        checked={data.DepressionSymptoms["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleDepressionChange}
                        disabled={formDisabled}
                      />
                      {data.DepressionSymptoms["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="DepressedMood"
                                  label="Depressed mood most of the day, nearly every day"
                                  divClassName="ms-3"
                                  checked={
                                    data.DepressedMood["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="SleepChanges"
                                  label="Insomnia or hypersomnia"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepChanges["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Guilt"
                                  label="Feelings of worthlessness/guilt"
                                  divClassName="ms-3"
                                  checked={data.Guilt["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="DiminishedInterest"
                                  label="Diminished interest or pleasure in most daily activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.DiminishedInterest["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="PurposelessMovement"
                                  label="Purposeless movement"
                                  divClassName="ms-3"
                                  checked={
                                    data.PurposelessMovement["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DifficultyConcentratingDepression"
                                  label="Inability to think/concentrate"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyConcentratingDepression !==
                                      undefined &&
                                    data.DifficultyConcentratingDepression[
                                      "@value"
                                    ] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="WeightChanges"
                                  label="Significant weight loss or weight gain"
                                  divClassName="ms-3"
                                  checked={
                                    data.WeightChanges["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="FatigueDepression"
                                  label="Fatigue"
                                  divClassName="ms-3"
                                  checked={
                                    data.FatigueDepression["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="ThoughtsOfDeath"
                                  label="Recurrent thoughts of death"
                                  divClassName="ms-3"
                                  checked={
                                    data.ThoughtsOfDeath["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="OtherDepression"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherDepression["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherDepression["@value"] === "true" ? (
                                  <FormInput
                                    name="OtherDepressionDetails"
                                    error={errors.OtherDepressionDetails}
                                    value={
                                      data.OtherDepressionDetails !== undefined
                                        ? data.OtherDepressionDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "OtherDepressionDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DepressionSymptoms !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DepressionSymptoms}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="DepressionFrequency"
                            label="Frequency:"
                            error={errors.DepressionFrequency}
                            value={
                              data.DepressionFrequency !== undefined
                                ? data.DepressionFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("DepressionFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="DepressionIntensity"
                            label="Intensity:"
                            error={errors.DepressionIntensity}
                            value={
                              data.DepressionIntensity !== undefined
                                ? data.DepressionIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("DepressionIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="DepressionDuration"
                            label="Duration:"
                            error={errors.DepressionDuration}
                            value={
                              data.DepressionDuration !== undefined
                                ? data.DepressionDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("DepressionDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Major Depressive Disorder,
                          Other Specified Depressive Disorder, Unspecified
                          Depressive Disorder
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="AttentionHyperactivity"
                        label="Symptoms of Attention and Hyperactivity"
                        divClassName="ms-3 fw-bold"
                        checked={
                          data.AttentionHyperactivity["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleAttentionHyperactivityChange}
                        disabled={formDisabled}
                      />
                      {data.AttentionHyperactivity["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="AttentionHyperactivityEasilyDistracted"
                                  label="Easily distracted"
                                  divClassName="ms-3"
                                  checked={
                                    data.AttentionHyperactivityEasilyDistracted !==
                                      undefined &&
                                    data.AttentionHyperactivityEasilyDistracted[
                                      "@value"
                                    ] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DifficultyFollowThrough"
                                  label="Difficulty following-through"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyFollowThrough["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Forgetful"
                                  label="Forgetful"
                                  divClassName="ms-3"
                                  checked={data.Forgetful["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="DifficultyListening"
                                  label="Difficulty listening"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyListening["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DisorganizedAttention"
                                  label="Disorganized in tasks and activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.DisorganizedAttention["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Fidgety"
                                  label="Fidgety"
                                  divClassName="ms-3"
                                  checked={data.Fidgety["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="LackOfDetails"
                                  label="Lack of attention to detail"
                                  divClassName="ms-3"
                                  checked={
                                    data.LackOfDetails["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="LosesThings"
                                  label="Loses things"
                                  divClassName="ms-3"
                                  checked={
                                    data.LosesThings["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="InterruptsOthers"
                                  label="Interrupts others"
                                  divClassName="ms-3"
                                  checked={
                                    data.InterruptsOthers["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="UnableToSitStill"
                                  label="Unable to sit still"
                                  divClassName="ms-3"
                                  checked={
                                    data.UnableToSitStill["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="ExcessiveTalking"
                                  label="Excessive talking"
                                  divClassName="ms-3"
                                  checked={
                                    data.ExcessiveTalking["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DifficultyWaiting"
                                  label="Difficulty waiting for their turn"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyWaiting["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="OtherAttention"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherAttention["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.OtherAttention["@value"] === "true" ? (
                                  <FormInput
                                    name="OtherAttentionDetails"
                                    error={errors.OtherAttentionDetails}
                                    value={
                                      data.OtherAttentionDetails !== undefined
                                        ? data.OtherAttentionDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("OtherAttentionDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.AttentionHyperactivity !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.AttentionHyperactivity}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="AttentionHyperactivityFrequency"
                            label="Frequency:"
                            error={errors.AttentionHyperactivityFrequency}
                            value={
                              data.AttentionHyperactivityFrequency !== undefined
                                ? data.AttentionHyperactivityFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttentionHyperactivityFrequency"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="AttentionHyperactivityIntensity"
                            label="Intensity:"
                            error={errors.AttentionHyperactivityIntensity}
                            value={
                              data.AttentionHyperactivityIntensity !== undefined
                                ? data.AttentionHyperactivityIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttentionHyperactivityIntensity"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="AttentionHyperactivityDuration"
                            label="Duration:"
                            error={errors.AttentionHyperactivityDuration}
                            value={
                              data.AttentionHyperactivityDuration !== undefined
                                ? data.AttentionHyperactivityDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttentionHyperactivityDuration"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Attention-Deficit/Hyperactivity
                          Disorder
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="SU"
                        label="Symptoms of Substance Use Disorders"
                        divClassName="ms-3 fw-bold"
                        checked={data.SU["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleSubstanceUseSymptomsChange}
                        disabled={formDisabled}
                      />
                      {data.SU["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="PatternOfSubstanceUse"
                                  label="Takes substances in large amounts and/or over long periods of time"
                                  divClassName="ms-3"
                                  checked={
                                    data.PatternOfSubstanceUse["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DesiresCutDownUse"
                                  label="Persistent desire to cut down on substance use"
                                  divClassName="ms-3"
                                  checked={
                                    data.DesiresCutDownUse["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="TimeSpentObtaining"
                                  label="Spends a significant amount of time obtaining substance(s)"
                                  divClassName="ms-3"
                                  checked={
                                    data.TimeSpentObtaining["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="Cravings"
                                  label="Cravings for substances"
                                  divClassName="ms-3"
                                  checked={data.Cravings["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="FailureOfResponsibilities"
                                  label="Failure to fulfill major roles at work, school, or home"
                                  divClassName="ms-3"
                                  checked={
                                    data.FailureOfResponsibilities["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="NegativeImpact"
                                  label="Use consistently negatively impacts relationships"
                                  divClassName="ms-3"
                                  checked={
                                    data.NegativeImpact["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="AbandonsActivities"
                                  label="Giving up/reducing previously enjoyed activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.AbandonsActivities["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Tolerance"
                                  label="Tolerance"
                                  divClassName="ms-3"
                                  checked={data.Tolerance["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="SUWithdrawal"
                                  label="Withdrawal"
                                  divClassName="ms-3"
                                  checked={
                                    data.SUWithdrawal["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="SUOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.SUOther["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.SUOther["@value"] === "true" ? (
                                  <FormInput
                                    name="SUOtherDetails"
                                    error={errors.SUOtherDetails}
                                    value={
                                      data.SUOtherDetails !== undefined
                                        ? data.SUOtherDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("SUOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.SU !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.SU}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="SubstanceUseFrequency"
                            label="Frequency:"
                            error={errors.SubstanceUseFrequency}
                            value={
                              data.SubstanceUseFrequency !== undefined
                                ? data.SubstanceUseFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("SubstanceUseFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="SubstanceUseIntensity"
                            label="Intensity:"
                            error={errors.SubstanceUseIntensity}
                            value={
                              data.SubstanceUseIntensity !== undefined
                                ? data.SubstanceUseIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("SubstanceUseIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="SubstanceUseDuration"
                            label="Duration:"
                            error={errors.SubstanceUseDuration}
                            value={
                              data.SubstanceUseDuration !== undefined
                                ? data.SubstanceUseDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("SubstanceUseDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="DMDD"
                        label="Symptoms of Disruptive Mood Dysregulation Disorder"
                        divClassName="ms-3 fw-bold"
                        checked={data.DMDD["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleDMDDChange}
                        disabled={formDisabled}
                      />
                      {data.DMDD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="Temper"
                                  label="Severe and recurrent temper outbursts (verbally and/or behaviorally)"
                                  divClassName="ms-3"
                                  checked={data.Temper["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="OutsideOutbursts"
                                  label="Outbursts are outside of developmental level"
                                  divClassName="ms-3"
                                  checked={
                                    data.OutsideOutbursts["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="FrequentOutbursts"
                                  label="Outbursts occur three or more times per week"
                                  divClassName="ms-3"
                                  checked={
                                    data.FrequentOutbursts["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="DMDDIrritable"
                                  label="Irritable/angry mood most of the day"
                                  divClassName="ms-3"
                                  checked={
                                    data.DMDDIrritable["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="OutburstsAroundOthers"
                                  label="Symptoms occur in home, school and/or with peers"
                                  divClassName="ms-3"
                                  checked={
                                    data.OutburstsAroundOthers["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DMDDOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.DMDDOther["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.DMDDOther["@value"] === "true" ? (
                                  <FormInput
                                    name="DMDDOtherDetails"
                                    error={errors.DMDDOtherDetails}
                                    value={
                                      data.DMDDOtherDetails !== undefined
                                        ? data.DMDDOtherDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("DMDDOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DMDD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DMDD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="DMDDFrequency"
                            label="Frequency:"
                            error={errors.DMDDFrequency}
                            value={
                              data.DMDDFrequency !== undefined
                                ? data.DMDDFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("DMDDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="DMDDIntensity"
                            label="Intensity:"
                            error={errors.DMDDIntensity}
                            value={
                              data.DMDDIntensity !== undefined
                                ? data.DMDDIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("DMDDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="DMDDDuration"
                            label="Duration:"
                            error={errors.DMDDDuration}
                            value={
                              data.DMDDDuration !== undefined
                                ? data.DMDDDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("DMDDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="ConductDisorder"
                        label="Symptoms of Conduct Disorder"
                        divClassName="ms-3 fw-bold"
                        checked={data.ConductDisorder["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleConductDisorderChange}
                        disabled={formDisabled}
                      />
                      {data.ConductDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="Aggression"
                                  label="Aggression to people and/or animals"
                                  divClassName="ms-3"
                                  checked={data.Aggression["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DestructionOfProperty"
                                  label="Destruction of Property"
                                  divClassName="ms-3"
                                  checked={
                                    data.DestructionOfProperty["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Theft"
                                  label="Deceitfulness or theft"
                                  divClassName="ms-3"
                                  checked={data.Theft["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="SeriousViolations"
                                  label="Serious violations of rules"
                                  divClassName="ms-3"
                                  checked={
                                    data.SeriousViolations["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col-4">
                                <FormCheckbox
                                  name="OtherConductDisorder"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherConductDisorder["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherConductDisorder["@value"] ===
                                "true" ? (
                                  <FormInput
                                    name="OtherConductDisorderDetails"
                                    error={errors.OtherConductDisorderDetails}
                                    value={
                                      data.OtherConductDisorderDetails !==
                                      undefined
                                        ? data.OtherConductDisorderDetails[
                                            "@value"
                                          ]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "OtherConductDisorderDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.ConductDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.ConductDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="ConductDisorderFrequency"
                            label="Frequency:"
                            error={errors.ConductDisorderFrequency}
                            value={
                              data.ConductDisorderFrequency !== undefined
                                ? data.ConductDisorderFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("ConductDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="ConductDisorderIntensity"
                            label="Intensity:"
                            error={errors.ConductDisorderIntensity}
                            value={
                              data.ConductDisorderIntensity !== undefined
                                ? data.ConductDisorderIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("ConductDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="ConductDisorderDuration"
                            label="Duration:"
                            error={errors.ConductDisorderDuration}
                            value={
                              data.ConductDisorderDuration !== undefined
                                ? data.ConductDisorderDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("ConductDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="ODD"
                        label="Symptoms of Oppositional Defiance Disorder (ODD)"
                        divClassName="ms-3 fw-bold"
                        checked={data.ODD["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleODDChange}
                        disabled={formDisabled}
                      />
                      {data.ODD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="IrritableODD"
                                  label="Angry/irritable mood"
                                  divClassName="ms-3"
                                  checked={
                                    data.IrritableODD["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Argumentative"
                                  label="Argumentative/defiant"
                                  divClassName="ms-3"
                                  checked={
                                    data.Argumentative["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Vindictive"
                                  label="Vindictive"
                                  divClassName="ms-3"
                                  checked={data.Vindictive["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="OtherODD"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.OtherODD["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.OtherODD["@value"] === "true" ? (
                                  <FormInput
                                    name="OtherODDDetails"
                                    error={errors.OtherODDDetails}
                                    value={
                                      data.OtherODDDetails !== undefined
                                        ? data.OtherODDDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("OtherODDDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.ODD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.ODD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="ODDFrequency"
                            label="Frequency:"
                            error={errors.ODDFrequency}
                            value={
                              data.ODDFrequency !== undefined
                                ? data.ODDFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("ODDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="ODDIntensity"
                            label="Intensity:"
                            error={errors.ODDIntensity}
                            value={
                              data.ODDIntensity !== undefined
                                ? data.ODDIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("ODDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="ODDDuration"
                            label="Duration:"
                            error={errors.ODDDuration}
                            value={
                              data.ODDDuration !== undefined
                                ? data.ODDDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("ODDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="OCD"
                        label="Symptoms of Obsessive-Compulsive Disorder (OCD)"
                        divClassName="ms-3 fw-bold"
                        checked={data.OCD["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleOCDChange}
                        disabled={formDisabled}
                      />
                      {data.OCD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="OCDPresence"
                                  label="Presence of obsessions, compulsions, or both"
                                  divClassName="ms-3"
                                  checked={
                                    data.OCDPresence["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Obsessions"
                                  label="Obsessions (recurrent and persistent thoughts, urges, or images)"
                                  divClassName="ms-3"
                                  checked={data.Obsessions["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="TimeConsuming"
                                  label="Obsessions or compulsions are time consuming and cause distress/impairment"
                                  divClassName="ms-3"
                                  checked={
                                    data.TimeConsuming["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="Compulsions"
                                  label="Compulsions/repetitive behaviors (hand washing, ordering, checking,) or mental acts (praying, counting, repeating words silently)"
                                  divClassName="ms-3"
                                  checked={
                                    data.Compulsions["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="ReduceAnxiety"
                                  label="Behaviors and mental acts done to prevent or reduce anxiety or distress"
                                  divClassName="ms-3"
                                  checked={
                                    data.ReduceAnxiety["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="SuppressUrges"
                                  label="Attempts to ignore or suppress thoughts, urges or images with other thought or action (compulsion)"
                                  divClassName="ms-3"
                                  checked={
                                    data.SuppressUrges["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="OCDOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={data.OCDOther["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OCDOther["@value"] === "true" ? (
                                  <FormInput
                                    name="OCDOtherDetails"
                                    error={errors.OCDOtherDetails}
                                    value={
                                      data.OCDOtherDetails !== undefined
                                        ? data.OCDOtherDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("OCDOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.OCD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.OCD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="OCDFrequency"
                            label="Frequency:"
                            error={errors.OCDFrequency}
                            value={
                              data.OCDFrequency !== undefined
                                ? data.OCDFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("OCDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="OCDIntensity"
                            label="Intensity:"
                            error={errors.OCDIntensity}
                            value={
                              data.OCDIntensity !== undefined
                                ? data.OCDIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("OCDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="OCDDuration"
                            label="Duration:"
                            error={errors.OCDDuration}
                            value={
                              data.OCDDuration !== undefined
                                ? data.OCDDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("OCDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="PanicDisorder"
                        label="Symptoms of Panic Disorder"
                        divClassName="ms-3 fw-bold"
                        checked={data.PanicDisorder["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handlePanicDisorderChange}
                        disabled={formDisabled}
                      />
                      {data.PanicDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="PoundingHeart"
                                  label="Palpitations, pounding heart, accelerated heart rate"
                                  divClassName="ms-3"
                                  checked={
                                    data.PoundingHeart["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="PanicDisorderSweating"
                                  label="Sweating"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderSweating["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Trembling"
                                  label="Trembling/shaking"
                                  divClassName="ms-3"
                                  checked={data.Trembling["@value"] === "true"}
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="ShortnessOfBreath"
                                  label="Shortness of breath"
                                  divClassName="ms-3"
                                  checked={
                                    data.ShortnessOfBreath["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Choking"
                                  label="Feelings of choking"
                                  divClassName="ms-3"
                                  checked={data.Choking["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="ChestPain"
                                  label="Chest pain/discomfort"
                                  divClassName="ms-3"
                                  checked={data.ChestPain["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="PanicDisorderNausea"
                                  label="Nausea/abdominal discomfort"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderNausea["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Dizzy"
                                  label="Dizzy/faint"
                                  divClassName="ms-3"
                                  checked={data.Dizzy["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="Chills"
                                  label="Chills"
                                  divClassName="ms-3"
                                  checked={data.Chills["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="Derealization"
                                  label="Derealization or depersonalization"
                                  divClassName="ms-3"
                                  checked={
                                    data.Derealization["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="FearOfCrazy"
                                  label="Fear of losing control/going crazy"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearOfCrazy["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="FearOfDying"
                                  label="Fear of dying"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearOfDying["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="PanicDisorderOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderOther["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                                {data.PanicDisorderOther["@value"] ===
                                "true" ? (
                                  <FormInput
                                    name="PanicDisorderOtherDetails"
                                    error={errors.PanicDisorderOtherDetails}
                                    value={
                                      data.PanicDisorderOtherDetails !==
                                      undefined
                                        ? data.PanicDisorderOtherDetails[
                                            "@value"
                                          ]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "PanicDisorderOtherDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.PanicDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.PanicDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="PanicDisorderFrequency"
                            label="Frequency:"
                            error={errors.PanicDisorderFrequency}
                            value={
                              data.PanicDisorderFrequency !== undefined
                                ? data.PanicDisorderFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("PanicDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="PanicDisorderIntensity"
                            label="Intensity:"
                            error={errors.PanicDisorderIntensity}
                            value={
                              data.PanicDisorderIntensity !== undefined
                                ? data.PanicDisorderIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("PanicDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="PanicDisorderDuration"
                            label="Duration:"
                            error={errors.PanicDisorderDuration}
                            value={
                              data.PanicDisorderDuration !== undefined
                                ? data.PanicDisorderDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("PanicDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="Phobias"
                        label="Symptoms of Phobias"
                        divClassName="ms-3 fw-bold"
                        checked={data.Phobias["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handlePhobiasChange}
                        disabled={formDisabled}
                      />
                      {data.Phobias["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="PhobiasSpecificFear"
                                  label="Marked fear/anxiety about a specific object or situation"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasSpecificFear["@value"] ===
                                    "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="ImmediateFear"
                                  label="Object or situation provokes immediate fear or anxiety"
                                  divClassName="ms-3"
                                  checked={
                                    data.ImmediateFear["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="PhobiasAvoidance"
                                  label="Avoids objective/situation"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasAvoidance["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="FearExaggerated"
                                  label="Fear and anxiety is out of proportion"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearExaggerated["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col-4">
                                <FormCheckbox
                                  name="PhobiasOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasOther["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.PhobiasOther["@value"] === "true" ? (
                                  <FormInput
                                    name="PhobiasOtherDetails"
                                    error={errors.PhobiasOtherDetails}
                                    value={
                                      data.PhobiasOtherDetails !== undefined
                                        ? data.PhobiasOtherDetails["@value"]
                                        : ""
                                    }
                                    onChange={handleChange}
                                    identical={compare("PhobiasOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.Phobias !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.Phobias}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <FormInput
                            name="PhobiasFrequency"
                            label="Frequency:"
                            error={errors.PhobiasFrequency}
                            value={
                              data.PhobiasFrequency !== undefined
                                ? data.PhobiasFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("PhobiasFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="PhobiasIntensity"
                            label="Intensity:"
                            error={errors.PhobiasIntensity}
                            value={
                              data.PhobiasIntensity !== undefined
                                ? data.PhobiasIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("PhobiasIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="PhobiasDuration"
                            label="Duration:"
                            error={errors.PhobiasDuration}
                            value={
                              data.PhobiasDuration !== undefined
                                ? data.PhobiasDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("PhobiasDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Specific Phobia, Social Anxiety
                          Disorder (Social Phobia), Agoraphobia
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="DI"
                        label="Developmental Impairments"
                        divClassName="ms-3 fw-bold"
                        checked={data.DI["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleDIChange}
                        disabled={formDisabled}
                      />
                      {data.DI["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="DILowIQ"
                                  label="Low intelligence/IQ"
                                  divClassName="ms-3"
                                  checked={data.DILowIQ["@value"] === "true"}
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DISensoryImpairments"
                                  label="Sensory impairments"
                                  divClassName="ms-3"
                                  checked={
                                    data.DISensoryImpairments["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DISpeechDelays"
                                  label="Language/speech delays"
                                  divClassName="ms-3"
                                  checked={
                                    data.DISpeechDelays["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormCheckbox
                                  name="DIMotorCoordinationDelays"
                                  label="Motor coordination delays"
                                  divClassName="ms-3"
                                  checked={
                                    data.DIMotorCoordinationDelays["@value"] ===
                                    "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DILearningDisorder"
                                  label="Specific learning disorder"
                                  divClassName="ms-3"
                                  checked={
                                    data.DILearningDisorder["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <FormCheckbox
                                  name="DISpatialDelays"
                                  label="Visio-spatial skill delays"
                                  divClassName="ms-3"
                                  checked={
                                    data.DISpatialDelays["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <FormCheckbox
                                  name="DevelopmentalImpairmentsOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.DevelopmentalImpairmentsOther !==
                                      undefined &&
                                    data.DevelopmentalImpairmentsOther[
                                      "@value"
                                    ] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.DevelopmentalImpairmentsOther[
                                  "@value"
                                ] === "true" ? (
                                  <FormInput
                                    name="DevelopmentalImpairmentsOtherDetails"
                                    error={
                                      errors.DevelopmentalImpairmentsOtherDetails
                                    }
                                    value={
                                      data.DevelopmentalImpairmentsOtherDetails !==
                                        undefined &&
                                      data.DevelopmentalImpairmentsOtherDetails[
                                        "@value"
                                      ]
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "DevelopmentalImpairmentsOtherDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DI !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DI}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          {data.DILowIQ["@value"] === "true" ||
                          data.DISensoryImpairments["@value"] === "true" ||
                          data.DISpeechDelays["@value"] === "true" ||
                          data.DIMotorCoordinationDelays["@value"] === "true" ||
                          data.DILearningDisorder["@value"] === "true" ||
                          data.DISpatialDelays["@value"] === "true" ? (
                            <FormInput
                              name="DevelopmentalImpairmentsDetails"
                              label="Please describe:"
                              error={errors.DevelopmentalImpairmentsDetails}
                              value={
                                data.DevelopmentalImpairmentsDetails !==
                                undefined
                                  ? data.DevelopmentalImpairmentsDetails[
                                      "@value"
                                    ]
                                  : ""
                              }
                              onChange={handleChange}
                              identical={compare(
                                "DevelopmentalImpairmentsDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </>
                      )}
                    </div>
                    <div className="border rounded mb-2 px-2 pt-2">
                      <FormCheckbox
                        name="OtherSymptoms"
                        label="Other Symptoms"
                        divClassName="ms-3 fw-bold"
                        checked={data.OtherSymptoms["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleOtherSymptomsChange}
                        disabled={formDisabled}
                      />
                      {data.OtherSymptoms["@value"] === "true" && (
                        <>
                          <FormInput
                            name="OtherSymptomsDetails"
                            label="Please describe:"
                            error={errors.OtherSymptomsDetails}
                            value={
                              data.OtherSymptomsDetails !== undefined
                                ? data.OtherSymptomsDetails["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("OtherSymptomsDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="OtherSymptomsFrequency"
                            label="Frequency:"
                            error={errors.OtherSymptomsFrequency}
                            value={
                              data.OtherSymptomsFrequency !== undefined
                                ? data.OtherSymptomsFrequency["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("OtherSymptomsFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="OtherSymptomsIntensity"
                            label="Intensity:"
                            error={errors.OtherSymptomsIntensity}
                            value={
                              data.OtherSymptomsIntensity !== undefined
                                ? data.OtherSymptomsIntensity["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("OtherSymptomsIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <FormInput
                            name="OtherSymptomsDuration"
                            label="Duration:"
                            error={errors.OtherSymptomsDuration}
                            value={
                              data.OtherSymptomsDuration !== undefined
                                ? data.OtherSymptomsDuration["@value"]
                                : ""
                            }
                            onChange={handleChange}
                            identical={compare("OtherSymptomsDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                    </div>
                  </ExpandDiv>
                  <ExpandDiv
                    id="ClinicalNarrative"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Clinical Narrative Summary"
                  >
                    Include etiology of presenting problem and maintenance of
                    problem; mental health history; alcohol/drug history;
                    severity of problem; where problem occurs (functioning at
                    home, at work, in community); onset of problem (acute vs.
                    chronic); client motivation for treatment; whether problem
                    is known to be responsive to treatment.
                    <FormTextArea
                      name="ClinicalNarrativeSummary"
                      error={errors.ClinicalNarrativeSummary}
                      value={data.ClinicalNarrativeSummary["@value"]}
                      onChange={handleChange}
                      identical={compare("ClinicalNarrativeSummary")}
                      commentsHandler={handleComments}
                      disabled
                    />
                    <FormTextArea
                      name="ClinicalNarrativeSummaryComments"
                      label="Clinical Narrative Additional Comments"
                      error={errors.ClinicalNarrativeSummaryComments}
                      value={data.ClinicalNarrativeSummaryComments["@value"]}
                      onChange={handleChange}
                      identical={compare("ClinicalNarrativeSummaryComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="5"
                    />
                  </ExpandDiv>
                  <ExpandDiv
                    id="Diagnosis"
                    expandStatus={expandStatus}
                    className="mt-2"
                    title="Diagnosis"
                  >
                    <FormRadioButton
                      name="TraditionalFosterCare"
                      label="Is the youth in a SAFY Traditional Foster Care Home?"
                      error={errors.TraditionalFosterCare}
                      value={data.TraditionalFosterCare["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.TraditionalFosterCare["@value"] !== "Yes" && (
                      <>
                        <FieldTable
                          name="DiagnosisDetails"
                          commentsHandler={handleComments}
                        >
                          <div className="container border-top border-start border-end mb-2">
                            <div className="row border-bottom">
                              <div className="col-3 border-end py-2">
                                ICD-10 or DSM-5 Code
                              </div>
                              <div className="col py-2">Diagnosis</div>
                            </div>
                            {data.DiagnosisDetails["@value"] &&
                              JSON.parse(data.DiagnosisDetails["@value"]).map(
                                (item, index) => (
                                  <div
                                    key={item._id}
                                    className="row border-bottom"
                                  >
                                    <div className="col-3 border-end py-2 d-flex">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <>
                                          <i
                                            className="fas fa-save me-2 d-print-none"
                                            onClick={() => {
                                              handleSaveRow(
                                                index,
                                                "DiagnosisDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "DiagnosisDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <FormInput
                                            name="Code"
                                            value={item.Code}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "DiagnosisDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <i
                                            className="fas fa-pen me-2 d-print-none"
                                            onClick={() => {
                                              handleEditRow(
                                                index,
                                                "DiagnosisDetails"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          {item.Code}
                                        </>
                                      )}
                                    </div>
                                    <div className="col py-2">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <FormTextArea
                                          name="Diagnosis"
                                          value={item.Diagnosis}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "DiagnosisDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        <>{item.Diagnosis}</>
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                          </div>
                          {!formDisabled ? (
                            <button
                              className="btn btn-primary mb-2"
                              onClick={() => {
                                handleAddRow("DiagnosisDetails", {
                                  IsEditable: true,
                                });
                              }}
                            >
                              Add Diagnosis
                            </button>
                          ) : null}
                          {errors.DiagnosisDetails !== undefined && (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DiagnosisDetails}
                                </div>
                              </div>
                            </div>
                          )}
                        </FieldTable>
                      </>
                    )}
                  </ExpandDiv>
                </>
              ) : null}
              <ExpandDiv
                id="TreatmentRecommendations"
                expandStatus={expandStatus}
                className="mt-2"
                title="Treatment Recommendations/Needs"
              >
                <FormRadioButton
                  name="PlacementNotes"
                  className="mt-2"
                  label="Are there any other Placement Considerations or Notes, including special needs?"
                  error={errors.PlacementNotes}
                  value={data.PlacementNotes["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PlacementNotes["@value"] === "Yes" && (
                  <FormInput
                    name="PlacementNotesComments"
                    error={errors.PlacementNotesComments}
                    value={data.PlacementNotesComments["@value"]}
                    onChange={handleChange}
                    identical={compare("PlacementNotesComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                {data.PSDType["@value"] === "Initial" ||
                data.PSDType["@value"] === "Review/Update" ? (
                  <>
                    <b>Treatment Recommendations</b>
                    <FieldTable
                      name="TreatmentRecommendations"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col py-2">Recommendation</div>
                        </div>
                        {data.TreatmentRecommendations["@value"] &&
                          JSON.parse(
                            data.TreatmentRecommendations["@value"]
                          ).map((item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "TreatmentRecommendations"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "TreatmentRecommendations"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <FormSelect
                                      name="Recommendation"
                                      value={item.Recommendation}
                                      options={treatmentRecommendationOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TreatmentRecommendations"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "TreatmentRecommendations"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    {item.Recommendation}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("TreatmentRecommendations", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Treatment Recommendation
                          </button>
                        </>
                      ) : null}
                      {errors.TreatmentRecommendations !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.TreatmentRecommendations}
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldTable>
                    <br />
                    <FormRadioButton
                      name="DiagnosisEducation"
                      label="Education regarding diagnosis provided to client/family:"
                      error={errors.DiagnosisEducation}
                      value={data.DiagnosisEducation["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="RecommendationEducation"
                      label="Education regarding recommendations provided to client/family:"
                      error={errors.RecommendationEducation}
                      value={data.RecommendationEducation["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="ClientAgrees"
                      label="Client in agreement with recommendations:"
                      error={errors.ClientAgrees}
                      value={data.ClientAgrees["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="FamilyAgress"
                      label="Family in agreement with recommendations:"
                      error={errors.FamilyAgress}
                      value={data.FamilyAgress["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="Partnership"
                      label="Partnership with other systems is warranted and consented to*"
                      error={errors.Partnership}
                      value={data.Partnership["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.Partnership["@value"] === "Yes" && (
                      <i>*Goal in Treatment Plan is required</i>
                    )}
                    {data.Partnership["@value"] === "No" && (
                      <FormInput
                        name="NoPartnershipReason"
                        label="Why?"
                        error={errors.NoPartnershipReason}
                        value={data.NoPartnershipReason["@value"]}
                        onChange={handleChange}
                        identical={compare("NoPartnershipReason")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </>
                ) : null}
              </ExpandDiv>
              {showNarrative === "Show" && (
                <div className="clinicalNarrative bg-white border p-2">
                  <label>
                    <b>Clinical Narrative Summary:</b>
                  </label>
                  <textarea
                    value={data.ClinicalNarrativeSummary["@value"]}
                    rows="5"
                    disabled
                  />
                </div>
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare && (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default COTFCPSD;
